export const WAEC_BIOLOGY_LEARN_SS2 = [
  {
    topic: 'Cell Biology',
    topicCode: 'SS2-BIO-01',
    module: 'Cell Biology',
    term: 'First Term',
    contentHTML:
      '<div class="learn-intro">The <span class="learn-keyword">cell</span> is the basic structural and functional unit of all living organisms. SS2 Cell Biology builds on SS1 by examining cell organelles in detail, cellular processes, and the differences between prokaryotic and eukaryotic cells.</div><h3 class="learn-subheading">1. Prokaryotic vs Eukaryotic Cells</h3><div class="learn-table-wrap"><table class="learn-table"><thead><tr><th>Feature</th><th>Prokaryote</th><th>Eukaryote</th></tr></thead><tbody><tr><td>Nucleus</td><td>Absent (nucleoid region)</td><td>Present (membrane-bound)</td></tr><tr><td>Size</td><td>1–10 μm</td><td>10–100 μm</td></tr><tr><td>Membrane-bound organelles</td><td>Absent</td><td>Present</td></tr><tr><td>DNA</td><td>Circular, no histones</td><td>Linear, with histones</td></tr><tr><td>Examples</td><td>Bacteria, Archaea</td><td>Plants, Animals, Fungi</td></tr></tbody></table></div><h3 class="learn-subheading">2. Cell Organelles and Functions</h3><div class="learn-table-wrap"><table class="learn-table"><thead><tr><th>Organelle</th><th>Function</th></tr></thead><tbody><tr><td>Nucleus</td><td>Controls cell activities; contains DNA (genetic info)</td></tr><tr><td>Mitochondria</td><td>Site of aerobic respiration; produces ATP (energy)</td></tr><tr><td>Ribosomes</td><td>Site of protein synthesis</td></tr><tr><td>Endoplasmic reticulum (ER)</td><td>Rough ER: protein transport; Smooth ER: lipid synthesis</td></tr><tr><td>Golgi apparatus</td><td>Modifies, packages, and secretes proteins</td></tr><tr><td>Chloroplasts</td><td>Site of photosynthesis (plant cells only)</td></tr><tr><td>Vacuoles</td><td>Storage; large central vacuole in plants maintains turgor</td></tr><tr><td>Lysosomes</td><td>Contain digestive enzymes; break down waste</td></tr><tr><td>Cell membrane</td><td>Selectively permeable; controls what enters/exits</td></tr><tr><td>Cell wall (plants)</td><td>Cellulose; provides support and shape</td></tr></tbody></table></div><h3 class="learn-subheading">3. Cell Processes</h3><ul class="learn-list"><li><strong>Osmosis:</strong> diffusion of water through a selectively permeable membrane from high water potential (low solute) to low water potential (high solute)</li><li><strong>Diffusion:</strong> movement of molecules from high to low concentration (passive, no energy needed)</li><li><strong>Active transport:</strong> movement against concentration gradient; requires ATP and carrier proteins</li><li><strong>Mitosis:</strong> cell division producing 2 identical diploid daughter cells (PMAT: Prophase, Metaphase, Anaphase, Telophase); for growth, repair, asexual reproduction</li><li><strong>Meiosis:</strong> produces 4 haploid genetically varied daughter cells; for sexual reproduction</li></ul><div class="learn-tip-box"><span class="learn-tip-icon">💡</span><strong>Mitosis vs Meiosis:</strong> Mitosis = 2 identical diploid cells (growth/repair). Meiosis = 4 varied haploid cells (sex cells/gametes).</div><div class="learn-tip-box"><span class="learn-tip-icon">🎯</span><strong>Summary:</strong> Prokaryotes: no membrane-bound nucleus/organelles (bacteria). Eukaryotes: have nucleus and organelles. Mitochondria = ATP. Chloroplasts = photosynthesis. Ribosomes = protein synthesis. Golgi = packs/secretes proteins.</div>',
    questions: [
      {
        q: 'Which of the following is found in prokaryotic cells but NOT in eukaryotic cells?',
        o: [
          'Membrane-bound nucleus',
          'Ribosomes',
          'Circular DNA with no histone proteins',
          'Cell membrane',
        ],
        a: 2,
        e: 'Prokaryotes have circular DNA not associated with histone proteins, located in a nucleoid region (not a membrane-bound nucleus). Eukaryotes have linear DNA wound around histones, enclosed in a membrane-bound nucleus.',
        h: 'Prokaryote: circular DNA without histones. Eukaryote: linear DNA with histones in membrane-bound nucleus.',
        yr: 'SS2',
      },
      {
        q: 'The organelle responsible for the production of ATP through aerobic respiration is the:',
        o: ['Ribosome', 'Chloroplast', 'Mitochondrion', 'Golgi apparatus'],
        a: 2,
        e: "Mitochondria are the sites of aerobic respiration, where glucose and oxygen are used to produce ATP (adenosine triphosphate) — the cell's energy currency. They are called the powerhouse of the cell.",
        h: 'Mitochondria = powerhouse of the cell; site of aerobic respiration and ATP production.',
        yr: 'SS2',
      },
      {
        q: 'Protein synthesis occurs at the:',
        o: ['Mitochondria', 'Nucleus', 'Lysosomes', 'Ribosomes'],
        a: 3,
        e: 'Ribosomes are the sites of protein synthesis. They translate mRNA sequences into polypeptide chains. Ribosomes are found free in the cytoplasm or attached to rough endoplasmic reticulum.',
        h: 'Ribosomes = site of protein synthesis.',
        yr: 'SS2',
      },
      {
        q: 'The Golgi apparatus functions to:',
        o: [
          'Produce energy for the cell',
          'Synthesise DNA',
          'Modify, package, and secrete proteins and lipids',
          'Carry out photosynthesis',
        ],
        a: 2,
        e: 'The Golgi apparatus (Golgi body) receives proteins from the rough ER, modifies them (e.g., adds carbohydrate chains), packages them into vesicles, and directs them to their destinations (secretion, lysosomes, cell membrane).',
        h: 'Golgi apparatus: modifies, packages, and secretes proteins.',
        yr: 'SS2',
      },
      {
        q: 'Osmosis is defined as the movement of water molecules:',
        o: [
          'From low to high water potential through any membrane',
          'From high to low water potential through a selectively permeable membrane',
          'Against a concentration gradient using energy',
          'From high solute concentration to low solute concentration',
        ],
        a: 1,
        e: 'Osmosis is the passive movement of water molecules from a region of higher water potential (lower solute concentration) to a region of lower water potential (higher solute concentration) through a selectively permeable membrane.',
        h: 'Osmosis: water moves from high water potential to low water potential through selectively permeable membrane.',
        yr: 'SS2',
      },
      {
        q: 'Active transport differs from diffusion in that active transport:',
        o: [
          'Moves substances from high to low concentration',
          'Does not require energy',
          'Moves substances against a concentration gradient and requires ATP and carrier proteins',
          'Only occurs in plant cells',
        ],
        a: 2,
        e: 'Active transport moves substances against their concentration gradient (from low to high concentration), requiring energy (ATP) and specific carrier proteins embedded in the cell membrane. Examples: uptake of mineral ions by plant roots, sodium-potassium pump in nerve cells.',
        h: 'Active transport: against concentration gradient; requires ATP and carrier proteins.',
        yr: 'SS2',
      },
      {
        q: 'Which of the following is found in plant cells but NOT in animal cells?',
        o: ['Mitochondria', 'Cell membrane', 'Ribosomes', 'Cell wall and chloroplasts'],
        a: 3,
        e: 'Plant cells have cell walls (made of cellulose) for rigidity and chloroplasts for photosynthesis — absent in animal cells. Both plant and animal cells have mitochondria, cell membranes, and ribosomes.',
        h: 'Plant only: cell wall (cellulose), chloroplasts, large central vacuole.',
        yr: 'SS2',
      },
      {
        q: 'The result of mitosis is:',
        o: [
          'Four haploid daughter cells',
          'Two genetically identical diploid daughter cells',
          'Four genetically varied cells',
          'Two cells with half the original chromosome number',
        ],
        a: 1,
        e: 'Mitosis produces two daughter cells that are genetically identical to the parent cell (same chromosome number — diploid). Used for growth, tissue repair, and asexual reproduction. Meiosis produces four haploid genetically varied cells.',
        h: 'Mitosis: 2 genetically identical diploid cells (growth, repair).',
        yr: 'SS2',
      },
      {
        q: 'Meiosis produces cells used for:',
        o: [
          'Growth of new tissues',
          'Repair of damaged cells',
          'Asexual reproduction',
          'Sexual reproduction (gametes — haploid cells)',
        ],
        a: 3,
        e: 'Meiosis is a type of cell division that produces four genetically varied haploid daughter cells (gametes: sperm, eggs, pollen). The haploid number is restored to diploid at fertilisation.',
        h: 'Meiosis: 4 haploid gametes for sexual reproduction.',
        yr: 'SS2',
      },
      {
        q: 'The selectively permeable membrane of a cell:',
        o: [
          'Allows all substances to pass freely',
          'Allows no substances to pass',
          'Allows water and small molecules to pass but controls the passage of larger molecules',
          'Is made of cellulose',
        ],
        a: 2,
        e: "The cell (plasma) membrane is selectively permeable — it allows water, small molecules, and lipid-soluble substances to pass freely, while controlling the movement of larger molecules, ions, and charged particles. This maintains the cell's internal environment.",
        h: 'Cell membrane: selectively permeable — controls what enters and exits the cell.',
        yr: 'SS2',
      },
      {
        q: 'Lysosomes contain enzymes that:',
        o: [
          'Synthesise proteins',
          'Produce ATP',
          'Break down waste materials, worn-out organelles, and engulfed bacteria',
          'Carry out photosynthesis',
        ],
        a: 2,
        e: 'Lysosomes are membrane-bound organelles containing hydrolytic (digestive) enzymes. They digest: worn-out organelles (autophagy), engulfed foreign particles (bacteria), and cellular debris. They maintain cell health.',
        h: 'Lysosomes: contain digestive enzymes; break down waste and foreign particles.',
        yr: 'SS2',
      },
      {
        q: 'The endoplasmic reticulum (ER) in cells exists in two forms. Rough ER differs from smooth ER in that rough ER:',
        o: [
          'Synthesises lipids',
          'Is not connected to the nucleus',
          'Has ribosomes attached to its surface and is involved in protein processing/transport',
          'Is found only in animal cells',
        ],
        a: 2,
        e: 'Rough ER has ribosomes on its surface (giving a rough appearance) — it processes and transports newly synthesised proteins. Smooth ER lacks ribosomes and is involved in lipid synthesis and detoxification (e.g., in liver cells).',
        h: 'Rough ER: ribosomes attached → protein synthesis/transport. Smooth ER: lipid synthesis, detox.',
        yr: 'SS2',
      },
      {
        q: 'A plant cell placed in a hypertonic solution (high solute concentration) will:',
        o: [
          'Become turgid',
          'Undergo plasmolysis — the vacuole shrinks, the cytoplasm pulls away from the cell wall',
          'Burst (lyse)',
          'Show no change',
        ],
        a: 1,
        e: 'In hypertonic solution (lower water potential outside), water leaves the cell by osmosis. Vacuole shrinks → cytoplasm pulls away from cell wall → plasmolysis. The cell wall prevents bursting (unlike animal cells which lyse).',
        h: 'Plant cell in hypertonic solution → water leaves → plasmolysis (cytoplasm pulls away from wall).',
        yr: 'SS2',
      },
      {
        q: 'An animal cell placed in a hypotonic solution (low solute concentration) will:',
        o: [
          'Become turgid but not burst',
          'Undergo plasmolysis',
          'Swell and may burst (lyse) as water enters by osmosis',
          'Show no change',
        ],
        a: 2,
        e: 'In hypotonic solution (higher water potential outside), water enters the cell by osmosis. Without a cell wall, the animal cell swells and may burst (cytolysis/lysis). Red blood cells in distilled water swell and lyse (haemolysis).',
        h: 'Animal cell in hypotonic solution → water enters → swells → may burst (lyse).',
        yr: 'SS2',
      },
      {
        q: 'The correct order of mitosis stages is:',
        o: [
          'Metaphase, Prophase, Anaphase, Telophase',
          'Prophase, Metaphase, Anaphase, Telophase (PMAT)',
          'Telophase, Anaphase, Metaphase, Prophase',
          'Anaphase, Prophase, Telophase, Metaphase',
        ],
        a: 1,
        e: 'Mitosis stages (PMAT): Prophase (chromosomes condense, spindle forms), Metaphase (chromosomes align at equator), Anaphase (sister chromatids separate to poles), Telophase (nuclear envelopes reform, chromosomes decondense), then Cytokinesis.',
        h: 'Mitosis: PMAT — Prophase, Metaphase, Anaphase, Telophase.',
        yr: 'SS2',
      },
      {
        q: 'Chloroplasts are found in plant cells because:',
        o: [
          'They help absorb water from soil',
          'They are the site of photosynthesis — converting light energy, CO₂, and water into glucose and oxygen',
          'They produce ATP for all cellular activities',
          'They store starch for immediate energy use',
        ],
        a: 1,
        e: 'Chloroplasts contain chlorophyll, which absorbs light energy. They are the site of photosynthesis: 6CO₂ + 6H₂O + light energy → C₆H₁₂O₆ + 6O₂. They are found in mesophyll cells of leaves.',
        h: 'Chloroplasts: site of photosynthesis; contain chlorophyll; plant cells only.',
        yr: 'SS2',
      },
      {
        q: 'Which of the following best describes diffusion?',
        o: [
          'Movement of water through a membrane',
          'Movement of molecules from low to high concentration requiring energy',
          'Movement of molecules from high to low concentration — net movement down concentration gradient, no energy needed',
          'Movement of ions against a concentration gradient',
        ],
        a: 2,
        e: 'Diffusion is the passive movement of molecules or ions from a region of higher concentration to a region of lower concentration, down a concentration gradient. No energy is needed. Examples: O₂ into cells, CO₂ out of cells, glucose from blood into cells.',
        h: 'Diffusion: passive movement from high to low concentration (down gradient, no energy needed).',
        yr: 'SS2',
      },
      {
        q: 'The cell wall of plant cells is primarily composed of:',
        o: [
          'Starch',
          'Protein',
          'Cellulose — a polysaccharide providing structural rigidity',
          'Chitin',
        ],
        a: 2,
        e: 'Plant cell walls are made of cellulose microfibrils embedded in a polysaccharide matrix. Cellulose is rigid and strong — providing structural support, shape, and preventing over-expansion (turgidity). Fungal cell walls contain chitin.',
        h: 'Plant cell wall: cellulose (polysaccharide) → structural support and rigidity.',
        yr: 'SS2',
      },
      {
        q: 'The nuclear membrane (envelope) surrounds the nucleus and its function is to:',
        o: [
          'Produce proteins',
          'Separate the nucleus from the cytoplasm and control movement of substances between nucleus and cytoplasm through nuclear pores',
          'Store ATP',
          'Carry out respiration',
        ],
        a: 1,
        e: 'The nuclear envelope is a double membrane surrounding the nucleus in eukaryotic cells. It separates nuclear contents (DNA, RNA) from the cytoplasm and controls the exchange of molecules (mRNA out, enzymes in) through nuclear pores.',
        h: 'Nuclear envelope: separates nucleus from cytoplasm; nuclear pores control exchange.',
        yr: 'SS2',
      },
      {
        q: "In which phase of mitosis do chromosomes line up at the cell's equator?",
        o: ['Prophase', 'Metaphase', 'Anaphase', 'Telophase'],
        a: 1,
        e: "Metaphase (M in PMAT): chromosomes are fully condensed and align at the cell's equatorial plate (metaphase plate). Spindle fibres from each pole attach to centromeres. This is the best phase for studying chromosome number and morphology.",
        h: 'Metaphase: chromosomes align at equator (metaphase plate).',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Nutrition in Plants and Animals',
    topicCode: 'SS2-BIO-02',
    module: 'Nutrition',
    term: 'First Term',
    contentHTML:
      '<div class="learn-intro"><span class="learn-keyword">Nutrition</span> is the process by which organisms obtain and use food for energy, growth, and repair. Plants are autotrophs (make their own food through photosynthesis), while animals are heterotrophs (obtain food by consuming other organisms).</div><h3 class="learn-subheading">1. Photosynthesis (Plant Nutrition)</h3><p class="learn-p">6CO₂ + 6H₂O + light energy → C₆H₁₂O₆ + 6O₂</p><ul class="learn-list"><li>Occurs in chloroplasts, in the grana (light reactions) and stroma (dark reactions/Calvin cycle)</li><li>Chlorophyll absorbs red and blue light; reflects green (hence leaves appear green)</li><li>Factors affecting rate: light intensity, CO₂ concentration, temperature, water availability</li><li>Light reactions: water photolysis, ATP and NADPH production, O₂ released</li><li>Dark reactions (Calvin cycle): CO₂ fixed using ATP and NADPH to produce glucose</li></ul><h3 class="learn-subheading">2. Mineral Nutrition in Plants</h3><div class="learn-table-wrap"><table class="learn-table"><thead><tr><th>Mineral</th><th>Function</th><th>Deficiency</th></tr></thead><tbody><tr><td>Nitrogen (N)</td><td>Amino acids, proteins, nucleic acids</td><td>Stunted growth, yellowing (chlorosis)</td></tr><tr><td>Phosphorus (P)</td><td>DNA, ATP, cell membranes</td><td>Purple leaves, poor root growth</td></tr><tr><td>Potassium (K)</td><td>Enzyme activation, stomatal control</td><td>Brown leaf margins, wilting</td></tr><tr><td>Magnesium (Mg)</td><td>Central atom of chlorophyll</td><td>Yellowing between veins (interveinal chlorosis)</td></tr><tr><td>Iron (Fe)</td><td>Chlorophyll synthesis</td><td>Yellowing of young leaves</td></tr></tbody></table></div><h3 class="learn-subheading">3. Animal Nutrition</h3><p class="learn-p">Classes of food: carbohydrates (energy), proteins (growth and repair), fats/lipids (energy store, insulation), vitamins (metabolic regulation), minerals (body building), water (solvent, transport, cooling).</p><h3 class="learn-subheading">4. Digestion</h3><p class="learn-p">Mechanical digestion (physical breakdown) + chemical digestion (enzyme action). Key enzymes: amylase (starch→maltose), protease/pepsin (protein→peptides), lipase (fat→fatty acids + glycerol).</p><div class="learn-tip-box"><span class="learn-tip-icon">💡</span><strong>Photosynthesis:</strong> Light reactions in grana (thylakoids). Dark reactions in stroma (Calvin cycle). O₂ released from water (not CO₂).</div><div class="learn-tip-box"><span class="learn-tip-icon">🎯</span><strong>Summary:</strong> Photosynthesis: 6CO₂+6H₂O+light→C₆H₁₂O₆+6O₂. Limiting factors: light, CO₂, temp. Nitrogen deficiency → chlorosis. Digestion: amylase→starch, pepsin→protein, lipase→fat.</div>',
    questions: [
      {
        q: 'The overall equation for photosynthesis is:',
        o: [
          'C₆H₁₂O₆ + 6O₂ → 6CO₂ + 6H₂O + energy',
          '6CO₂ + 6H₂O + light energy → C₆H₁₂O₆ + 6O₂',
          '6O₂ + C₆H₁₂O₆ → CO₂ + H₂O',
          'CO₂ + H₂O → O₂ + glucose',
        ],
        a: 1,
        e: 'Photosynthesis: 6CO₂ + 6H₂O + light energy → C₆H₁₂O₆ + 6O₂. This is the reverse of aerobic respiration. The first option is aerobic respiration.',
        h: 'Photosynthesis: 6CO₂ + 6H₂O + light → C₆H₁₂O₆ + 6O₂.',
        yr: 'SS2',
      },
      {
        q: 'Photosynthesis occurs in which organelle?',
        o: ['Mitochondrion', 'Ribosome', 'Chloroplast', 'Vacuole'],
        a: 2,
        e: 'Photosynthesis occurs in the chloroplast. The light reactions occur in the thylakoid membranes (grana); the dark reactions (Calvin cycle) occur in the stroma.',
        h: 'Photosynthesis in chloroplast: light reactions in grana; dark reactions in stroma.',
        yr: 'SS2',
      },
      {
        q: 'Which colour of light is LEAST absorbed by chlorophyll?',
        o: ['Red', 'Blue', 'Green — it is reflected, making leaves appear green', 'Violet'],
        a: 2,
        e: 'Chlorophyll absorbs mostly red and blue light for photosynthesis but reflects green light — which is why leaves appear green to our eyes. Green is the least useful wavelength for photosynthesis.',
        h: 'Chlorophyll absorbs red and blue; reflects green (hence green leaves).',
        yr: 'SS2',
      },
      {
        q: 'Which factor is a limiting factor for photosynthesis on a dull cloudy day?',
        o: ['Temperature', 'CO₂ concentration', 'Light intensity', 'Water availability'],
        a: 2,
        e: 'On a cloudy day, light intensity is reduced — fewer photons available to drive the light reactions. This makes light intensity the limiting factor (the factor preventing the rate from increasing). Increasing CO₂ or temperature would have little effect if light is limiting.',
        h: 'Cloudy day → light intensity is the limiting factor for photosynthesis.',
        yr: 'SS2',
      },
      {
        q: 'In the light reactions of photosynthesis, water molecules are split to:',
        o: [
          'Produce glucose',
          'Release CO₂',
          'Release O₂ (as a by-product) and provide electrons and H⁺ ions for ATP/NADPH production',
          'Fix carbon dioxide',
        ],
        a: 2,
        e: 'Water photolysis in light reactions: 2H₂O → 4H⁺ + 4e⁻ + O₂. The oxygen is released as a waste product. The H⁺ and electrons are used to produce ATP (via photophosphorylation) and NADPH, which power the Calvin cycle.',
        h: 'Light reactions: water photolysis → O₂ released; ATP and NADPH produced.',
        yr: 'SS2',
      },
      {
        q: 'The Calvin cycle (dark reactions) occurs in the:',
        o: [
          'Thylakoid membranes',
          'Grana',
          'Stroma of the chloroplast',
          'Outer chloroplast membrane',
        ],
        a: 2,
        e: 'The Calvin cycle (dark reactions/light-independent reactions) occurs in the stroma — the fluid matrix of the chloroplast surrounding the grana. It uses ATP and NADPH from the light reactions to fix CO₂ into glucose.',
        h: 'Calvin cycle (dark reactions): stroma of chloroplast. Uses ATP and NADPH to fix CO₂.',
        yr: 'SS2',
      },
      {
        q: 'Nitrogen deficiency in plants causes:',
        o: [
          'Purple coloration of leaves',
          'Brown leaf margins',
          'Stunted growth and yellowing of leaves (chlorosis) — N needed for proteins and chlorophyll',
          'Wilting of leaves',
        ],
        a: 2,
        e: 'Nitrogen is needed for amino acids, proteins, nucleic acids (DNA, RNA), and chlorophyll. Deficiency: stunted growth (insufficient proteins for growth) and chlorosis (yellowing — insufficient chlorophyll).',
        h: 'Nitrogen deficiency: stunted growth + chlorosis (yellowing). N needed for protein and chlorophyll.',
        yr: 'SS2',
      },
      {
        q: 'Which mineral is the central atom of the chlorophyll molecule?',
        o: ['Iron', 'Calcium', 'Potassium', 'Magnesium'],
        a: 3,
        e: 'Magnesium is the central atom (metalloporphyrin centre) of the chlorophyll molecule. Magnesium deficiency causes interveinal chlorosis (yellowing between leaf veins while veins remain green).',
        h: 'Magnesium is the central atom of chlorophyll. Deficiency → interveinal chlorosis.',
        yr: 'SS2',
      },
      {
        q: 'Which class of food is the primary source of energy in the human diet?',
        o: ['Proteins', 'Fats', 'Carbohydrates', 'Vitamins'],
        a: 2,
        e: "Carbohydrates (glucose, starch, glycogen) are the body's primary energy source. They are broken down to glucose, which is used in cellular respiration to produce ATP. Fats are secondary energy stores; proteins are mainly for growth and repair.",
        h: 'Carbohydrates = primary energy source for the body.',
        yr: 'SS2',
      },
      {
        q: 'The enzyme amylase breaks down:',
        o: [
          'Proteins into amino acids',
          'Fats into fatty acids and glycerol',
          'Starch into maltose (then glucose)',
          'Cellulose into glucose',
        ],
        a: 2,
        e: 'Amylase is a carbohydrase that breaks starch into maltose (disaccharide), which is further broken down to glucose by maltase. Salivary amylase (in mouth) and pancreatic amylase (in small intestine) catalyse this reaction.',
        h: 'Amylase: starch → maltose (then glucose). Found in saliva and pancreas.',
        yr: 'SS2',
      },
      {
        q: 'Lipase acts on:',
        o: [
          'Starch',
          'Proteins',
          'Fats (lipids) — breaking them into fatty acids and glycerol',
          'DNA',
        ],
        a: 2,
        e: 'Lipase breaks down fats (triglycerides) into fatty acids and glycerol. Pancreatic lipase acts in the small intestine. Bile emulsifies fat into small droplets, increasing the surface area for lipase action.',
        h: 'Lipase: fats → fatty acids + glycerol.',
        yr: 'SS2',
      },
      {
        q: 'Bile is produced by the liver and stored in the gallbladder. Its role in digestion is to:',
        o: [
          'Digest proteins enzymatically',
          'Neutralise acid from stomach and emulsify fats into tiny droplets, increasing surface area for lipase',
          'Absorb glucose into the blood',
          'Produce insulin to regulate blood glucose',
        ],
        a: 1,
        e: 'Bile is not an enzyme — it contains bile salts that emulsify large fat globules into tiny droplets (increased surface area for lipase). Bile also neutralises stomach acid (HCl) to maintain the optimal pH for intestinal enzymes.',
        h: 'Bile: emulsifies fats (increased SA for lipase) + neutralises stomach acid.',
        yr: 'SS2',
      },
      {
        q: 'Pepsin digests proteins in the stomach. It is secreted as inactive pepsinogen because:',
        o: [
          'It needs acid to activate it',
          'The cell would be digested by active pepsin before it could be secreted',
          'It is cheaper to produce inactive form',
          'It needs bile salts to work',
        ],
        a: 1,
        e: 'Pepsin is produced as inactive pepsinogen (a zymogen) to prevent the gastric cells from being digested by active enzyme before secretion. HCl in the stomach converts pepsinogen to pepsin. Similar protection exists for pancreatic enzymes.',
        h: 'Pepsin secreted as inactive pepsinogen to protect stomach cells. HCl activates it.',
        yr: 'SS2',
      },
      {
        q: 'In which part of the human digestive system is most absorption of digested food into the blood?',
        o: [
          'Stomach',
          'Large intestine',
          'Small intestine (ileum) — villi and microvilli increase surface area for absorption',
          'Oesophagus',
        ],
        a: 2,
        e: 'The small intestine (ileum) is the major site of absorption. Villi (finger-like projections) and microvilli (brush border) vastly increase the surface area for absorption of glucose, amino acids, fatty acids, vitamins, and minerals into the blood and lymph.',
        h: 'Small intestine (ileum): main absorption site. Villi and microvilli increase surface area.',
        yr: 'SS2',
      },
      {
        q: 'Heterotrophic nutrition means an organism:',
        o: [
          'Makes its own food using sunlight',
          'Cannot make its own food and must obtain organic molecules by consuming other organisms',
          'Uses chemical energy to make food',
          'Only eats plants',
        ],
        a: 1,
        e: 'Heterotrophs (heteros = different; trophos = feeder) cannot synthesise organic molecules from inorganic sources — they must consume pre-formed organic molecules from other organisms. Animals, fungi, and most bacteria are heterotrophs.',
        h: 'Heterotrophic: cannot make own food; must eat organic material from other organisms.',
        yr: 'SS2',
      },
      {
        q: 'Vitamin C (ascorbic acid) deficiency causes:',
        o: [
          'Rickets (weak bones)',
          'Scurvy — bleeding gums, poor wound healing, weakened blood vessels',
          'Night blindness',
          'Anaemia',
        ],
        a: 1,
        e: 'Vitamin C is needed for collagen synthesis (structural protein in blood vessels, gums, skin). Deficiency → scurvy: bleeding gums, delayed wound healing, easy bruising, joint pain.',
        h: 'Vitamin C deficiency → scurvy (bleeding gums, poor healing). Needed for collagen.',
        yr: 'SS2',
      },
      {
        q: 'The function of roughage (dietary fibre) in the diet is to:',
        o: [
          'Provide energy',
          'Supply essential amino acids',
          'Add bulk to food and stimulate peristalsis in the large intestine, aiding regular bowel movements',
          'Store vitamins in the body',
        ],
        a: 2,
        e: 'Dietary fibre (cellulose from plant cell walls) is indigestible but essential. It adds bulk to faeces, stimulates peristalsis (muscular contractions of intestinal wall), prevents constipation, and reduces risk of colon cancer.',
        h: 'Dietary fibre: adds bulk; stimulates peristalsis; prevents constipation.',
        yr: 'SS2',
      },
      {
        q: 'The process of peristalsis in the digestive system refers to:',
        o: [
          'Secretion of digestive enzymes',
          'Absorption of nutrients across the gut wall',
          'Wave-like muscular contractions that propel food along the alimentary canal',
          'Chemical digestion of proteins',
        ],
        a: 2,
        e: 'Peristalsis: rhythmic wave-like contractions of circular and longitudinal smooth muscles in the wall of the alimentary canal. These contractions push food bolus from the oesophagus to the anus without the need for gravity.',
        h: 'Peristalsis: wave-like muscular contractions propel food along the digestive tract.',
        yr: 'SS2',
      },
      {
        q: 'Which vitamin is produced in the skin when exposed to sunlight and prevents rickets?',
        o: ['Vitamin A', 'Vitamin B', 'Vitamin C', 'Vitamin D'],
        a: 3,
        e: 'Vitamin D (calciferol) is synthesised in the skin when ultraviolet light converts 7-dehydrocholesterol to vitamin D₃. It is essential for calcium absorption and bone mineralisation. Deficiency → rickets (soft, deformed bones in children) and osteomalacia (in adults).',
        h: 'Vitamin D: synthesised in skin by UV light; needed for calcium absorption. Deficiency → rickets.',
        yr: 'SS2',
      },
      {
        q: 'Phototropism in plants is a response to light that involves growth. It is an example of:',
        o: [
          'Chemical nutrition',
          'Autotrophic nutrition',
          "A plant's ability to respond to environmental stimuli — but photosynthesis is the nutritional process that requires light",
          'A form of animal nutrition',
        ],
        a: 2,
        e: 'Phototropism is a growth response (not a nutritional process) where shoots grow toward light. Photosynthesis is the nutritional process. The stem grows toward light (positive phototropism) due to uneven auxin distribution.',
        h: 'Photosynthesis is plant nutrition. Phototropism is a growth response (not nutrition).',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Transport Systems',
    topicCode: 'SS2-BIO-03',
    module: 'Transport',
    term: 'First Term',
    contentHTML:
      '<div class="learn-intro"><span class="learn-keyword">Transport systems</span> move substances from where they are absorbed or produced to where they are needed. Plants use xylem and phloem; animals (including humans) use the circulatory system.</div><h3 class="learn-subheading">1. Transport in Plants</h3><div class="learn-table-wrap"><table class="learn-table"><thead><tr><th>Tissue</th><th>Transports</th><th>Direction</th><th>Movement</th></tr></thead><tbody><tr><td>Xylem</td><td>Water and mineral salts</td><td>Root → stem → leaves (upward)</td><td>Transpiration pull (passive); dead cells</td></tr><tr><td>Phloem</td><td>Dissolved sugars (sucrose) and amino acids</td><td>Source to sink (bidirectional)</td><td>Active (translocation); living cells</td></tr></tbody></table></div><p class="learn-p"><strong>Transpiration:</strong> loss of water vapour from leaves mainly through stomata. Transpiration pull creates tension that draws water up the xylem.</p><p class="learn-p"><strong>Factors increasing transpiration:</strong> high temperature, low humidity, high light intensity, wind.</p><h3 class="learn-subheading">2. The Human Circulatory System</h3><ul class="learn-list"><li><strong>Double circulation:</strong> pulmonary (heart ↔ lungs) and systemic (heart ↔ body)</li><li><strong>Heart chambers:</strong> right atrium, right ventricle, left atrium, left ventricle</li><li><strong>Arteries:</strong> carry blood away from heart; thick walls; high pressure; oxygenated (except pulmonary artery)</li><li><strong>Veins:</strong> carry blood toward heart; thin walls; low pressure; valves prevent backflow; deoxygenated (except pulmonary vein)</li><li><strong>Capillaries:</strong> one cell thick; exchange of gases, nutrients, waste with tissues</li></ul><h3 class="learn-subheading">3. Blood Components</h3><ul class="learn-list"><li><strong>Red blood cells (erythrocytes):</strong> carry O₂ via haemoglobin; no nucleus; biconcave disc shape</li><li><strong>White blood cells (leucocytes):</strong> immune defence; phagocytes (engulf bacteria) and lymphocytes (antibodies)</li><li><strong>Platelets (thrombocytes):</strong> blood clotting</li><li><strong>Plasma:</strong> liquid matrix; transports dissolved nutrients, hormones, CO₂, waste</li></ul><div class="learn-tip-box"><span class="learn-tip-icon">💡</span><strong>Key:</strong> Xylem = water (upward, dead cells). Phloem = sugars (bidirectional, living cells). Arteries = away from heart. Veins = toward heart (valves). Capillaries = exchange.</div><div class="learn-tip-box"><span class="learn-tip-icon">🎯</span><strong>Summary:</strong> Xylem: water+minerals, upward, transpiration pull. Phloem: sucrose, translocation. Double circulation. Arteries (high pressure), veins (valves), capillaries (exchange). RBC: haemoglobin + O₂.</div>',
    questions: [
      {
        q: 'Xylem vessels transport:',
        o: [
          'Dissolved sugars from leaves to roots',
          'Water and dissolved mineral salts from roots to leaves (upward)',
          'Amino acids from storage to growing regions',
          'CO₂ from leaves to roots',
        ],
        a: 1,
        e: 'Xylem transports water and mineral salts absorbed by roots upward through the stem to leaves and other organs. This is driven by transpiration pull (suction from water evaporation in leaves). Xylem is made of dead cells.',
        h: 'Xylem: water + mineral salts, upward, transpiration pull, dead cells.',
        yr: 'SS2',
      },
      {
        q: 'Phloem differs from xylem in that phloem:',
        o: [
          'Transports water only',
          'Is made of dead cells',
          'Transports dissolved organic solutes (mainly sucrose) and is made of living cells',
          'Only transports upward',
        ],
        a: 2,
        e: 'Phloem transports dissolved organic solutes (sucrose, amino acids) from sources (leaves) to sinks (roots, fruits, growing regions) by active translocation. Phloem is made of living sieve tube elements and companion cells.',
        h: 'Phloem: sucrose/amino acids, living cells, bidirectional translocation.',
        yr: 'SS2',
      },
      {
        q: 'Transpiration is the loss of water vapour from plants, mainly through:',
        o: ['Roots', 'Lenticels in bark', 'Stomata in leaves', 'Xylem vessels directly'],
        a: 2,
        e: 'About 90% of transpiration occurs through stomata (pores in leaf epidermis, each surrounded by guard cells). Stomata open during the day for gas exchange and close at night to reduce water loss.',
        h: 'Transpiration: mainly through stomata (90%). Guard cells control stomatal opening.',
        yr: 'SS2',
      },
      {
        q: 'Transpiration rate increases with:',
        o: [
          'High humidity and still air',
          'Low temperature and darkness',
          'High temperature, low humidity, wind, and high light intensity',
          'Stomatal closure',
        ],
        a: 2,
        e: 'Transpiration increases with: high temperature (more evaporation), low humidity (greater concentration gradient), wind (removes water vapour from around leaves), and high light intensity (stomata open wider). High humidity and still air reduce the gradient.',
        h: 'Transpiration increases: high temp, low humidity, wind, bright light.',
        yr: 'SS2',
      },
      {
        q: 'The human heart has how many chambers?',
        o: ['2', '3', '4 (right atrium, right ventricle, left atrium, left ventricle)', '6'],
        a: 2,
        e: 'The human heart has four chambers: right atrium (receives deoxygenated blood from body), right ventricle (pumps to lungs), left atrium (receives oxygenated blood from lungs), left ventricle (pumps oxygenated blood to body).',
        h: 'Human heart: 4 chambers. Right side = deoxygenated blood. Left side = oxygenated blood.',
        yr: 'SS2',
      },
      {
        q: 'Arteries differ from veins in that arteries:',
        o: [
          'Carry blood toward the heart',
          'Have thinner walls and valves',
          'Carry blood away from the heart and have thick, elastic, muscular walls (high pressure)',
          'Are found only near the lungs',
        ],
        a: 2,
        e: 'Arteries: carry blood AWAY from heart; thick elastic muscular walls (withstand high blood pressure); no valves. Veins: carry blood TOWARD heart; thinner walls; lower pressure; have valves to prevent backflow.',
        h: 'Arteries: away from heart; thick walls; high pressure. Veins: toward heart; valves.',
        yr: 'SS2',
      },
      {
        q: 'Capillaries are important for exchange of substances because:',
        o: [
          'They have thick walls',
          'Their walls are only one cell thick (thin), allowing easy diffusion of gases, nutrients, and waste between blood and tissues',
          'They carry oxygenated blood at high pressure',
          'They have valves to prevent backflow',
        ],
        a: 1,
        e: 'Capillaries have walls only one cell thick (simple squamous epithelium) — very thin for maximum diffusion efficiency. O₂ and glucose diffuse from blood into tissues; CO₂ and waste diffuse from tissues into blood. Huge surface area.',
        h: 'Capillaries: one cell thick → easy diffusion of gases and nutrients between blood and tissues.',
        yr: 'SS2',
      },
      {
        q: 'Red blood cells are adapted for oxygen transport because they:',
        o: [
          'Have a large nucleus to produce more haemoglobin',
          'Have a biconcave shape and contain haemoglobin — giving large surface area and no nucleus for maximum haemoglobin content',
          'Are larger than other blood cells',
          'Have thick walls to withstand pressure',
        ],
        a: 1,
        e: 'Red blood cells (erythrocytes): (1) biconcave disc shape → increased surface area/volume ratio for O₂ diffusion; (2) no nucleus → maximum space for haemoglobin; (3) contain haemoglobin → reversibly binds O₂ (oxyhaemoglobin in lungs; releases O₂ to tissues).',
        h: 'RBC: biconcave (large SA), no nucleus (more haemoglobin space), haemoglobin carries O₂.',
        yr: 'SS2',
      },
      {
        q: 'In the double circulation system, the pulmonary circulation carries blood:',
        o: [
          'From heart to body organs and back',
          'From heart to lungs and back (for oxygenation)',
          'From intestines to liver',
          'From kidneys to heart',
        ],
        a: 1,
        e: 'Double circulation: (1) Pulmonary circulation: right ventricle → lungs → left atrium (blood is oxygenated). (2) Systemic circulation: left ventricle → body organs → right atrium (blood delivers O₂ and collects CO₂). Two separate circuits.',
        h: 'Pulmonary: heart ↔ lungs (oxygenation). Systemic: heart ↔ body (O₂ delivery).',
        yr: 'SS2',
      },
      {
        q: 'The valves in veins prevent:',
        o: [
          'Blood from flowing too fast',
          'Backflow of blood — ensuring one-way flow toward the heart',
          'Exchange of gases',
          'Blood clotting',
        ],
        a: 1,
        e: 'Veins have lower blood pressure than arteries. Without valves, blood would flow backward (away from heart) under gravity. Venous valves are semi-lunar and open when blood flows toward the heart, closing to prevent backflow.',
        h: 'Venous valves: prevent backflow — ensure one-way blood flow toward the heart.',
        yr: 'SS2',
      },
      {
        q: 'White blood cells (leucocytes) function in:',
        o: [
          'Carrying oxygen around the body',
          'Blood clotting',
          'Transporting CO₂',
          'Immune defence — phagocytosis (engulfing pathogens) and antibody production',
        ],
        a: 3,
        e: 'White blood cells are the main cellular components of the immune system. Phagocytes (neutrophils, macrophages) engulf and destroy bacteria/debris. Lymphocytes produce antibodies and provide specific immunity against pathogens.',
        h: 'WBCs: immune defence. Phagocytes engulf bacteria. Lymphocytes produce antibodies.',
        yr: 'SS2',
      },
      {
        q: 'Plasma is the liquid component of blood that transports:',
        o: [
          'Only oxygen and carbon dioxide',
          'Dissolved nutrients (glucose, amino acids), hormones, CO₂, urea, and heat',
          'Only red and white blood cells',
          'Only clotting factors',
        ],
        a: 1,
        e: 'Blood plasma (55% of blood volume) transports: dissolved nutrients (glucose, amino acids, fatty acids), hormones, CO₂ (dissolved as bicarbonate), urea (waste), heat, ions, and proteins (albumin, fibrinogen).',
        h: 'Plasma: liquid part of blood; transports dissolved nutrients, hormones, CO₂, urea.',
        yr: 'SS2',
      },
      {
        q: 'The transpiration pull (cohesion-tension theory) explains water movement in xylem by:',
        o: [
          'Active pumping of water up xylem vessels',
          'Root pressure pushing water up',
          'Evaporation from leaves creates tension/negative pressure pulling water columns up xylem — held together by cohesion between water molecules',
          'Gravity pulling water from leaves to roots',
        ],
        a: 2,
        e: 'Cohesion-tension theory: evaporation from leaves (transpiration) creates water potential gradient; water molecules are pulled up xylem as a continuous column due to cohesion (hydrogen bonding between water molecules) and adhesion (water to xylem walls).',
        h: 'Cohesion-tension: transpiration → tension → water pulled up xylem as continuous column.',
        yr: 'SS2',
      },
      {
        q: 'Guard cells control stomatal opening. Stomata open when guard cells:',
        o: [
          'Lose water and become flaccid',
          'Become turgid (absorb water) — the sausage-shaped guard cells bow outward, opening the pore',
          'Die during the day',
          'Secrete CO₂',
        ],
        a: 1,
        e: 'Guard cells are sausage-shaped (bean-shaped in cross-section) with thicker inner walls. When turgid (water absorbed → increased turgor pressure), the thinner outer walls expand more → cells bow outward → pore opens. Flaccid guard cells (water loss) → stomata close.',
        h: 'Stomata open: guard cells become turgid (absorb water) → bow outward → pore opens.',
        yr: 'SS2',
      },
      {
        q: 'Sickle cell anaemia is a hereditary disease affecting red blood cells. The abnormal haemoglobin causes RBCs to:',
        o: [
          'Become larger than normal',
          'Become sickle (crescent) shaped, which can block capillaries and leads to reduced O₂ carrying capacity',
          'Have too much haemoglobin',
          'Become white blood cells',
        ],
        a: 1,
        e: 'In sickle cell anaemia, a single amino acid substitution in haemoglobin causes it to polymerise when deoxygenated, deforming RBCs into sickle shapes. These rigid cells block capillaries (crisis) and are destroyed faster than normal (anaemia).',
        h: 'Sickle cell: abnormal Hb → sickle-shaped RBCs → block capillaries + reduced O₂ capacity.',
        yr: 'SS2',
      },
      {
        q: 'The role of platelets (thrombocytes) in blood is:',
        o: [
          'Carrying O₂',
          'Producing antibodies',
          'Blood clotting — forming a platelet plug at wound sites and triggering the clotting cascade',
          'Engulfing bacteria',
        ],
        a: 2,
        e: 'Platelets (small cell fragments) accumulate at wound sites, forming a plug. They release factors that trigger the clotting cascade: fibrinogen → fibrin, forming a mesh that traps RBCs to form a clot (thrombus) sealing the wound.',
        h: 'Platelets: blood clotting. Plug wound + trigger fibrinogen → fibrin clot.',
        yr: 'SS2',
      },
      {
        q: 'Root pressure contributes to water uptake in plants because:',
        o: [
          'Roots actively secrete water',
          'Ions are actively transported into the xylem, lowering water potential in xylem — causing water to enter by osmosis, building positive pressure',
          'Roots absorb sunlight',
          'Root cells transpire more than leaves',
        ],
        a: 1,
        e: 'Root pressure: mineral ions are actively transported into root xylem, lowering water potential. Water follows by osmosis from soil into xylem, creating a positive pressure that can push water up the stem (especially at night when transpiration is low). Visible as guttation.',
        h: 'Root pressure: active ion transport into xylem → osmosis of water → positive upward pressure.',
        yr: 'SS2',
      },
      {
        q: 'Blood type (ABO system) is determined by:',
        o: [
          'Number of red blood cells',
          'Antigens on the surface of red blood cells (A, B, AB, or O antigens)',
          'The colour of haemoglobin',
          'Platelet count',
        ],
        a: 1,
        e: 'ABO blood groups: type A (A antigens, anti-B antibodies), type B (B antigens, anti-A antibodies), type AB (both antigens, no antibodies — universal recipient), type O (no antigens, both antibodies — universal donor). Transfusion must match to avoid agglutination.',
        h: 'Blood type (ABO): determined by antigens on RBC surface. O = universal donor; AB = universal recipient.',
        yr: 'SS2',
      },
      {
        q: 'The hepatic portal vein carries blood from:',
        o: [
          'Heart to liver',
          'Liver to kidneys',
          'Intestines to liver — blood rich in absorbed nutrients after digestion',
          'Lungs to heart',
        ],
        a: 2,
        e: 'The hepatic portal vein carries nutrient-rich blood (glucose, amino acids, fatty acids) absorbed from the small intestine directly to the liver. The liver processes these nutrients — storing excess glucose as glycogen, deaminating excess amino acids.',
        h: 'Hepatic portal vein: intestines → liver (carries absorbed nutrients from digestion).',
        yr: 'SS2',
      },
      {
        q: 'Haemoglobin in red blood cells combines with oxygen to form:',
        o: [
          'Carboxyhaemoglobin',
          'Oxyhaemoglobin (HbO₂) in the lungs; releases O₂ to tissues in low O₂ conditions',
          'Deoxyribose',
          'Methaemoglobin',
        ],
        a: 1,
        e: 'Hb + O₂ ⇌ HbO₂ (oxyhaemoglobin). In lungs (high O₂ partial pressure), Hb loads O₂. In tissues (low O₂, high CO₂), Hb unloads O₂ to cells. CO binds more strongly than O₂, forming carboxyhaemoglobin (CO poisoning).',
        h: 'Haemoglobin + O₂ → oxyhaemoglobin (in lungs). Releases O₂ in tissues.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Gaseous Exchange',
    topicCode: 'SS2-BIO-04',
    module: 'Respiration',
    term: 'First Term',
    contentHTML:
      '<div class="learn-intro"><span class="learn-keyword">Gaseous exchange</span> is the process by which organisms take in oxygen (for respiration) and release carbon dioxide (a waste product). Exchange surfaces are adapted for efficiency in different organisms.</div><h3 class="learn-subheading">1. Respiratory Surfaces: Adaptations</h3><p class="learn-p">All efficient gaseous exchange surfaces share:</p><ul class="learn-list"><li>Large surface area (to maximise diffusion rate)</li><li>Thin walls (short diffusion distance)</li><li>Moist surface (gases dissolve in moisture for diffusion)</li><li>Rich blood supply (maintains concentration gradient)</li></ul><h3 class="learn-subheading">2. Gaseous Exchange in Different Organisms</h3><div class="learn-table-wrap"><table class="learn-table"><thead><tr><th>Organism</th><th>Exchange Surface</th><th>Adaptation</th></tr></thead><tbody><tr><td>Fish</td><td>Gills</td><td>Gill filaments and lamellae; counter-current flow maximises O₂ uptake</td></tr><tr><td>Insects</td><td>Tracheae/spiracles</td><td>Air tubes deliver O₂ directly to cells; no blood used for gas transport</td></tr><tr><td>Earthworm</td><td>Moist skin</td><td>Gases diffuse through moist mucus-covered body surface</td></tr><tr><td>Frogs</td><td>Lungs + moist skin</td><td>Both respiratory surfaces used</td></tr><tr><td>Humans</td><td>Alveoli in lungs</td><td>300 million alveoli; vast surface area ~70 m²</td></tr></tbody></table></div><h3 class="learn-subheading">3. Human Respiratory System</h3><p class="learn-p">Pathway: nose → trachea → bronchi → bronchioles → alveoli. Alveoli adaptations: thin walls (one cell), moist surface, surrounded by capillaries, large number (huge SA).</p><h3 class="learn-subheading">4. Cellular Respiration</h3><ul class="learn-list"><li><strong>Aerobic:</strong> C₆H₁₂O₆ + 6O₂ → 6CO₂ + 6H₂O + 38 ATP (in mitochondria)</li><li><strong>Anaerobic in animals:</strong> glucose → lactic acid + 2 ATP (no O₂)</li><li><strong>Anaerobic in yeast:</strong> glucose → ethanol + CO₂ + 2 ATP (fermentation)</li></ul><div class="learn-tip-box"><span class="learn-tip-icon">💡</span><strong>Aerobic vs Anaerobic:</strong> Aerobic = 38 ATP, complete oxidation, needs O₂. Anaerobic = 2 ATP, incomplete, no O₂ needed, produces lactic acid (animals) or ethanol+CO₂ (yeast).</div><div class="learn-tip-box"><span class="learn-tip-icon">🎯</span><strong>Summary:</strong> Exchange surface: large SA, thin, moist, good blood supply. Alveoli (humans), gills (fish), tracheae (insects). Aerobic: 38 ATP. Anaerobic: 2 ATP + lactic acid or ethanol.</div>',
    questions: [
      {
        q: 'Which of the following is NOT a feature of an efficient gaseous exchange surface?',
        o: [
          'Large surface area',
          'Thin walls for short diffusion distance',
          'Dry surface for maximum gas permeability',
          'Rich blood supply to maintain concentration gradient',
        ],
        a: 2,
        e: 'Exchange surfaces must be moist (not dry) — gases must dissolve in the moisture layer to diffuse across. A dry surface would prevent dissolution and stop diffusion. All other features (large SA, thin walls, good blood supply) are essential adaptations.',
        h: 'Exchange surface must be MOIST (not dry). Large SA, thin walls, good blood supply also needed.',
        yr: 'SS2',
      },
      {
        q: 'The alveoli of the human lungs are adapted for gaseous exchange by having:',
        o: [
          'Thick walls and small numbers',
          'A single large air sac per lung',
          'About 300 million alveoli providing ~70 m² surface area, thin walls (one cell), moist lining, and dense capillary network',
          'Cartilage rings to keep them open',
        ],
        a: 2,
        e: 'Alveoli adaptations: ~300 million → huge surface area (~70 m²); walls one cell thin (0.2 μm) → short diffusion distance; moist lining → dissolves gases; surrounded by capillaries → maintains O₂/CO₂ gradient.',
        h: 'Alveoli: 300 million → 70 m² SA; one cell thick; moist; dense capillary network.',
        yr: 'SS2',
      },
      {
        q: 'Fish gills use counter-current flow to maximise oxygen uptake. This means:',
        o: [
          'Blood and water flow in the same direction',
          'Blood and water flow in opposite directions — maintaining a concentration gradient of O₂ from water into blood along the entire gill length',
          'Blood does not absorb oxygen from water',
          'Gills pump water using muscular effort',
        ],
        a: 1,
        e: 'Counter-current exchange: blood flows in the opposite direction to water over the gill lamellae. This ensures the blood is always meeting water with higher O₂ concentration throughout the gill — maintaining the gradient and achieving up to 80% O₂ extraction from water.',
        h: 'Counter-current: blood and water flow oppositely → O₂ gradient maintained throughout → ~80% O₂ extracted.',
        yr: 'SS2',
      },
      {
        q: 'Insects use tracheae for gaseous exchange. This system is efficient because:',
        o: [
          'Blood carries oxygen to cells',
          'Air is pumped by the heart',
          'Air is delivered directly to cells through a branching system of tubes — no blood needed for gas transport',
          'Insects have very large lungs',
        ],
        a: 2,
        e: 'Insects have a tracheal system: air enters through spiracles → passes through tracheae → tracheal branches (tracheoles) → cells. Oxygen diffuses directly to cells; no need for blood to transport gases. This system only works for small animals (short diffusion distances).',
        h: 'Insect tracheae: air delivered directly to cells through tubes. No blood for gas transport.',
        yr: 'SS2',
      },
      {
        q: 'Aerobic respiration in eukaryotic cells occurs mainly in the:',
        o: ['Nucleus', 'Cell membrane', 'Mitochondria', 'Ribosome'],
        a: 2,
        e: 'Aerobic respiration: glycolysis occurs in the cytoplasm; Krebs cycle and electron transport chain (ETC) occur in the mitochondria. Most ATP (34 of 38 total) is produced in the ETC in the inner mitochondrial membrane.',
        h: 'Aerobic respiration: glycolysis in cytoplasm; Krebs cycle + ETC in mitochondria.',
        yr: 'SS2',
      },
      {
        q: 'The overall equation for aerobic respiration is:',
        o: [
          'C₆H₁₂O₆ + 6O₂ → 6CO₂ + 6H₂O + 38 ATP',
          'C₆H₁₂O₆ → 2C₂H₅OH + 2CO₂ + 2 ATP',
          'C₆H₁₂O₆ → 2C₃H₆O₃ + 2 ATP',
          '6CO₂ + 6H₂O + light → C₆H₁₂O₆ + 6O₂',
        ],
        a: 0,
        e: 'Aerobic respiration: C₆H₁₂O₆ + 6O₂ → 6CO₂ + 6H₂O + 38 ATP. Complete oxidation of glucose; O₂ is consumed; CO₂ and water are produced; maximum ATP yield.',
        h: 'Aerobic: C₆H₁₂O₆ + 6O₂ → 6CO₂ + 6H₂O + 38 ATP.',
        yr: 'SS2',
      },
      {
        q: 'During vigorous exercise, muscles may respire anaerobically. The product in animal muscle cells is:',
        o: [
          'Ethanol and CO₂',
          'Lactic acid (lactate) + 2 ATP — causing muscle fatigue',
          'Glucose and water',
          'Glycogen and oxygen',
        ],
        a: 1,
        e: 'Animal anaerobic respiration: glucose → 2 lactic acid + 2 ATP (no O₂). Lactic acid accumulates → muscle fatigue and pain. After exercise, extra O₂ is used (oxygen debt) to oxidise lactic acid back to glucose in the liver.',
        h: 'Animal anaerobic: glucose → lactic acid + 2 ATP. Causes muscle fatigue.',
        yr: 'SS2',
      },
      {
        q: 'Yeast carries out anaerobic respiration (fermentation) producing:',
        o: [
          'Lactic acid only',
          'Ethanol (alcohol) + CO₂ + 2 ATP',
          'Only ATP and no waste',
          'Oxygen and glucose',
        ],
        a: 1,
        e: 'Yeast (facultative anaerobe) fermentation: C₆H₁₂O₆ → 2C₂H₅OH + 2CO₂ + 2 ATP. Used in beer/wine brewing and bread making (CO₂ makes dough rise). Ethanol is the alcohol in alcoholic beverages.',
        h: 'Yeast fermentation: glucose → ethanol + CO₂ + 2 ATP.',
        yr: 'SS2',
      },
      {
        q: 'Breathing (ventilation) differs from gaseous exchange in that breathing:',
        o: [
          'Is the same as gaseous exchange',
          'Refers to the physical movement of air into and out of the lungs (inhalation and exhalation) — gaseous exchange refers to diffusion of O₂ and CO₂ across alveolar walls',
          'Occurs at cellular level',
          'Requires no energy',
        ],
        a: 1,
        e: 'Breathing (ventilation) = physical process of inhaling/exhaling air using muscular effort (diaphragm, intercostal muscles). Gaseous exchange = diffusion of O₂ into blood and CO₂ into alveolar air across the alveolar surface. Respiration = cellular process releasing energy.',
        h: 'Breathing = ventilation (physical air movement). Gaseous exchange = diffusion at alveoli. Respiration = cellular energy release.',
        yr: 'SS2',
      },
      {
        q: 'During inhalation, the diaphragm:',
        o: [
          'Relaxes and moves upward',
          'Contracts and moves downward (flattens) — increasing thoracic volume and decreasing pressure, drawing air in',
          'Remains stationary',
          'Moves left and right alternately',
        ],
        a: 1,
        e: 'Inhalation: (1) Diaphragm contracts → flattens (moves down). (2) External intercostal muscles contract → ribs move up and out. (3) Thoracic volume increases → pressure falls below atmospheric → air flows in. Exhalation reverses this process.',
        h: 'Inhalation: diaphragm contracts (flattens) + ribs up/out → volume increases → pressure falls → air in.',
        yr: 'SS2',
      },
      {
        q: 'Earthworms carry out gaseous exchange through their skin. This is possible because:',
        o: [
          'They have many lungs',
          'Their skin is thick and waterproof',
          'Their body surface is thin and kept moist by mucus secretions — allowing O₂ and CO₂ to diffuse across directly into the blood',
          'They have gills under their skin',
        ],
        a: 2,
        e: 'Earthworm cutaneous respiration: skin is thin, permeable, and kept moist by mucus. O₂ from soil air dissolves in mucus → diffuses across skin → into blood capillaries just below skin. CO₂ diffuses outward. This is why earthworms need moist environments and die if they dry out.',
        h: 'Earthworm: cutaneous (skin) respiration. Thin moist skin → diffusion of O₂ into blood capillaries.',
        yr: 'SS2',
      },
      {
        q: 'Aerobic respiration releases more ATP than anaerobic respiration because:',
        o: [
          'Aerobic occurs faster',
          'Aerobic uses more glucose',
          'Aerobic completely oxidises glucose using O₂, via Krebs cycle and electron transport chain, extracting maximum energy (38 ATP vs 2 ATP)',
          'Anaerobic requires more enzymes',
        ],
        a: 2,
        e: 'Aerobic: glucose completely oxidised → 38 ATP (glycolysis 2 + Krebs cycle 2 + ETC 34). Anaerobic: glucose incompletely broken down → only 2 ATP (glycolysis only; pyruvate is converted to lactic acid or ethanol instead of entering the Krebs cycle).',
        h: 'Aerobic: 38 ATP (complete oxidation via Krebs + ETC). Anaerobic: 2 ATP (glycolysis only).',
        yr: 'SS2',
      },
      {
        q: 'The rate of gaseous exchange in insects is controlled by:',
        o: [
          'Heart rate changes',
          'Gill movements',
          'Opening and closing of spiracles — valves on the body surface controlling air entry into tracheae',
          'Skin moisture levels',
        ],
        a: 2,
        e: 'Insects control gas exchange by opening/closing spiracles (pores on abdomen/thorax). Open spiracles allow O₂ in and CO₂ out but also allow water vapour to escape. Insects close spiracles to reduce water loss in hot/dry conditions.',
        h: 'Insects: spiracles open/close to control gas exchange and limit water loss.',
        yr: 'SS2',
      },
      {
        q: 'Oxygen debt after exercise refers to:',
        o: [
          'The amount of O₂ still in the blood',
          'The extra O₂ consumed above resting level after exercise — used to oxidise lactic acid (convert back to glucose/glycogen) and restore normal conditions',
          'Reduction in lung capacity',
          'Blood containing insufficient haemoglobin',
        ],
        a: 1,
        e: 'After exercise, extra O₂ is needed to: (1) oxidise accumulated lactic acid back to glucose (in liver); (2) restore ATP and phosphocreatine levels; (3) maintain elevated temperature and heart rate during recovery. This excess O₂ consumption = oxygen debt (EPOC).',
        h: 'Oxygen debt: extra O₂ after exercise to oxidise lactic acid back to glucose.',
        yr: 'SS2',
      },
      {
        q: 'The trachea in humans is kept permanently open by:',
        o: [
          'Mucus lining',
          'Cilia on its surface',
          'C-shaped rings of cartilage',
          'Smooth muscle contractions',
        ],
        a: 2,
        e: 'The trachea has C-shaped rings of hyaline cartilage (incomplete at the back to allow the oesophagus to expand during swallowing). These rigid rings prevent the trachea from collapsing during inhalation (when pressure falls inside).',
        h: 'Trachea: C-shaped cartilage rings prevent collapse during inhalation.',
        yr: 'SS2',
      },
      {
        q: 'Mucus and cilia in the respiratory tract work together to:',
        o: [
          'Speed up gas exchange',
          'Increase surface area of lungs',
          'Trap dust/bacteria in mucus; cilia beat to move mucus and trapped particles up to the throat (mucociliary escalator)',
          'Produce surfactant in alveoli',
        ],
        a: 2,
        e: 'Mucociliary escalator: goblet cells produce mucus that traps inhaled particles (dust, bacteria, pollen). Cilia on respiratory epithelium beat rhythmically, moving mucus up toward the throat where it is swallowed. Smoking destroys cilia → chronic bronchitis.',
        h: 'Mucociliary escalator: mucus traps particles; cilia beat upward → debris swallowed.',
        yr: 'SS2',
      },
      {
        q: 'The oxygen dissociation curve of haemoglobin shows that:',
        o: [
          'O₂ binding is linear and constant',
          'Haemoglobin has very low affinity for oxygen at all pressures',
          'Haemoglobin loads O₂ efficiently at high O₂ partial pressure (in lungs) and unloads it efficiently at low O₂ pressure (in tissues) — S-shaped cooperative binding curve',
          'Haemoglobin cannot release O₂',
        ],
        a: 2,
        e: 'The S-shaped (sigmoidal) O₂ dissociation curve reflects cooperative binding: Hb loads O₂ rapidly at high pO₂ (lungs ~100 mmHg) and releases O₂ rapidly at low pO₂ (tissues ~40 mmHg or lower during exercise). The Bohr effect: CO₂ shifts curve right → more O₂ released to active tissues.',
        h: 'O₂ dissociation: S-shaped curve. High pO₂ (lungs) → loads O₂. Low pO₂ (tissues) → unloads O₂.',
        yr: 'SS2',
      },
      {
        q: 'Emphysema is a lung disease that reduces gaseous exchange efficiency because:',
        o: [
          'Airways become blocked by mucus',
          'The alveolar walls break down — reducing the surface area for gaseous exchange, and reducing lung elasticity',
          'The diaphragm becomes paralysed',
          'Blood becomes unable to carry CO₂',
        ],
        a: 1,
        e: 'Emphysema (mainly caused by smoking): enzymes destroy alveolar walls → alveoli merge into larger air spaces → drastically reduced surface area for gas exchange → difficulty breathing/O₂ shortage. Elastin loss reduces lung recoil → air trapped in lungs.',
        h: 'Emphysema: alveolar walls destroyed → reduced SA for gas exchange → breathlessness.',
        yr: 'SS2',
      },
      {
        q: 'What is the role of surfactant in the alveoli?',
        o: [
          'Speeds up diffusion of O₂',
          'Reduces surface tension of alveolar fluid — preventing alveoli from collapsing on exhalation',
          'Kills bacteria in the lungs',
          'Produces antibodies in the lungs',
        ],
        a: 1,
        e: 'Surfactant is a phospholipid-protein mixture secreted by type II alveolar cells. It reduces the surface tension of the fluid lining the alveoli, preventing them from collapsing during exhalation. Premature babies often lack surfactant → respiratory distress syndrome.',
        h: 'Surfactant: reduces alveolar surface tension → prevents alveolar collapse on exhalation.',
        yr: 'SS2',
      },
      {
        q: 'Carbon dioxide is transported in the blood mainly as:',
        o: [
          'CO₂ dissolved in plasma only',
          'Bound to haemoglobin as carboxyhaemoglobin',
          'Bicarbonate ions (HCO₃⁻) in plasma — formed when CO₂ dissolves in water in RBCs and dissociates',
          'CO₂ in white blood cells',
        ],
        a: 2,
        e: 'CO₂ transport: ~70% as bicarbonate (HCO₃⁻): CO₂ + H₂O ⇌ H₂CO₃ ⇌ H⁺ + HCO₃⁻ (catalysed by carbonic anhydrase in RBCs); ~23% bound to haemoglobin (carbaminohaemoglobin); ~7% dissolved in plasma.',
        h: 'CO₂ transport: mainly as HCO₃⁻ (bicarbonate) in plasma — 70%.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Excretion and Osmoregulation',
    topicCode: 'SS2-BIO-05',
    module: 'Excretion',
    term: 'First Term',
    contentHTML:
      '<div class="learn-intro"><span class="learn-keyword">Excretion</span> is the removal of metabolic waste products from the body. <span class="learn-keyword">Osmoregulation</span> is the control of water and solute concentrations in body fluids.</div><h3 class="learn-subheading">1. Excretory Organs and Waste Products</h3><div class="learn-table-wrap"><table class="learn-table"><thead><tr><th>Organ</th><th>Waste Product Excreted</th></tr></thead><tbody><tr><td>Kidneys</td><td>Urea, excess water, excess salts, uric acid</td></tr><tr><td>Lungs</td><td>CO₂ and water vapour</td></tr><tr><td>Skin</td><td>Sweat: water, salts, small amounts of urea</td></tr><tr><td>Liver</td><td>Produces urea (deamination of excess amino acids); bile pigments</td></tr></tbody></table></div><h3 class="learn-subheading">2. The Kidney and Nephron</h3><p class="learn-p">Each kidney contains about 1 million nephrons. Urine formation involves three processes:</p><ul class="learn-list"><li><strong>Ultrafiltration:</strong> high pressure in glomerular capillaries forces small molecules (water, glucose, urea, salts) into the Bowman\'s capsule. Blood cells and large proteins remain in blood.</li><li><strong>Selective reabsorption:</strong> all glucose, most water, and essential salts are reabsorbed back into blood in the proximal convoluted tubule and loop of Henlé.</li><li><strong>Tubular secretion:</strong> some waste products are actively secreted from blood into the tubule.</li></ul><h3 class="learn-subheading">3. Osmoregulation</h3><ul class="learn-list"><li>Controlled by ADH (antidiuretic hormone) from the pituitary gland</li><li>High blood osmolarity (dehydration) → more ADH released → kidneys reabsorb more water → concentrated urine</li><li>Low blood osmolarity (overhydration) → less ADH → dilute urine</li></ul><h3 class="learn-subheading">4. Excretion in Plants</h3><p class="learn-p">Plants produce CO₂ (respiration) and O₂ (photosynthesis) as waste gases, released through stomata and lenticels. Some waste products are stored in leaves that fall off (e.g., tannins, resins).</p><div class="learn-tip-box"><span class="learn-tip-icon">💡</span><strong>Urea Formation:</strong> Liver deaminates excess amino acids → ammonia (toxic) + organic acids. Ammonia + CO₂ → urea (less toxic) via ornithine cycle. Urea excreted by kidneys.</div><div class="learn-tip-box"><span class="learn-tip-icon">🎯</span><strong>Summary:</strong> Excretion ≠ egestion. Kidneys: ultrafiltration → selective reabsorption → tubular secretion → urine. ADH: controls water reabsorption. Liver: deamination → urea. Sweat ≠ urine (less urea).</div>',
    questions: [
      {
        q: 'Excretion is best defined as the removal of:',
        o: [
          'Undigested food (egestion)',
          'Metabolic waste products produced by chemical reactions inside cells',
          'Excess food from the diet',
          'Carbon dioxide from the atmosphere',
        ],
        a: 1,
        e: 'Excretion = removal of metabolic waste products (CO₂, urea, water, bile pigments) produced by chemical reactions inside cells. Egestion = removal of undigested food. Secretion = release of useful substances.',
        h: 'Excretion = removal of metabolic waste (from cell reactions). Not to be confused with egestion (undigested food).',
        yr: 'SS2',
      },
      {
        q: 'The main nitrogenous excretory product in humans is:',
        o: [
          'Ammonia',
          'Uric acid',
          'Urea — formed in the liver by deamination of excess amino acids',
          'Glucose',
        ],
        a: 2,
        e: 'In humans, excess amino acids cannot be stored. The liver removes the amino group (deamination): amino acid → ammonia (toxic) + organic acid. Ammonia is quickly converted to urea (less toxic, water-soluble) in the ornithine cycle. Urea passes to kidneys for excretion in urine.',
        h: 'Human nitrogenous waste = urea. Made in liver (deamination). Excreted by kidneys.',
        yr: 'SS2',
      },
      {
        q: 'Ultrafiltration in the kidney occurs at the:',
        o: [
          'Loop of Henlé',
          'Collecting duct',
          "Glomerulus (in the Bowman's capsule) — high blood pressure forces small molecules through the capillary walls",
          'Ureter',
        ],
        a: 2,
        e: "Ultrafiltration: the glomerulus is a knot of capillaries inside the Bowman's capsule with high blood pressure (efferent arteriole is narrower than afferent). This forces water, glucose, urea, uric acid, and salts into the Bowman's capsule. Blood cells and plasma proteins (too large) remain in blood.",
        h: "Ultrafiltration: glomerulus → high pressure forces small molecules into Bowman's capsule. Large proteins/cells remain.",
        yr: 'SS2',
      },
      {
        q: 'Selective reabsorption in the kidney ensures that:',
        o: [
          'All filtered substances are excreted',
          'Glucose (all) and most water and salts are reabsorbed from the filtrate back into blood, leaving waste in the tubule',
          'Only urea is reabsorbed',
          'Water is excreted while glucose is retained',
        ],
        a: 1,
        e: 'In the proximal convoluted tubule: all glucose (by active transport), 85% of water, and essential ions are reabsorbed into surrounding blood capillaries. In the loop of Henlé and DCT: further water and salt reabsorption. Final urine: urea, excess water, excess salts — no glucose.',
        h: 'Selective reabsorption: all glucose, most water, and essential ions reabsorbed. Urea remains.',
        yr: 'SS2',
      },
      {
        q: 'The hormone ADH (antidiuretic hormone) controls:',
        o: [
          'Glucose reabsorption in the kidney',
          'Blood pressure in the arteries',
          'Water reabsorption in the kidney collecting duct — more ADH → more water reabsorbed → concentrated urine',
          'Urea production in the liver',
        ],
        a: 2,
        e: 'ADH (vasopressin) from posterior pituitary: makes collecting duct and distal convoluted tubule more permeable to water. High blood osmolarity (dehydration) → more ADH → more water reabsorbed → small volume concentrated urine. Low osmolarity → less ADH → dilute urine.',
        h: 'ADH: controls water reabsorption in collecting duct. Dehydration → more ADH → concentrated urine.',
        yr: 'SS2',
      },
      {
        q: "Which of the following would be found in the filtrate in the Bowman's capsule but NOT in normal urine?",
        o: [
          'Urea',
          'Glucose — all filtered glucose is reabsorbed in the proximal convoluted tubule',
          'Excess salts',
          'Water',
        ],
        a: 1,
        e: "Glucose is filtered into the Bowman's capsule (present in filtrate) but is completely reabsorbed in the proximal convoluted tubule by active transport. Normal urine contains NO glucose. Glucose appears in urine (glucosuria) in untreated diabetes mellitus (blood glucose too high for all to be reabsorbed).",
        h: 'Glucose: in filtrate but not in normal urine (fully reabsorbed). Glucosuria = diabetes sign.',
        yr: 'SS2',
      },
      {
        q: 'The loop of Henlé in the nephron is important for:',
        o: [
          'Filtering blood under high pressure',
          'Creating a concentration gradient in the medulla that allows water to be reabsorbed from the collecting duct (producing concentrated urine)',
          'Secreting glucose into the tubule',
          'Producing ADH',
        ],
        a: 1,
        e: 'The loop of Henlé (descending and ascending limbs) creates an osmotic gradient in the renal medulla by actively pumping Na⁺ and Cl⁻ out of the ascending limb. This high-osmolarity medulla draws water out of the collecting duct (when ADH is present), concentrating urine.',
        h: 'Loop of Henlé: creates osmotic gradient in medulla → enables concentrated urine production.',
        yr: 'SS2',
      },
      {
        q: 'Deamination of amino acids occurs in the:',
        o: [
          'Kidney',
          'Lungs',
          'Small intestine',
          'Liver — amino groups removed → ammonia → converted to urea via ornithine cycle',
        ],
        a: 3,
        e: 'Deamination occurs in the liver: amino acid → R-group (organic acid, used for energy/glucose synthesis) + NH₂ (amino group → ammonia NH₃). NH₃ is toxic → ornithine cycle converts it to urea. Urea travels via blood to kidneys for excretion.',
        h: 'Deamination in liver: amino acid → organic acid + ammonia → urea (ornithine cycle).',
        yr: 'SS2',
      },
      {
        q: 'Sweating contributes to excretion but primarily serves the function of:',
        o: [
          'Excreting urea from the body',
          'Thermoregulation — evaporation of sweat cools the body surface',
          'Providing water for the body',
          'Increasing blood pressure',
        ],
        a: 1,
        e: 'Sweat contains water, NaCl, and traces of urea. Its primary role is thermoregulation (cooling by evaporation), not excretion. The small amount of urea in sweat is secondary. Kidneys are the primary excretory organs for urea.',
        h: 'Sweat: primary function = thermoregulation (cooling). Secondary: minor excretion of salts/urea.',
        yr: 'SS2',
      },
      {
        q: 'The ureter connects:',
        o: [
          'Bladder to the urethra',
          'Kidney to the bladder — transports urine by peristaltic contractions',
          'Kidney to the heart',
          "Bowman's capsule to the loop of Henlé",
        ],
        a: 1,
        e: 'Each kidney has one ureter that carries urine from the renal pelvis to the urinary bladder. Urine moves by peristaltic muscular contractions (not gravity alone). The urethra is a separate tube that carries urine from the bladder to the outside.',
        h: 'Ureter: kidney → bladder (carries urine). Urethra: bladder → outside.',
        yr: 'SS2',
      },
      {
        q: 'Osmoregulation is important because:',
        o: [
          'It controls blood pressure only',
          'Cells function optimally only within a narrow range of osmotic concentration — too much or too little water causes cells to swell/shrink and disrupts enzyme function',
          'It produces ADH',
          'It controls glucose levels',
        ],
        a: 1,
        e: 'Osmoregulation maintains the osmotic concentration of blood and tissue fluids within narrow limits. Cells placed in hypertonic solution lose water and shrink; in hypotonic solution they gain water and may burst. All enzyme-controlled reactions require the correct water potential.',
        h: 'Osmoregulation: maintains correct osmotic balance — prevents cells swelling/shrinking; optimises enzyme function.',
        yr: 'SS2',
      },
      {
        q: 'In plants, how is excretion mainly accomplished?',
        o: [
          'By kidneys similar to animals',
          'Through urine produced in roots',
          'By releasing CO₂ and O₂ through stomata/lenticels; storing waste products in vacuoles or leaves that are shed',
          'By sweating through the bark',
        ],
        a: 2,
        e: 'Plants produce CO₂ (respiration waste) and O₂ (photosynthesis waste) released as gases through stomata and lenticels. Tannins, resins, and other metabolic waste products are stored in cell vacuoles or in leaves/bark that are periodically shed.',
        h: 'Plant excretion: gases through stomata/lenticels; waste stored in vacuoles or shed in leaves.',
        yr: 'SS2',
      },
      {
        q: 'The kidney is described as a homeostatic organ because:',
        o: [
          'It produces hormones that regulate all body functions',
          'It maintains constant internal conditions by regulating blood composition, water content, pH, and removing metabolic waste',
          'It controls heart rate',
          'It digests proteins',
        ],
        a: 1,
        e: 'Homeostasis = maintenance of a constant internal environment. Kidneys contribute by: regulating blood osmolarity (osmoregulation), blood pH (excreting H⁺), removing urea and other waste, regulating ion concentrations (Na⁺, K⁺).',
        h: 'Kidneys = homeostatic organs: regulate blood osmolarity, pH, ion balance, and remove waste.',
        yr: 'SS2',
      },
      {
        q: 'Bile pigments (bilirubin) are excreted by the:',
        o: [
          'Kidneys in urine',
          'Skin in sweat',
          'Liver — in bile, into the intestine, excreted in faeces',
          'Lungs in exhaled air',
        ],
        a: 2,
        e: 'Bile pigments (bilirubin, biliverdin) are breakdown products of haemoglobin from old/dead red blood cells. The liver converts haemoglobin to bile pigments and excretes them in bile into the small intestine → they give faeces its brown colour.',
        h: 'Bile pigments: RBC breakdown → liver → bile → intestine → excreted in faeces.',
        yr: 'SS2',
      },
      {
        q: 'Ammonia, urea, and uric acid are all nitrogenous waste products. Compared to ammonia, urea is preferred in humans because:',
        o: [
          'Urea is more toxic but more soluble',
          'Ammonia is less toxic than urea',
          'Urea is less toxic than ammonia and requires less water to excrete than uric acid — suitable for terrestrial mammals',
          'Urea is produced in the kidneys',
        ],
        a: 2,
        e: 'Nitrogenous waste: ammonia (very toxic, needs lots of water — aquatic animals excrete it); urea (less toxic, water-soluble — terrestrial mammals); uric acid (least toxic, insoluble, minimal water — birds, reptiles, insects). Urea is a good compromise for land mammals.',
        h: 'Urea: less toxic than ammonia (OK for terrestrial animals); more water-efficient than keeping ammonia.',
        yr: 'SS2',
      },
      {
        q: "During kidney dialysis, the patient's blood is:",
        o: [
          'Replaced with new blood',
          'Passed through a dialysis machine where waste products diffuse out across a selectively permeable membrane into dialysis fluid',
          'Filtered by an artificial kidney transplanted in the body',
          'Given high doses of ADH to produce more urine',
        ],
        a: 1,
        e: "Haemodialysis: blood from patient's artery passes through the dialysis machine alongside dialysis fluid separated by a semi-permeable membrane. Urea and excess ions diffuse from blood (high concentration) into dialysis fluid (low concentration). Clean blood returns to a vein.",
        h: 'Dialysis: blood passes semi-permeable membrane; waste diffuses into dialysis fluid; clean blood returns.',
        yr: 'SS2',
      },
      {
        q: 'What happens to the concentration and volume of urine produced when a person drinks large amounts of water?',
        o: [
          'Urine becomes more concentrated and volume decreases',
          'Urine becomes more dilute and volume increases — low blood osmolarity → less ADH secreted → less water reabsorbed → more dilute urine',
          'No change in urine',
          'Urine stops being produced',
        ],
        a: 1,
        e: 'Excess water intake → blood osmolarity decreases → detected by osmoreceptors in hypothalamus → less ADH released → collecting duct less permeable to water → less water reabsorbed → large volume of dilute urine.',
        h: 'Excess water → low osmolarity → less ADH → dilute urine (large volume).',
        yr: 'SS2',
      },
      {
        q: 'The juxtaglomerular apparatus in the kidney regulates blood pressure by releasing:',
        o: [
          'ADH when blood pressure is low',
          'Renin when blood pressure is low — triggering the renin-angiotensin-aldosterone system to increase blood pressure',
          'Insulin when blood glucose rises',
          'Urea when blood protein is high',
        ],
        a: 1,
        e: 'Low blood pressure → juxtaglomerular cells release renin → activates angiotensinogen → angiotensin I → ACE (angiotensin-converting enzyme) → angiotensin II → vasoconstriction + aldosterone release (Na⁺ reabsorption) → increased blood pressure.',
        h: 'Low BP → renin release → renin-angiotensin system → vasoconstriction + aldosterone → BP rises.',
        yr: 'SS2',
      },
      {
        q: 'Gout is a condition caused by the accumulation of uric acid crystals in joints. It is related to excretion because:',
        o: [
          'Uric acid is a product of fat metabolism',
          'Insufficient uric acid is produced',
          'Excess uric acid (from purine breakdown) is not efficiently excreted by the kidneys — crystallises in joints causing pain and inflammation',
          'The kidneys excrete too much uric acid',
        ],
        a: 2,
        e: 'Gout: excess uric acid (from breakdown of purines in nucleic acids and certain foods) not fully excreted. Uric acid is poorly soluble in water; when blood levels rise, crystals (monosodium urate) deposit in joints (especially big toe) causing intense inflammation.',
        h: 'Gout: excess uric acid not excreted → urate crystals in joints → inflammation and pain.',
        yr: 'SS2',
      },
      {
        q: 'The skin contributes to excretion by secreting sweat, which contains:',
        o: [
          'Only water',
          'Water, sodium chloride, small amounts of urea, and traces of other metabolic waste products',
          'Pure urea without water',
          'Glucose and amino acids',
        ],
        a: 1,
        e: 'Sweat (produced by sweat glands in the skin) contains: water (~99%), NaCl (main solute), small amounts of urea, lactic acid, and ammonia. The primary function of sweating is thermoregulation (cooling by evaporation), but it also contributes minor excretory function by removing some urea and salts.',
        h: 'Sweat contains: water, NaCl, small urea and lactic acid. Primary role: cooling (thermoregulation).',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Reproduction in Plants',
    topicCode: 'SS2-BIO-06',
    module: 'Reproduction',
    term: 'Second Term',
    contentHTML:
      '<div class="learn-intro"><span class="learn-keyword">Reproduction</span> ensures the continuation of a species. Plants reproduce sexually (involving flowers, pollination, fertilisation, and seed dispersal) and asexually (vegetative propagation).</div><h3 class="learn-subheading">1. Structure of a Flower</h3><ul class="learn-list"><li><strong>Sepals:</strong> protect the bud</li><li><strong>Petals:</strong> attract pollinators (coloured, scented)</li><li><strong>Stamens:</strong> male organs — anther (produces pollen) + filament</li><li><strong>Carpel/Pistil:</strong> female organs — stigma (receives pollen) + style + ovary (contains ovules)</li></ul><h3 class="learn-subheading">2. Pollination</h3><div class="learn-table-wrap"><table class="learn-table"><thead><tr><th>Feature</th><th>Wind-pollinated (anemophily)</th><th>Insect-pollinated (entomophily)</th></tr></thead><tbody><tr><td>Petals</td><td>Small, green/absent</td><td>Large, brightly coloured</td></tr><tr><td>Pollen</td><td>Light, smooth, abundant</td><td>Sticky, spiny, less abundant</td></tr><tr><td>Stigma</td><td>Feathery, outside flower</td><td>Sticky, inside flower</td></tr><tr><td>Nectar/scent</td><td>Absent</td><td>Present</td></tr><tr><td>Examples</td><td>Maize, grasses</td><td>Hibiscus, sunflower</td></tr></tbody></table></div><h3 class="learn-subheading">3. Fertilisation and Seed Development</h3><p class="learn-p">Double fertilisation in flowering plants: pollen grain germinates on stigma → pollen tube grows down style → two male nuclei enter ovule. One fertilises the egg cell (→ zygote → embryo). The other fuses with polar nuclei (→ triploid endosperm). Zygote → embryo; ovule → seed; ovary wall → fruit.</p><h3 class="learn-subheading">4. Seed Dispersal</h3><ul class="learn-list"><li><strong>Wind:</strong> light seeds with wings/parachutes (dandelion, maple)</li><li><strong>Water:</strong> buoyant seeds (coconut)</li><li><strong>Animals:</strong> hooks/spines (burrs) or fleshy fruits eaten and seeds excreted</li><li><strong>Explosive mechanisms:</strong> seed pods burst under tension (peas, beans)</li></ul><h3 class="learn-subheading">5. Vegetative Propagation</h3><p class="learn-p">Asexual reproduction: runners (strawberry), tubers (yam, potato), bulbs (onion, garlic), rhizomes (ginger), suckers (banana). Advantages: faster, identical offspring; Disadvantages: no genetic variation.</p><div class="learn-tip-box"><span class="learn-tip-icon">💡</span><strong>Double Fertilisation:</strong> Male nucleus 1 + egg = zygote (embryo). Male nucleus 2 + 2 polar nuclei = endosperm (food for embryo). Unique to flowering plants.</div><div class="learn-tip-box"><span class="learn-tip-icon">🎯</span><strong>Summary:</strong> Flower: sepal, petal, stamen (anther+filament), carpel (stigma+style+ovary). Pollination: wind (light pollen) or insect (sticky pollen). Double fertilisation. Ovule→seed; ovary→fruit.</div>',
    questions: [
      {
        q: 'The anther of a flower is responsible for:',
        o: [
          'Receiving pollen grains',
          'Producing ovules',
          'Producing pollen grains — the male gametes of flowering plants',
          'Secreting nectar to attract pollinators',
        ],
        a: 2,
        e: 'The anther is part of the stamen (male organ). It produces pollen grains by meiosis. When mature, the anther opens (dehisces) to release pollen. The filament supports the anther.',
        h: 'Anther: produces pollen grains (male reproductive cells) by meiosis.',
        yr: 'SS2',
      },
      {
        q: 'The stigma of a flower functions to:',
        o: [
          'Produce male gametes',
          'Receive pollen grains during pollination — it has a sticky surface for pollen attachment',
          'Produce nectar',
          'Protect the young bud',
        ],
        a: 1,
        e: 'The stigma is the top of the carpel (female organ). It is sticky (in insect-pollinated flowers) or feathery (in wind-pollinated) to receive and trap pollen grains. After pollination, the pollen grain germinates on the stigma.',
        h: 'Stigma: receives pollen during pollination; sticky (insect) or feathery (wind).',
        yr: 'SS2',
      },
      {
        q: 'Wind-pollinated flowers are characterised by:',
        o: [
          'Large, brightly coloured petals and sweet nectar',
          'Small or absent petals, large feathery stigmas, light smooth abundant pollen, no nectar',
          'Sticky pollen and strong scent',
          'Pollen produced in small quantities for efficiency',
        ],
        a: 1,
        e: 'Wind-pollinated flowers: small/absent petals (no need to attract animals); large feathery stigmas exposed outside flower (catch airborne pollen); light, smooth, abundant pollen (carried by air); no scent/nectar (no pollinators needed). Examples: maize, grasses, cereals.',
        h: 'Wind-pollinated: small petals, feathery stigma, light abundant smooth pollen, no nectar.',
        yr: 'SS2',
      },
      {
        q: 'Double fertilisation in flowering plants refers to:',
        o: [
          'Two flowers being fertilised simultaneously',
          'Two pollen grains fertilising one egg cell',
          'One male nucleus fertilising the egg cell (→ zygote) and another fusing with polar nuclei (→ triploid endosperm)',
          'Fertilisation occurring twice in the same season',
        ],
        a: 2,
        e: 'Double fertilisation (unique to angiosperms): pollen tube delivers 2 male nuclei. (1) 1st male nucleus + egg cell → zygote (develops into embryo). (2) 2nd male nucleus + 2 polar nuclei → triploid (3n) endosperm (nutritive tissue for embryo). Both fertilisations happen simultaneously.',
        h: 'Double fertilisation: male nucleus 1 + egg → embryo. Male nucleus 2 + polar nuclei → endosperm.',
        yr: 'SS2',
      },
      {
        q: 'After fertilisation, the ovule develops into:',
        o: [
          'The fruit',
          'The seed — containing the embryo and endosperm',
          'The flower',
          'The pollen grain',
        ],
        a: 1,
        e: 'After fertilisation: ovule → seed (containing embryo and endosperm); ovary wall → fruit (pericarp). The seed contains the embryo (young plant) and stored food (endosperm or cotyledons). The entire ovary matures into the fruit.',
        h: 'After fertilisation: ovule → seed; ovary → fruit.',
        yr: 'SS2',
      },
      {
        q: 'Self-pollination occurs when:',
        o: [
          'Pollen is transferred between flowers of different plants',
          'Pollen from the anther of a flower reaches the stigma of the same flower or another flower on the same plant',
          'Pollen is transferred by wind only',
          'Two different plant species exchange pollen',
        ],
        a: 1,
        e: "Self-pollination: pollen from a flower's own anther (or another flower on the same plant) reaches its own stigma. It can occur in bisexual flowers. It leads to less genetic variation than cross-pollination.",
        h: "Self-pollination: pollen from same plant's anther to same plant's stigma.",
        yr: 'SS2',
      },
      {
        q: 'The coconut fruit is adapted for water dispersal because:',
        o: [
          'It has hooks to attach to animals',
          'It has a light feathery structure',
          'The fruit is buoyant — with a fibrous husk (coir) trapping air — allowing it to float long distances across water',
          'It produces many small seeds',
        ],
        a: 2,
        e: 'Coconut is adapted for water dispersal: (1) large fibrous husk (coir) with air pockets for buoyancy; (2) waterproof outer shell; (3) internal food store (coconut water + flesh) for long journeys; (4) can float across oceans without sinking or losing viability.',
        h: 'Coconut: water dispersal. Fibrous buoyant husk + waterproof shell allow ocean travel.',
        yr: 'SS2',
      },
      {
        q: 'Vegetative propagation by rhizomes is shown by:',
        o: [
          'Strawberry (runners)',
          'Ginger and couch grass — horizontal underground stems that produce new shoots and roots',
          'Banana (suckers)',
          'Onion (bulbs)',
        ],
        a: 1,
        e: 'Rhizomes are horizontal underground stems (not roots) with nodes that can produce new shoots and roots. Examples: ginger, couch grass, iris, turmeric. Runners are above-ground horizontal stems (strawberry). Suckers grow from existing root systems (banana).',
        h: 'Rhizomes: horizontal underground stems → new plants. Examples: ginger, couch grass, iris.',
        yr: 'SS2',
      },
      {
        q: 'The main advantage of sexual reproduction in plants over vegetative propagation is:',
        o: [
          'Faster reproduction',
          'Produces identical plants',
          'Produces genetic variation in offspring — enabling adaptation and evolution through natural selection',
          'Requires no special conditions',
        ],
        a: 2,
        e: 'Sexual reproduction (involving meiosis and fertilisation) shuffles genes → genetic variation in offspring. This allows natural selection to act, enabling species to adapt to changing environments. Vegetative propagation produces clones (identical offspring) with no variation.',
        h: 'Sexual reproduction advantage: genetic variation → adaptability to changing environments.',
        yr: 'SS2',
      },
      {
        q: 'Insect-pollinated flowers have brightly coloured petals and nectar because:',
        o: [
          'Wind is not available as a pollinator',
          'They need to attract insect pollinators — bright colours and scent guide insects to flowers; nectar rewards them, and sticky pollen attaches to their bodies for transfer',
          'Insects damage wind-pollinated flowers',
          'Brightly coloured flowers are better protected from disease',
        ],
        a: 1,
        e: "Insect-pollinated flowers use insects as vectors. Adaptations: bright coloured petals (visual attraction), sweet scent (olfactory attraction), nectar (reward for insect), sticky/spiny pollen (adheres to insect), sticky stigma (catches pollen from insect's body).",
        h: 'Insect-pollinated: bright petals, scent, nectar (attract/reward insects); sticky pollen attaches to insect body.',
        yr: 'SS2',
      },
      {
        q: 'Germination of a seed requires:',
        o: [
          'Light (always essential)',
          'Soil nutrients',
          'Warmth, water, and oxygen — (light not always essential for initial germination)',
          'Cold temperatures only',
        ],
        a: 2,
        e: 'Seed germination conditions: (1) water — activates enzymes, softens testa, enables metabolic reactions; (2) warmth — optimal enzyme activity for metabolism; (3) oxygen — for aerobic respiration to provide energy (ATP) for growth. Light is NOT required for germination of most seeds.',
        h: 'Germination requires: water, warmth, and oxygen. Light NOT always required.',
        yr: 'SS2',
      },
      {
        q: 'The pollen tube grows from the pollen grain toward the ovule through:',
        o: [
          'The sepal',
          'The petal',
          'The style — pollen germinates on stigma, tube grows down style, guided by chemical signals to ovule',
          'The anther',
        ],
        a: 2,
        e: 'After landing on the stigma, the pollen grain absorbs sugary fluid and germinates, growing a pollen tube down through the style toward the ovule in the ovary. The pollen tube carries two male nuclei for double fertilisation.',
        h: 'Pollen germinates on stigma → pollen tube grows through style → reaches ovule.',
        yr: 'SS2',
      },
      {
        q: 'In epigeal germination (e.g., bean), the cotyledons:',
        o: [
          'Remain underground',
          'Are pushed above the soil surface by the elongating hypocotyl — becoming photosynthetic',
          'Remain permanently attached to the seed coat',
          'Are digested by soil bacteria',
        ],
        a: 1,
        e: 'Epigeal germination (beans, sunflower): hypocotyl elongates → pushes cotyledons above ground. Cotyledons emerge, become green, and photosynthesize briefly before withering. Hypogeal germination (maize, peas): cotyledons remain below ground; plumule (shoot) emerges above.',
        h: 'Epigeal: cotyledons emerge above ground (hypocotyl elongates). Example: bean. Hypogeal: cotyledons stay underground (maize, peas).',
        yr: 'SS2',
      },
      {
        q: 'Fruits with hooks or barbs are dispersed by:',
        o: [
          'Wind',
          'Water',
          'Animals (external: hooks catch on fur/clothing)',
          'Self-explosive mechanisms',
        ],
        a: 2,
        e: 'Biotic dispersal by external attachment: fruits/seeds have hooks, burrs, or barbs that attach to animal fur or clothing. Examples: Bidens (black-jack), burdock, Xanthium. The animal unwittingly transports seeds to new locations.',
        h: 'Hooked/barbed fruits: dispersed by attachment to animal fur/clothing (e.g., blackjack, burdock).',
        yr: 'SS2',
      },
      {
        q: 'The endosperm in a seed provides:',
        o: [
          'The outer protective covering',
          'Nutrition (food store) for the developing embryo during germination — digested by enzymes secreted from cotyledons',
          'The root of the new plant',
          'The genetic material for the embryo',
        ],
        a: 1,
        e: 'Endosperm is nutritive tissue (formed from double fertilisation: 2nd male nucleus + 2 polar nuclei → 3n endosperm). It stores starch, proteins, and oils that nourish the germinating embryo until it can photosynthesise. In monocots (maize), endosperm is large; in dicots (beans), cotyledons absorb and store endosperm food.',
        h: 'Endosperm: food store for embryo during germination. Forms from double fertilisation (triploid).',
        yr: 'SS2',
      },
      {
        q: 'Cross-pollination is advantageous over self-pollination because:',
        o: [
          'It is more reliable and always succeeds',
          'It requires no pollinators',
          'It increases genetic diversity — mixing genes from two different individuals — leading to more variation and vigour (hybrid vigour)',
          'It produces more seeds',
        ],
        a: 2,
        e: 'Cross-pollination mixes genetic material from two separate plants → increased genetic diversity in offspring (outbreeding). This leads to hybrid vigour (heterosis) — offspring often stronger/more productive than either parent. Self-pollination leads to inbreeding and reduced variation.',
        h: 'Cross-pollination: genetic diversity, hybrid vigour (stronger offspring). Self-pollination: inbreeding.',
        yr: 'SS2',
      },
      {
        q: 'Bulbs as a form of vegetative propagation are found in:',
        o: [
          'Yam (tubers)',
          'Ginger (rhizomes)',
          'Onion and garlic — short underground stems surrounded by fleshy leaf bases storing food',
          'Banana (suckers)',
        ],
        a: 2,
        e: 'Bulbs (onion, garlic, tulip): consist of a very short flattened stem (basal plate) surrounded by fleshy scale leaves (modified leaves) storing carbohydrates. Axillary buds develop into new bulbs or shoots. Each bulb can produce several daughter bulbs.',
        h: 'Bulbs: onion, garlic, tulip. Short stem + fleshy storage leaf bases. New bulbs develop from axillary buds.',
        yr: 'SS2',
      },
      {
        q: 'The sepals of a flower serve to:',
        o: [
          'Produce pollen',
          'Attract pollinators',
          'Protect the flower bud before it opens — enclosing and guarding inner floral parts during development',
          'Perform photosynthesis in the flower',
        ],
        a: 2,
        e: 'Sepals (collectively the calyx) are leaf-like structures at the base of the flower. Their primary role is to protect the developing bud before opening, enclosing all inner floral parts. After the flower opens, sepals may remain (persistent) or fall off (deciduous).',
        h: 'Sepals: protect the flower bud before it opens (bud protection).',
        yr: 'SS2',
      },
      {
        q: 'Explosive seed dispersal (e.g., in pea pods) occurs when:',
        o: [
          'Seeds attach to passing animals',
          'The pod/fruit wall dries and creates internal tension — suddenly splitting and flinging seeds outward',
          'Wind carries the seeds',
          'Seeds float on water',
        ],
        a: 1,
        e: 'Explosive dispersal: the fruit wall (pods) dries unevenly — contrasting tensions in different layers cause the pod to suddenly split and twist violently, catapulting seeds outward (up to several metres). Examples: peas, beans, touch-me-not (Impatiens), Himalayan balsam.',
        h: 'Explosive dispersal: dried pod walls split/twist suddenly → seeds flung outward. Examples: peas, beans.',
        yr: 'SS2',
      },
      {
        q: 'The ovary wall in a flowering plant develops after fertilisation into:',
        o: [
          'The seed',
          'The embryo',
          'The fruit (pericarp) — which protects the seeds and aids in their dispersal',
          'The pollen grain',
        ],
        a: 2,
        e: 'After fertilisation: ovule → seed (containing embryo + endosperm); ovary wall → fruit (pericarp/fruit wall). The fruit protects seeds and often plays a role in dispersal (fleshy fruits attract animals, dry pods split, wings aid wind dispersal). This is why only plants with ovaries (angiosperms) produce true fruits.',
        h: 'After fertilisation: ovary wall → fruit. Ovule → seed. Fruit protects and aids seed dispersal.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Reproduction in Animals',
    topicCode: 'SS2-BIO-07',
    module: 'Reproduction',
    term: 'Second Term',
    contentHTML:
      '<div class="learn-intro">Animals reproduce sexually (most) or asexually (some). Sexual reproduction involves the fusion of male and female gametes. This topic covers types of reproduction, fertilisation, development, and parental care in animals.</div><h3 class="learn-subheading">1. Types of Reproduction</h3><ul class="learn-list"><li><strong>Asexual:</strong> binary fission (Amoeba, bacteria), budding (Hydra, yeast), fragmentation (Planaria), parthenogenesis (aphids, Daphnia)</li><li><strong>Sexual:</strong> requires gametes (sperm + egg); fertilisation restores diploid number</li></ul><h3 class="learn-subheading">2. External vs Internal Fertilisation</h3><div class="learn-table-wrap"><table class="learn-table"><thead><tr><th>Feature</th><th>External</th><th>Internal</th></tr></thead><tbody><tr><td>Location</td><td>Outside body (in water)</td><td>Inside female reproductive tract</td></tr><tr><td>Gametes produced</td><td>Very large numbers (wasteful)</td><td>Fewer gametes needed</td></tr><tr><td>Parental care</td><td>Usually minimal</td><td>Usually more extensive</td></tr><tr><td>Success rate</td><td>Lower</td><td>Higher</td></tr><tr><td>Examples</td><td>Fish, frogs, most aquatic animals</td><td>Reptiles, birds, mammals</td></tr></tbody></table></div><h3 class="learn-subheading">3. Development</h3><ul class="learn-list"><li><strong>Oviparous:</strong> lay eggs (fish, amphibia, birds, reptiles, monotremes)</li><li><strong>Viviparous:</strong> give birth to live young; embryo nourished via placenta (most mammals)</li><li><strong>Ovoviviparous:</strong> eggs retained in mother but no placental connection (some sharks, snakes)</li></ul><h3 class="learn-subheading">4. Human Reproduction</h3><ul class="learn-list"><li>Male: testes produce sperm; testosterone secreted</li><li>Female: ovaries produce eggs; oestrogen and progesterone regulate menstrual cycle (~28 days)</li><li>Menstrual cycle: menstruation → follicular phase → ovulation (day 14) → luteal phase</li><li>Placenta: exchanges nutrients, gases, waste between mother and foetus</li><li>Gestation: ~9 months (40 weeks) in humans</li></ul><div class="learn-tip-box"><span class="learn-tip-icon">💡</span><strong>Menstrual Cycle:</strong> FSH → follicle matures → oestrogen rises → LH surge → ovulation (day 14) → corpus luteum → progesterone → maintains uterine lining.</div><div class="learn-tip-box"><span class="learn-tip-icon">🎯</span><strong>Summary:</strong> External fertilisation (aquatic, many gametes). Internal fertilisation (terrestrial, fewer gametes, higher success). Oviparous (eggs). Viviparous (live birth, placenta). Menstrual cycle ~28 days; ovulation ~day 14.</div>',
    questions: [
      {
        q: 'Amoeba reproduces asexually by:',
        o: [
          'Budding',
          'Fragmentation',
          'Binary fission — splitting into two equal daughter cells',
          'Spore formation',
        ],
        a: 2,
        e: 'Amoeba reproduces by binary fission: the nucleus divides by mitosis first, then the cytoplasm divides (cytokinesis) producing two genetically identical daughter cells. This is an asexual process — no gametes, no fertilisation.',
        h: 'Amoeba: binary fission (splits into two equal daughter cells). Asexual.',
        yr: 'SS2',
      },
      {
        q: 'Internal fertilisation is advantageous over external fertilisation because:',
        o: [
          'More gametes are produced',
          'Less energy is required',
          'Fertilisation occurs inside the body — reducing gamete wastage, protecting the zygote, and increasing success rate',
          'It occurs only in aquatic environments',
        ],
        a: 2,
        e: 'Internal fertilisation (reptiles, birds, mammals): sperm deposited inside female body → fertilises egg inside → zygote protected from desiccation and predation → higher success rate. Fewer gametes needed. Allows development in terrestrial environments where water is unavailable for external fertilisation.',
        h: 'Internal fertilisation: higher success rate, fewer gametes wasted, allows terrestrial life.',
        yr: 'SS2',
      },
      {
        q: 'Viviparous animals differ from oviparous animals in that viviparous animals:',
        o: [
          'Lay eggs with shells',
          'Give birth to live young — embryo develops inside the mother and is nourished via the placenta',
          'Retain eggs inside but have no placental connection',
          'Only reproduce asexually',
        ],
        a: 1,
        e: "Viviparous (most mammals): embryo develops inside mother's uterus; placenta exchanges nutrients, O₂, and waste between mother and foetus. Oviparous (birds, reptiles, fish, insects): lay eggs externally. Ovoviviparous (some sharks, snakes): eggs retained inside but no placenta.",
        h: 'Viviparous: live birth; embryo nourished via placenta. Oviparous: egg laying.',
        yr: 'SS2',
      },
      {
        q: 'The function of the placenta in mammals is to:',
        o: [
          'Produce sperm for fertilisation',
          'Protect the foetus from all infections',
          'Exchange nutrients, oxygen, and waste products between maternal and foetal blood without the bloods mixing',
          'Produce the amniotic fluid',
        ],
        a: 2,
        e: 'Placenta functions: (1) exchange O₂ and CO₂ between mother and foetus; (2) transfer nutrients (glucose, amino acids) from mother to foetus; (3) remove foetal waste (CO₂, urea) to maternal blood; (4) produce hormones (HCG, oestrogen, progesterone) to maintain pregnancy. Maternal and foetal blood do NOT mix.',
        h: "Placenta: exchanges O₂, nutrients, waste between mother and foetus (bloods don't mix).",
        yr: 'SS2',
      },
      {
        q: 'Ovulation in the human menstrual cycle occurs approximately:',
        o: [
          'On day 1 (start of menstruation)',
          'On day 7',
          'On day 14 (midcycle) — triggered by a surge of LH (luteinising hormone) from the pituitary',
          'On day 28 (end of cycle)',
        ],
        a: 2,
        e: 'In a regular 28-day cycle, ovulation occurs around day 14. A surge in LH (luteinising hormone) from the anterior pituitary triggers the mature Graafian follicle to rupture and release the egg (secondary oocyte) from the ovary into the fallopian tube.',
        h: 'Ovulation: day 14 of 28-day cycle, triggered by LH surge.',
        yr: 'SS2',
      },
      {
        q: 'FSH (follicle-stimulating hormone) in the female menstrual cycle:',
        o: [
          'Triggers ovulation directly',
          'Stimulates the growth and maturation of follicles in the ovary and causes the follicle to produce oestrogen',
          'Causes the uterine lining to be shed',
          'Maintains the corpus luteum',
        ],
        a: 1,
        e: 'FSH (from anterior pituitary) stimulates ovarian follicle growth and development. The maturing follicle produces oestrogen, which: (1) stimulates uterine lining regeneration; (2) inhibits FSH (negative feedback); (3) eventually causes the LH surge → ovulation.',
        h: 'FSH: stimulates follicle growth → follicle produces oestrogen.',
        yr: 'SS2',
      },
      {
        q: 'The corpus luteum forms after ovulation and secretes:',
        o: [
          'FSH and LH',
          'Oestrogen only',
          'Progesterone (and some oestrogen) — which maintains the thickened uterine lining for possible implantation',
          'Testosterone',
        ],
        a: 2,
        e: "After ovulation, the ruptured follicle becomes the corpus luteum (yellow body), which secretes progesterone (and some oestrogen). Progesterone maintains the thick, vascular uterine lining ready for implantation. If fertilisation doesn't occur, corpus luteum degenerates → progesterone falls → menstruation begins.",
        h: 'Corpus luteum → progesterone → maintains uterine lining. Degenerates → menstruation if no fertilisation.',
        yr: 'SS2',
      },
      {
        q: 'Menstruation in the human female is caused by:',
        o: [
          'Fertilisation of the egg',
          'A surge in progesterone levels',
          'The degeneration of the corpus luteum causing progesterone to fall — leading to breakdown and shedding of the uterine lining (endometrium)',
          'An increase in FSH at the end of the cycle',
        ],
        a: 2,
        e: 'When fertilisation does not occur: corpus luteum degenerates (without HCG from embryo to maintain it) → progesterone and oestrogen fall → uterine lining (endometrium) is not maintained → it breaks down and is shed as menstrual blood (days 1-5 of cycle).',
        h: 'Menstruation: corpus luteum degenerates → progesterone falls → uterine lining shed.',
        yr: 'SS2',
      },
      {
        q: 'Parthenogenesis is a form of asexual reproduction where:',
        o: [
          'Two males reproduce together',
          'An unfertilised egg develops into a new organism without fertilisation',
          'Two organisms bud off from a parent',
          'An organism splits into multiple fragments',
        ],
        a: 1,
        e: 'Parthenogenesis: development of an unfertilised egg into a new organism. Examples: aphids, bees (drones), Daphnia, some reptiles (Komodo dragon). In bees: unfertilised eggs → haploid males (drones); fertilised eggs → diploid females (workers/queens).',
        h: 'Parthenogenesis: unfertilised egg develops into new organism. Examples: bees, aphids, Daphnia.',
        yr: 'SS2',
      },
      {
        q: 'In humans, sperm is produced in the:',
        o: [
          'Prostate gland',
          'Seminal vesicles',
          'Testes — in the seminiferous tubules, stimulated by FSH and testosterone',
          'Epididymis',
        ],
        a: 2,
        e: 'Sperm production (spermatogenesis) occurs in the seminiferous tubules of the testes. FSH from the pituitary stimulates sperm production; testosterone (from Leydig cells) also promotes sperm development. Sperm matures and is stored in the epididymis.',
        h: 'Sperm produced in seminiferous tubules of testes. Stimulated by FSH and testosterone.',
        yr: 'SS2',
      },
      {
        q: 'The function of the amniotic fluid surrounding the foetus is:',
        o: [
          'Providing nutrition to the foetus',
          'Producing hormones for the mother',
          'Acting as a shock absorber to protect the foetus from mechanical damage, and maintaining temperature and a fluid environment',
          'Transporting oxygen to the foetus',
        ],
        a: 2,
        e: 'Amniotic fluid: (1) cushions/protects foetus from physical jolts and pressure; (2) maintains constant temperature; (3) allows foetal movement (important for muscle/joint development); (4) prevents infection; (5) provides space for foetal growth. It is swallowed and recycled by the foetus.',
        h: 'Amniotic fluid: cushions foetus from shock; maintains temperature; allows movement.',
        yr: 'SS2',
      },
      {
        q: 'Hydra reproduces asexually by:',
        o: [
          'Binary fission',
          'Fragmentation',
          'Budding — a small outgrowth (bud) forms on the parent and eventually detaches as a new individual',
          'Spore formation',
        ],
        a: 2,
        e: 'Hydra reproduces by budding: a small outgrowth (bud) forms on the parent body wall due to mitotic cell division. The bud develops tentacles and a mouth, then pinches off to become an independent organism. This is asexual — the offspring is genetically identical to the parent.',
        h: 'Hydra: budding (small outgrowth → detaches as new organism). Genetically identical.',
        yr: 'SS2',
      },
      {
        q: 'Testosterone in males is produced by:',
        o: [
          'Seminiferous tubules',
          'Prostate gland',
          'Leydig (interstitial) cells in the testes — stimulated by LH from the pituitary',
          'Epididymis',
        ],
        a: 2,
        e: 'Testosterone is secreted by Leydig (interstitial) cells located between the seminiferous tubules in the testes. LH from the anterior pituitary stimulates Leydig cells to secrete testosterone. Testosterone promotes secondary sexual characteristics, libido, and spermatogenesis.',
        h: 'Testosterone: secreted by Leydig cells in testes, stimulated by LH.',
        yr: 'SS2',
      },
      {
        q: 'External fertilisation requires the release of large numbers of gametes because:',
        o: [
          'It is more efficient',
          'Large numbers increase genetic diversity',
          'Most gametes are lost to water currents, predation, and unfavourable conditions — large numbers increase the probability of successful fertilisation',
          'The female can only receive a small number of sperm',
        ],
        a: 2,
        e: 'In external fertilisation (fish, frogs), sperm and eggs are released into water. Most gametes are diluted, eaten, or damaged before fertilisation can occur. Releasing millions of eggs and billions of sperm increases the probability that at least some will meet and fertilise successfully.',
        h: 'External fertilisation: millions of gametes released because most are lost — ensures some fertilisation occurs.',
        yr: 'SS2',
      },
      {
        q: 'The umbilical cord connects:',
        o: [
          "The mother's brain to the foetus",
          "The foetal heart to the mother's heart",
          'The foetus to the placenta — containing umbilical arteries (deoxygenated blood out) and umbilical vein (oxygenated blood in)',
          'The placenta to the uterine wall',
        ],
        a: 2,
        e: 'Umbilical cord: connects foetus to placenta. Contains: (1) two umbilical arteries (carry deoxygenated, waste-laden blood from foetus to placenta); (2) one umbilical vein (carries oxygenated, nutrient-rich blood from placenta to foetus). Note: arteries carry away (from foetus), vein carries toward (foetus) — opposite to the systemic convention.',
        h: 'Umbilical cord: foetus to placenta. 2 arteries (waste out) + 1 vein (O₂/nutrients in).',
        yr: 'SS2',
      },
      {
        q: 'Which of the following is an example of ovoviviparity?',
        o: [
          'Chicken (lays eggs)',
          'Human (live birth with placenta)',
          'Some sharks and snakes — eggs develop inside the mother but the embryo is nourished by the egg yolk, not the placenta',
          'Frog (external fertilisation, eggs in water)',
        ],
        a: 2,
        e: "Ovoviviparous: eggs are retained inside the mother's body and hatch internally, but there is no placental connection between mother and embryo. The embryo is nourished by egg yolk (not mother's blood). Examples: some sharks (e.g., dogfish), garter snakes, some lizards.",
        h: 'Ovoviviparous: eggs retained inside mother; embryo nourished by yolk (no placenta). Some sharks, snakes.',
        yr: 'SS2',
      },
      {
        q: 'HCG (human chorionic gonadotrophin) is important in early pregnancy because:',
        o: [
          'It stimulates ovulation',
          'It causes menstruation to begin',
          'It maintains the corpus luteum so it continues producing progesterone — preventing menstruation and maintaining the uterine lining',
          'It suppresses oestrogen production',
        ],
        a: 2,
        e: 'HCG is produced by the embryo (trophoblast) shortly after implantation. It acts on the corpus luteum to maintain it (preventing its degeneration) so it continues secreting progesterone, which maintains the uterine lining. HCG detected in urine is the basis of pregnancy tests.',
        h: 'HCG (from embryo): maintains corpus luteum → continues progesterone → maintains uterine lining.',
        yr: 'SS2',
      },
      {
        q: 'Oestrogen in the female menstrual cycle is primarily responsible for:',
        o: [
          'Causing ovulation directly',
          'Maintaining the corpus luteum',
          'Regenerating and thickening the uterine lining after menstruation and stimulating LH surge at mid-cycle',
          'Inhibiting follicle growth',
        ],
        a: 2,
        e: 'Oestrogen (from maturing follicle): (1) stimulates regeneration of uterine endometrium after menstruation; (2) at high levels, triggers the LH surge → ovulation; (3) stimulates development of secondary sexual characteristics in females (puberty).',
        h: 'Oestrogen: thickens uterine lining after menstruation; high levels trigger LH surge → ovulation.',
        yr: 'SS2',
      },
      {
        q: 'The gestation period in humans is approximately:',
        o: ['3 months', '6 months', '9 months (40 weeks from last menstrual period)', '12 months'],
        a: 2,
        e: 'Human gestation (pregnancy) lasts approximately 40 weeks (about 9 months) from the last menstrual period (LMP), or 38 weeks from fertilisation. The three trimesters: 1st (weeks 1-12): organogenesis; 2nd (13-26): growth; 3rd (27-40): maturation and preparation for birth.',
        h: 'Human gestation: ~40 weeks (9 months). Three trimesters.',
        yr: 'SS2',
      },
      {
        q: 'Colostrum, the first milk produced after birth, is important because it:',
        o: [
          'Contains more fat than mature breast milk',
          'Has fewer nutrients than formula milk',
          'Contains maternal antibodies (IgA) that provide passive immunity to the newborn — protecting it against infections during early life when its own immune system is immature',
          'Is produced only by non-mammalian animals',
        ],
        a: 2,
        e: "Colostrum (first 2-3 days of breastfeeding): rich in maternal IgA antibodies, white blood cells, growth factors, and nutrients. The newborn's immune system is not yet producing antibodies effectively — colostrum provides passive immunity against pathogens the mother has encountered. Also seals gut lining and promotes beneficial gut bacteria.",
        h: 'Colostrum: maternal antibodies (IgA) → passive immunity for newborn. Also nutrients and growth factors.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Genetics and Heredity',
    topicCode: 'SS2-BIO-08',
    module: 'Genetics',
    term: 'Second Term',
    contentHTML:
      '<div class="learn-intro"><span class="learn-keyword">Genetics</span> is the study of heredity — how traits are passed from parents to offspring. Mendel\'s Laws form the foundation of classical genetics.</div><h3 class="learn-subheading">1. Key Terms</h3><ul class="learn-list"><li><strong>Gene:</strong> unit of heredity; segment of DNA coding for a protein</li><li><strong>Allele:</strong> alternative form of a gene</li><li><strong>Dominant:</strong> allele expressed in heterozygote (capital letter, e.g., T)</li><li><strong>Recessive:</strong> allele expressed only in homozygote (lowercase, e.g., t)</li><li><strong>Genotype:</strong> genetic makeup (e.g., TT, Tt, tt)</li><li><strong>Phenotype:</strong> physical/observable expression of genotype</li><li><strong>Homozygous:</strong> both alleles the same (TT or tt)</li><li><strong>Heterozygous:</strong> two different alleles (Tt)</li><li><strong>Locus:</strong> position of a gene on a chromosome</li></ul><h3 class="learn-subheading">2. Mendel\'s Laws</h3><ul class="learn-list"><li><strong>Law of Segregation:</strong> each individual has two alleles for each trait; they separate during gamete formation — each gamete receives only one allele</li><li><strong>Law of Independent Assortment:</strong> genes on different chromosomes assort independently during meiosis (applies to non-linked genes)</li></ul><h3 class="learn-subheading">3. Monohybrid Cross</h3><p class="learn-p">Tall (T) × Tall (T): all Tall (TT). Tall (TT) × short (tt): all Tall (Tt) in F1. F1 × F1: 3 Tall : 1 short (3:1 ratio in F2). Tt × tt (testcross): 1 Tall : 1 short.</p><h3 class="learn-subheading">4. Sex Determination</h3><p class="learn-p">Humans: females XX, males XY. Sex chromosomes: X (larger, more genes) and Y (smaller). Males determine sex of offspring (provide either X or Y).</p><h3 class="learn-subheading">5. Sex-linked Traits</h3><p class="learn-p">Genes on the X chromosome. Example: colour blindness (X^B normal, X^b colour blind). Males (XY) need only one X^b allele to be colour blind; females need X^b X^b. Females X^B X^b = carrier (unaffected).</p><div class="learn-tip-box"><span class="learn-tip-icon">💡</span><strong>Punnett Square:</strong> Draw a grid. Write parental gametes along top and side. Fill in to find offspring genotype and phenotype ratios.</div><div class="learn-tip-box"><span class="learn-tip-icon">🎯</span><strong>Summary:</strong> Segregation: alleles separate in gametes. Independent assortment: genes on different chromosomes. F2 monohybrid: 3:1. Testcross: 1:1. Sex-linked: on X chromosome. Colour blindness: more common in males.</div>',
    questions: [
      {
        q: "Mendel's Law of Segregation states that:",
        o: [
          'Genes on different chromosomes assort independently',
          'Dominant alleles always mask recessive ones',
          'During gamete formation, the two alleles of each gene separate so that each gamete receives only one allele',
          'All offspring resemble the dominant parent',
        ],
        a: 2,
        e: 'Law of Segregation: each organism has two alleles for each trait; during meiosis (gamete formation), these alleles separate so each gamete receives only one allele. At fertilisation, offspring receives one allele from each parent → two alleles.',
        h: 'Segregation: two alleles separate during gamete formation → each gamete gets one allele.',
        yr: 'SS2',
      },
      {
        q: 'In genetics, the phenotype of an organism refers to:',
        o: [
          'Its genetic makeup (alleles present)',
          'Its observable physical characteristics — the expression of the genotype in an organism',
          'The number of chromosomes it possesses',
          'Whether it is homozygous or heterozygous',
        ],
        a: 1,
        e: 'Phenotype = observable/physical characteristics of an organism (e.g., tall/short, blue/brown eyes). Genotype = the alleles present (e.g., TT, Tt, tt). The phenotype is the result of the genotype interacting with the environment.',
        h: 'Phenotype = observable characteristics. Genotype = alleles present.',
        yr: 'SS2',
      },
      {
        q: 'In a cross between a homozygous tall plant (TT) and a homozygous short plant (tt), the F1 offspring will all be:',
        o: [
          'Short (tt)',
          'Tall (TT)',
          'Tall (Tt) — heterozygous, showing the dominant tall phenotype',
          'Half tall and half short',
        ],
        a: 2,
        e: 'TT × tt: all gametes from TT are T; all gametes from tt are t. All F1 offspring = Tt (heterozygous). Since T (tall) is dominant over t (short), all F1 plants are phenotypically tall. Tt genotype = tall phenotype.',
        h: 'TT × tt → all Tt (all tall). T (tall) is dominant.',
        yr: 'SS2',
      },
      {
        q: 'The F2 generation from crossing two F1 heterozygous tall plants (Tt × Tt) gives a genotype ratio of:',
        o: [
          '1 TT : 2 Tt : 1 tt — phenotype ratio 3 tall : 1 short',
          'All TT',
          '2 TT : 2 Tt',
          '1 TT : 1 tt',
        ],
        a: 0,
        e: 'Tt × Tt Punnett square: TT (25%), Tt (50%), tt (25%) → genotype ratio 1:2:1. Phenotype: TT and Tt are both tall; tt is short → 3 tall : 1 short (3:1) phenotype ratio.',
        h: 'Tt × Tt → 1TT:2Tt:1tt (genotype) = 3 tall:1 short (phenotype). Classic Mendelian 3:1 ratio.',
        yr: 'SS2',
      },
      {
        q: 'A testcross is used to determine if an individual showing the dominant phenotype is:',
        o: [
          'Homozygous dominant (TT) or heterozygous (Tt) — by crossing with homozygous recessive (tt)',
          'Male or female',
          'Diploid or haploid',
          'Dominant or recessive',
        ],
        a: 0,
        e: 'Testcross: cross unknown dominant phenotype × homozygous recessive (tt). If all offspring show dominant phenotype → unknown is TT (homozygous). If offspring are 1 dominant : 1 recessive → unknown is Tt (heterozygous). Testcross reveals hidden alleles.',
        h: 'Testcross: dominant phenotype × tt. All dominant → TT. 1:1 ratio → Tt.',
        yr: 'SS2',
      },
      {
        q: 'The human sex determination system means that the sex of a child is determined by:',
        o: [
          'The mother, who contributes either X or Y',
          'The father — who can contribute either X (→ daughter) or Y (→ son) in his sperm',
          'The number of chromosomes in the egg',
          'Whether fertilisation occurs early or late',
        ],
        a: 1,
        e: 'Human sex chromosomes: females XX, males XY. Eggs always carry X (mother always contributes X). Sperm carry either X or Y. If Y-bearing sperm fertilises the egg → XY → male. If X-bearing sperm → XX → female. Father determines the sex.',
        h: 'Father determines sex: X sperm → girl (XX); Y sperm → boy (XY).',
        yr: 'SS2',
      },
      {
        q: 'Colour blindness is a sex-linked trait. A colour-blind mother (X^b X^b) and a normal-sighted father (X^B Y) have children. Which prediction is correct?',
        o: [
          'All children will have normal colour vision',
          'All sons will be colour blind; all daughters will be carriers',
          'All sons will be colour blind (X^b Y); all daughters will be carriers (X^B X^b)',
          'Half the daughters will be colour blind',
        ],
        a: 2,
        e: 'Mother (X^b X^b) × Father (X^B Y): daughters get X^b from mother and X^B from father → all X^B X^b (carriers, normal vision). Sons get X^b from mother and Y from father → all X^b Y (colour blind). All sons are colour blind; all daughters are carriers.',
        h: 'Colour-blind mother × normal father: all sons colour blind; all daughters carriers.',
        yr: 'SS2',
      },
      {
        q: 'A carrier female for colour blindness has the genotype:',
        o: [
          'X^B X^B',
          'X^B X^b — she has one normal allele and one colour-blind allele but has normal colour vision',
          'X^b X^b',
          'X^b Y',
        ],
        a: 1,
        e: 'Carrier female: X^B X^b. She has one dominant normal allele (X^B) that masks the recessive colour-blind allele (X^b), so she has normal colour vision but can pass the colour-blind allele to her sons (→ colour blind) or daughters (→ carriers).',
        h: 'Carrier female: X^B X^b. Normal vision but can pass X^b to offspring.',
        yr: 'SS2',
      },
      {
        q: "Mendel's Law of Independent Assortment states that:",
        o: [
          'Alleles always stay together during gamete formation',
          'Genes on the same chromosome always assort together',
          'Genes located on different (non-homologous) chromosomes assort independently of each other during gamete formation',
          'Dominant alleles are always inherited together',
        ],
        a: 2,
        e: 'Independent assortment: genes on different chromosomes segregate independently during meiosis I. This generates new combinations of alleles in gametes. Example: seed colour and seed shape genes in peas are on different chromosomes → 4 gamete types in equal proportions.',
        h: 'Independent assortment: genes on different chromosomes segregate independently → new allele combinations.',
        yr: 'SS2',
      },
      {
        q: 'Incomplete dominance is demonstrated when:',
        o: [
          'The dominant allele completely masks the recessive',
          'The F1 hybrid shows a phenotype intermediate between the two parents — neither allele is completely dominant',
          'Both alleles are expressed simultaneously',
          'The recessive allele masks the dominant',
        ],
        a: 1,
        e: 'Incomplete dominance: neither allele completely masks the other. F1 shows intermediate phenotype. Example: red snapdragon (R^R R^R) × white (R^W R^W) → all pink (R^R R^W) in F1. F2 from F1 × F1: 1 red : 2 pink : 1 white (1:2:1 phenotype ratio).',
        h: 'Incomplete dominance: F1 hybrid = intermediate phenotype. Example: red × white → pink.',
        yr: 'SS2',
      },
      {
        q: 'ABO blood group shows codominance because:',
        o: [
          'Only one allele is expressed in blood type AB',
          'Blood type AB individuals express only the A antigen',
          'In blood type AB, both I^A and I^B alleles are fully expressed — both A and B antigens are produced on red blood cells',
          'The O allele is dominant over both A and B',
        ],
        a: 2,
        e: 'ABO codominance: I^A and I^B are both dominant and codominant with each other. Blood type AB (I^A I^B): both A and B antigens expressed on RBCs. Blood type O (ii): neither A nor B antigens. I^A and I^B are both dominant over i (O).',
        h: 'Codominance: blood type AB expresses BOTH A and B antigens (I^A and I^B codominant).',
        yr: 'SS2',
      },
      {
        q: 'A cross between a red bull (X^R X^R where R=roan coat colour allele) and a white cow (X^W X^W) produces roan offspring in cattle. This is an example of:',
        o: [
          'Dominance',
          'Sex-linkage',
          'Incomplete dominance (red + white → roan — intermediate appearance)',
          'Epistasis',
        ],
        a: 2,
        e: 'Roan coat in cattle: X^R X^W → roan (mixture of red and white hairs — intermediate appearance, not a blend). This is incomplete dominance. The two alleles are expressed together resulting in a phenotype between the two parental phenotypes.',
        h: 'Red × white → roan = incomplete dominance (neither allele completely dominant).',
        yr: 'SS2',
      },
      {
        q: 'The chromosome number in humans is:',
        o: [
          '23 chromosomes total',
          '46 chromosomes (23 pairs) in somatic cells; 23 chromosomes in gametes',
          '44 autosomes only',
          '48 chromosomes in total',
        ],
        a: 1,
        e: 'Humans: 2n = 46 (23 pairs) in diploid somatic cells. 22 pairs = autosomes; 1 pair = sex chromosomes (XX or XY). Gametes (sperm and egg) are haploid: n = 23. Fertilisation restores 2n = 46.',
        h: 'Humans: 2n=46 (somatic cells). n=23 (gametes). 22 pairs autosomes + 1 pair sex chromosomes.',
        yr: 'SS2',
      },
      {
        q: 'Haemophilia is a sex-linked condition in which blood clotting is impaired. A carrier mother (X^H X^h) and a normal father (X^H Y) — what proportion of sons will have haemophilia?',
        o: [
          'None',
          'All sons',
          'Half the sons (50%) — receive either X^H Y (normal) or X^h Y (haemophilia)',
          'All daughters',
        ],
        a: 2,
        e: 'Carrier mother X^H X^h × normal father X^H Y: sons get Y from father and either X^H or X^h from mother (equal probability). Sons: 50% X^H Y (normal) and 50% X^h Y (haemophilia). Daughters: 50% X^H X^H (normal) and 50% X^H X^h (carriers).',
        h: 'Carrier mother × normal father: 50% of sons will have haemophilia.',
        yr: 'SS2',
      },
      {
        q: 'Multiple alleles means:',
        o: [
          'An individual has more than two alleles for a trait',
          'A gene has more than two possible allelic forms in the population — though each individual still has only two',
          'Two genes control the same trait',
          'All alleles are dominant',
        ],
        a: 1,
        e: 'Multiple alleles: a gene exists in more than two allelic forms in the population. Example: ABO blood group has three alleles: I^A, I^B, and i. Each individual has only two alleles (one from each parent), but the population has three possible allele types.',
        h: 'Multiple alleles: gene has >2 allelic forms in the population. Example: ABO blood group (I^A, I^B, i).',
        yr: 'SS2',
      },
      {
        q: 'A man with blood type A (I^A i) and a woman with blood type B (I^B i) can produce children with which blood types?',
        o: [
          'A and B only',
          'A, B, AB, and O — all four blood types are possible',
          'O only',
          'AB only',
        ],
        a: 1,
        e: 'I^A i × I^B i. Punnett square: I^A I^B (AB), I^A i (A), I^B i (B), ii (O). All four blood types possible in equal probability (1:1:1:1). This is a classic genetics cross.',
        h: 'I^A i × I^B i → A, B, AB, and O all possible (1:1:1:1).',
        yr: 'SS2',
      },
      {
        q: 'Linkage in genetics refers to:',
        o: [
          'Genes located on different chromosomes assort independently',
          'Genes located on the same chromosome tend to be inherited together (linked)',
          'Dominant alleles always linked together',
          'Male genes linked to female genes',
        ],
        a: 1,
        e: 'Linked genes: genes located on the same chromosome tend to be inherited together (do not assort independently). They can be separated by crossing over (recombination) during meiosis prophase I. The closer two genes are on the same chromosome, the more strongly they are linked.',
        h: 'Linkage: genes on same chromosome inherited together. Can be separated by crossing over.',
        yr: 'SS2',
      },
      {
        q: 'A dihybrid cross between RrYy × RrYy (where R=round, r=wrinkled; Y=yellow, y=green) gives a phenotype ratio of:',
        o: [
          '1:1:1:1',
          '3:1',
          '9:3:3:1 (round yellow : round green : wrinkled yellow : wrinkled green)',
          '1:2:1',
        ],
        a: 2,
        e: 'Dihybrid cross RrYy × RrYy (genes on different chromosomes): Phenotype ratio 9:3:3:1 (9 round yellow : 3 round green : 3 wrinkled yellow : 1 wrinkled green). This follows independent assortment — classic Mendelian dihybrid ratio.',
        h: 'Dihybrid F2 ratio: 9:3:3:1. (9 dominant-dominant : 3 dominant-recessive : 3 recessive-dominant : 1 recessive-recessive).',
        yr: 'SS2',
      },
      {
        q: 'In a monohybrid cross, the phenotype ratio of 3:1 in the F2 generation is explained by:',
        o: [
          'Both parents being homozygous dominant',
          'The dominant allele completely masking the recessive allele in heterozygotes — so Tt appears identical to TT, producing 3 dominant : 1 recessive phenotype from 1TT : 2Tt : 1tt genotype ratio',
          'Recessive allele being more common',
          'All offspring being heterozygous',
        ],
        a: 1,
        e: 'F2 from Tt × Tt: genotype ratio 1TT:2Tt:1tt. Phenotype: TT and Tt both show dominant trait (T masks t in Tt) → 3 dominant phenotype; only tt shows recessive → 1 recessive. Hence 3:1 phenotype ratio. This ratio is the classic Mendelian result for complete dominance.',
        h: '3:1 F2 ratio: 1TT + 2Tt (dominant) : 1tt (recessive). T masks t in Tt → dominant phenotype.',
        yr: 'SS2',
      },
      {
        q: 'Crossing over during meiosis prophase I increases genetic variation by:',
        o: [
          'Duplicating chromosomes',
          'Reducing the chromosome number',
          'Exchanging segments between homologous chromosomes (non-sister chromatids) — producing new combinations of alleles (recombinant chromosomes) not present in either parent',
          'Preventing fertilisation from occurring',
        ],
        a: 2,
        e: 'Crossing over (recombination): during prophase I of meiosis, homologous chromosomes pair (synapsis) and non-sister chromatids exchange corresponding segments at points called chiasmata. This produces recombinant chromosomes with new allele combinations not found in either parent. Combined with independent assortment and random fertilisation, crossing over generates enormous genetic diversity.',
        h: 'Crossing over: exchange of segments between homologous chromosomes → new allele combinations (recombinant chromosomes).',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Variation and Evolution',
    topicCode: 'SS2-BIO-09',
    module: 'Genetics',
    term: 'Second Term',
    contentHTML:
      '<div class="learn-intro"><span class="learn-keyword">Variation</span> refers to differences between individuals of the same species. <span class="learn-keyword">Evolution</span> is the change in heritable characteristics of populations over successive generations — driven by natural selection, mutation, and genetic drift.</div><h3 class="learn-subheading">1. Types of Variation</h3><ul class="learn-list"><li><strong>Continuous variation:</strong> shows a range of values with no distinct categories; polygenic traits; e.g., height, skin colour, body mass. Normal distribution curve.</li><li><strong>Discontinuous variation:</strong> distinct categories with no intermediates; controlled by one or few genes; e.g., blood group (A/B/AB/O), tongue rolling, attached/unattached earlobes.</li></ul><h3 class="learn-subheading">2. Causes of Variation</h3><ul class="learn-list"><li><strong>Genetic causes:</strong> mutation, meiosis (crossing over, independent assortment), sexual reproduction, genetic recombination</li><li><strong>Environmental causes:</strong> nutrition, climate, exercise, disease, education (affect phenotype but not genotype)</li></ul><h3 class="learn-subheading">3. Darwin\'s Theory of Natural Selection</h3><ul class="learn-list"><li>Populations produce more offspring than can survive (overproduction)</li><li>Variation exists within the population (heritable differences)</li><li>Competition for limited resources (struggle for existence)</li><li>Individuals with advantageous traits survive and reproduce more (survival of the fittest)</li><li>Advantageous traits are inherited by offspring → frequency increases in population over generations</li></ul><h3 class="learn-subheading">4. Evidence for Evolution</h3><ul class="learn-list"><li>Fossil record (shows progression of life forms)</li><li>Comparative anatomy (homologous and analogous structures)</li><li>Comparative embryology (embryos of different vertebrates are similar)</li><li>Molecular biology (similar DNA/proteins in related species)</li><li>Artificial selection (breeding programs show rapid change)</li></ul><h3 class="learn-subheading">5. Lamarck vs Darwin</h3><p class="learn-p">Lamarck: acquired characteristics can be inherited (now disproven). Darwin: natural selection acts on existing heritable variation (accepted theory). Neo-Darwinism adds genetics to Darwin\'s theory.</p><div class="learn-tip-box"><span class="learn-tip-icon">💡</span><strong>Natural Selection Steps:</strong> Overproduction → Variation → Competition → Survival of fittest → Inheritance → Population change over time.</div><div class="learn-tip-box"><span class="learn-tip-icon">🎯</span><strong>Summary:</strong> Continuous variation (height, polygenic, normal curve). Discontinuous (blood group, one gene). Natural selection: variation + competition + survival of fittest → evolution. Mutation = raw material for evolution.</div>',
    questions: [
      {
        q: 'Continuous variation is characterised by:',
        o: [
          'Distinct categories with no intermediates',
          'A range of values forming a normal distribution curve; controlled by many genes (polygenic) plus environment',
          'Only two possible phenotypes',
          'Traits determined by a single gene',
        ],
        a: 1,
        e: 'Continuous variation: no distinct categories; individuals show a full range from one extreme to the other. Controlled by many genes (polygenic) plus environment. Examples: height, weight, skin colour, intelligence. Data plotted as a normal distribution (bell curve).',
        h: 'Continuous variation: range of values, normal distribution, polygenic. Examples: height, weight.',
        yr: 'SS2',
      },
      {
        q: 'Which of the following is an example of discontinuous variation?',
        o: [
          'Height in humans',
          'Body mass',
          'ABO blood group — only four distinct categories (A, B, AB, O) with no intermediates',
          'Skin colour',
        ],
        a: 2,
        e: 'Discontinuous variation: distinct, non-overlapping categories with no intermediates. ABO blood groups: only A, B, AB, or O — you cannot have a blood group between A and B. Controlled by one or few genes; environment has little effect.',
        h: 'Discontinuous variation: distinct categories, no intermediates. Example: blood groups (A, B, AB, O).',
        yr: 'SS2',
      },
      {
        q: 'Mutations are important in evolution because:',
        o: [
          'Mutations always improve the organism',
          'Mutations are always harmful and lethal',
          'Mutations create new alleles — providing new heritable variation that natural selection can act upon',
          'Mutations only occur in somatic cells',
        ],
        a: 2,
        e: 'Mutations are random changes in DNA sequence. Most are neutral or harmful, but rarely a mutation is beneficial in a given environment. Beneficial mutations → increased survival and reproduction → frequency increases over generations. Mutations are the ultimate source of new genetic variation for evolution.',
        h: 'Mutations: create new alleles → raw material for natural selection → evolution.',
        yr: 'SS2',
      },
      {
        q: "Darwin's concept of 'survival of the fittest' means:",
        o: [
          'The physically strongest individual always survives',
          'Organisms that are best adapted to their current environment survive, reproduce more, and pass on their advantageous traits',
          'All individuals of a species are equally fit',
          'Only the largest organisms survive competition',
        ],
        a: 1,
        e: "'Survival of the fittest' (Herbert Spencer's term, adopted by Darwin): individuals with heritable traits best suited to their environment are more likely to survive, reproduce, and pass those traits to offspring. Fitness = reproductive success in a given environment, not just physical strength.",
        h: 'Survival of fittest: best-adapted individuals reproduce more → pass on advantageous traits.',
        yr: 'SS2',
      },
      {
        q: "Lamarck's theory of evolution (inheritance of acquired characteristics) was rejected because:",
        o: [
          'He did not observe animals',
          'His observations of giraffes were incorrect',
          "Acquired characteristics (changes to the body during an organism's lifetime) are not encoded in DNA and cannot be passed to offspring — Weismann's experiment with mice tails disproved it",
          'Darwin proposed the same theory',
        ],
        a: 2,
        e: "Lamarck proposed that characteristics acquired during an organism's lifetime (e.g., muscles developed through use) are passed to offspring. This was disproved by Weismann (cut tails of mice for 20+ generations — offspring always born with full tails) and by modern genetics (only heritable genetic changes are passed on).",
        h: 'Lamarck disproved: acquired characteristics (non-genetic changes) cannot be inherited.',
        yr: 'SS2',
      },
      {
        q: 'The fossil record provides evidence for evolution by showing:',
        o: [
          'That all species appeared simultaneously',
          'That species have always been exactly the same',
          'A progression of life forms from simple to more complex over geological time, with transitional forms linking major groups',
          'That evolution does not occur',
        ],
        a: 2,
        e: 'Fossil record evidence: (1) shows progression from simple (prokaryotes, ~3.5 billion years ago) to complex organisms over geological time; (2) transitional fossils (e.g., Archaeopteryx linking dinosaurs and birds); (3) extinct species; (4) changes in species over time visible in rock strata.',
        h: 'Fossil record: shows progression of life forms; transitional fossils; extinct species.',
        yr: 'SS2',
      },
      {
        q: 'Homologous structures in different species (e.g., human arm, whale flipper, bat wing) suggest:',
        o: [
          'These animals perform the same function with these structures',
          'The structures evolved independently in each species (convergent evolution)',
          'These species share a common ancestor — the basic bone structure is the same though adapted for different functions',
          'The structures are identical in all species',
        ],
        a: 2,
        e: 'Homologous structures: same basic underlying structure (e.g., pentadactyl limb with humerus, radius, ulna, carpals, metacarpals, phalanges) but modified for different functions. This suggests common ancestry (divergent evolution). Different from analogous structures (same function, different origin — e.g., bird wing and insect wing).',
        h: 'Homologous structures: same basic structure, different functions → common ancestry (divergent evolution).',
        yr: 'SS2',
      },
      {
        q: 'Industrial melanism in the peppered moth is a classic example of natural selection because:',
        o: [
          'Moths changed colour voluntarily',
          'Darker moths survived better in polluted (sooty) environments — birds preferentially ate lighter moths on dark tree bark — dark allele frequency increased',
          'Moths mated more when darker',
          'Light moths mutated to become dark',
        ],
        a: 1,
        e: 'Before industrial revolution: most moths pale (camouflaged on lichen-covered trees) — dark moths easily seen and eaten by birds. After pollution: soot killed lichen → dark bark → dark moths better camouflaged → light moths eaten more → dark allele frequency increased in population. Classic natural selection in action.',
        h: 'Peppered moth: pollution → dark moths better camouflaged → light moths eaten → dark allele increased.',
        yr: 'SS2',
      },
      {
        q: 'Genetic drift is a mechanism of evolution that refers to:',
        o: [
          'The directional change driven by natural selection',
          'Random changes in allele frequencies in small populations due to chance events — not natural selection',
          'Mutation creating new alleles',
          'Migration of individuals into or out of a population',
        ],
        a: 1,
        e: 'Genetic drift: random fluctuations in allele frequencies in small populations due to chance (not natural selection). In small populations, chance events (e.g., a fire killing some individuals) can dramatically change allele frequencies. Extreme form: founder effect (new population from few individuals) and bottleneck effect.',
        h: 'Genetic drift: random allele frequency changes in small populations due to chance.',
        yr: 'SS2',
      },
      {
        q: 'Speciation occurs when:',
        o: [
          'Two populations of the same species mate freely',
          'A population migrates to a new area only',
          'Two populations become reproductively isolated — unable to interbreed and produce fertile offspring — and diverge genetically to form two distinct species',
          'Mutations occur in a single individual',
        ],
        a: 2,
        e: 'Speciation: formation of new species. Allopatric speciation: geographic barrier (river, mountain) isolates populations → they evolve independently → become reproductively isolated → new species. Sympatric speciation: reproductive isolation without geographic barrier (e.g., polyploidy in plants).',
        h: 'Speciation: reproductive isolation → independent evolution → new species. Geographic barrier often initiates.',
        yr: 'SS2',
      },
      {
        q: 'Analogous structures (e.g., bird wing and insect wing) are evidence of:',
        o: [
          'Common ancestry',
          'Divergent evolution',
          'Convergent evolution — different ancestral lineages evolving similar structures for the same function (flight) due to similar environmental pressures',
          'Genetic drift',
        ],
        a: 2,
        e: 'Analogous structures: same function, different evolutionary origin. Bird wing (modified forelimb, bone and feathers) vs insect wing (membranous, from exoskeleton extension) — both for flight but completely different structures. This shows convergent evolution — natural selection favouring similar solutions independently.',
        h: 'Analogous structures: same function, different origin → convergent evolution.',
        yr: 'SS2',
      },
      {
        q: 'Variation within a species is essential for natural selection because:',
        o: [
          'It allows all individuals to survive equally',
          'Without heritable variation, all individuals would be equally fit — natural selection has nothing to act upon; variation provides the raw material for selection',
          'It prevents speciation',
          'Variation is always harmful',
        ],
        a: 1,
        e: 'Natural selection requires variation: if all individuals were identical, none would have a selective advantage. Heritable variation (due to mutations, sexual reproduction, recombination) means some individuals are better adapted → they survive and reproduce more → allele frequencies change → evolution.',
        h: 'Variation = raw material for natural selection. Without variation, no selection can occur.',
        yr: 'SS2',
      },
      {
        q: 'The Hardy-Weinberg principle states that allele frequencies in a population remain constant if:',
        o: [
          'Natural selection occurs',
          'The population is small',
          'The population is large, random mating occurs, no mutation, no migration, no natural selection — equilibrium is maintained',
          'Mutations are frequent',
        ],
        a: 2,
        e: 'Hardy-Weinberg equilibrium: allele and genotype frequencies remain constant generation after generation if: (1) large population; (2) random mating; (3) no mutation; (4) no migration; (5) no natural selection. When these conditions are violated, evolution occurs.',
        h: 'Hardy-Weinberg: allele frequencies constant if large pop, random mating, no mutation/migration/selection.',
        yr: 'SS2',
      },
      {
        q: "Overproduction (high reproductive rate) is important to Darwin's theory because:",
        o: [
          'It ensures all offspring survive',
          'It creates variation in a population',
          'It means more individuals are born than resources can support — creating competition for survival, which drives natural selection',
          'It speeds up mutation rate',
        ],
        a: 2,
        e: 'Malthus (economist) influenced Darwin: populations reproduce faster than food/resources grow. More individuals than resources → competition → only the best-adapted survive and reproduce. This overproduction is the engine of natural selection.',
        h: 'Overproduction: more offspring than resources → competition → selection of best-adapted.',
        yr: 'SS2',
      },
      {
        q: 'Antibiotic resistance in bacteria is an example of natural selection because:',
        o: [
          'Bacteria deliberately mutate to become resistant',
          'Antibiotics cause bacteria to evolve new genes',
          'Random mutations occasionally produce resistant bacteria; antibiotics kill non-resistant bacteria, so resistant bacteria survive, reproduce, and the resistance allele frequency increases',
          'All bacteria are naturally resistant',
        ],
        a: 2,
        e: 'Antibiotic resistance evolution: (1) random mutations occasionally produce resistant bacteria (before antibiotics introduced); (2) antibiotic exposure kills non-resistant bacteria; (3) resistant bacteria survive, reproduce; (4) resistant alleles increase in frequency. Classic natural selection — antibiotic acts as the selective pressure.',
        h: 'Antibiotic resistance: natural selection. Random mutation → resistance → antibiotics kill sensitive → resistant frequency increases.',
        yr: 'SS2',
      },
      {
        q: 'Comparative embryology as evidence for evolution shows that:',
        o: [
          'All embryos are identical throughout development',
          'Early embryos of different vertebrate species (fish, amphibian, reptile, bird, mammal) are remarkably similar — suggesting common ancestry — and diverge as development proceeds',
          'Embryos show no similarity whatsoever',
          'Only mammals have embryos',
        ],
        a: 1,
        e: 'Comparative embryology: early embryos of all vertebrates (fish, frogs, lizards, chicks, humans) share common features: gill slits (pharyngeal arches), notochord, similar tail. The more closely related two species, the longer their embryos remain similar. This suggests all vertebrates share common ancestors.',
        h: 'Comparative embryology: all vertebrate embryos similar early on → common ancestry.',
        yr: 'SS2',
      },
      {
        q: 'Artificial selection (selective breeding) provides evidence for evolution because:',
        o: [
          'It shows species cannot change',
          'It demonstrates that humans, not nature, control evolution',
          'It demonstrates that heritable traits can be deliberately changed over generations — showing the power of selection to change populations rapidly (e.g., dog breeds from wolves, crop plant varieties)',
          'It disproves natural selection',
        ],
        a: 2,
        e: 'Artificial selection: humans select and breed individuals with desired traits. Over generations, populations change dramatically. Dog breeds (from wolves, thousands of years of selection); crop plants (maize/corn from teosinte); livestock breeds all show rapid heritable change under selection. This proves selection can cause evolution.',
        h: 'Artificial selection: humans select for desired traits → rapid population change → demonstrates evolution.',
        yr: 'SS2',
      },
      {
        q: 'The theory of evolution is described as a scientific theory because:',
        o: [
          'It is just a guess with no evidence',
          'It has been tested by many scientists, supported by multiple lines of evidence, and makes testable predictions — though not yet proven with absolute certainty',
          'It was proposed by one scientist and never tested',
          'All scientists agree it is incorrect',
        ],
        a: 1,
        e: "Scientific theory ≠ everyday 'guess'. A scientific theory is a well-substantiated explanation supported by extensive evidence from multiple independent sources. Evolution is supported by fossils, genetics, comparative anatomy, molecular biology, observed natural selection. It is the foundation of modern biology.",
        h: 'Scientific theory = well-substantiated, multi-evidence explanation. Evolution supported by fossils, genetics, anatomy, molecular biology.',
        yr: 'SS2',
      },
      {
        q: 'Which of the following would NOT contribute to genetic variation in a sexually reproducing species?',
        o: [
          'Crossing over during meiosis prophase I',
          'Independent assortment of chromosomes during meiosis',
          'Random fertilisation of gametes',
          'Binary fission producing identical daughter cells',
        ],
        a: 3,
        e: 'Binary fission is asexual reproduction producing genetically identical cells — it does NOT contribute to genetic variation. Crossing over, independent assortment, and random fertilisation are all sources of genetic variation in sexually reproducing organisms.',
        h: 'Binary fission: asexual, no genetic variation. Crossing over, independent assortment, random fertilisation: all increase variation.',
        yr: 'SS2',
      },
      {
        q: 'The gene pool of a population refers to:',
        o: [
          'The DNA in a single individual',
          'The physical appearance of all individuals',
          'All the alleles of all genes present in all individuals of a population at a given time',
          'The number of chromosomes in each cell',
        ],
        a: 2,
        e: 'Gene pool: the total collection of all alleles in all individuals of a population. A large, diverse gene pool = greater genetic variation = greater adaptability to environmental change. Factors that reduce gene pool size (genetic drift, bottleneck, founder effect) reduce evolutionary potential.',
        h: 'Gene pool: all alleles of all genes in all individuals of a population. Large = more variation = more adaptable.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Ecology',
    topicCode: 'SS2-BIO-10',
    module: 'Ecology',
    term: 'Second Term',
    contentHTML:
      '<div class="learn-intro"><span class="learn-keyword">Ecology</span> is the scientific study of the relationships between organisms and their environment. It includes the study of populations, communities, ecosystems, and the flow of energy and matter.</div><h3 class="learn-subheading">1. Ecological Terms</h3><ul class="learn-list"><li><strong>Habitat:</strong> the place where an organism lives</li><li><strong>Niche:</strong> the functional role of an organism in its ecosystem (what it eats, what eats it, how it interacts)</li><li><strong>Population:</strong> all individuals of one species in an area at the same time</li><li><strong>Community:</strong> all populations of different species in an area</li><li><strong>Ecosystem:</strong> community + abiotic (non-living) environment</li><li><strong>Biome:</strong> large-scale ecosystem characterised by climate and vegetation type</li></ul><h3 class="learn-subheading">2. Food Chains and Webs</h3><ul class="learn-list"><li>Producer → Primary consumer → Secondary consumer → Tertiary consumer</li><li>Arrows show direction of energy flow</li><li>Trophic levels: producers (level 1), herbivores (level 2), carnivores (level 3+)</li><li>Energy is lost at each trophic level (~90% as heat); only ~10% passed on</li></ul><h3 class="learn-subheading">3. Nutrient Cycles</h3><ul class="learn-list"><li><strong>Carbon cycle:</strong> photosynthesis (CO₂ → organic carbon), respiration/decomposition/combustion (organic carbon → CO₂)</li><li><strong>Nitrogen cycle:</strong> N₂ fixation (Rhizobium), nitrification (Nitrosomonas, Nitrobacter), denitrification (Pseudomonas), ammonification (decomposers)</li></ul><h3 class="learn-subheading">4. Population Ecology</h3><p class="learn-p">Population growth: J-shaped (exponential) in unlimited resources; S-shaped (logistic) when resources are limited (reaches carrying capacity K). Limiting factors: predation, disease, food shortage, competition.</p><div class="learn-tip-box"><span class="learn-tip-icon">💡</span><strong>Energy Flow:</strong> ~10% efficiency between trophic levels. Shorter food chain = more energy available. Producers always at base.</div><div class="learn-tip-box"><span class="learn-tip-icon">🎯</span><strong>Summary:</strong> Ecosystem = community + abiotic environment. Food chain: producer→consumer. 10% energy passed up. Carbon cycle: photosynthesis↔respiration. Nitrogen cycle: fixation→nitrification→plant uptake→denitrification.</div>',
    questions: [
      {
        q: 'The niche of an organism refers to:',
        o: [
          'The physical place where it lives (habitat)',
          'Its functional role in the ecosystem — what it eats, what eats it, how it uses resources, and its interactions with other species',
          'The size of its population',
          'Its geographic range across a continent',
        ],
        a: 1,
        e: "Niche ≠ habitat. Habitat = where it lives (address). Niche = its ecological role (profession): what it eats, when it's active, how it uses resources, what predators it has, etc. No two species can occupy exactly the same niche (competitive exclusion principle).",
        h: "Niche = ecological role (not just location). Two species can't occupy same niche → competitive exclusion.",
        yr: 'SS2',
      },
      {
        q: 'In a food chain: grass → grasshopper → frog → snake → eagle. The grass is a:',
        o: [
          'Primary consumer',
          'Secondary consumer',
          'Producer — it converts light energy into chemical energy through photosynthesis',
          'Decomposer',
        ],
        a: 2,
        e: 'Grass is a producer (autotroph): it converts sunlight + CO₂ + water → glucose (photosynthesis). All food chains start with a producer. Grasshopper = primary consumer (herbivore); Frog = secondary consumer; Snake = tertiary consumer; Eagle = quaternary consumer.',
        h: 'Grass = producer (makes food by photosynthesis). All food chains begin with a producer.',
        yr: 'SS2',
      },
      {
        q: 'Energy is lost between trophic levels mainly because:',
        o: [
          'Energy is destroyed at each level',
          'Organisms use energy for their own metabolism (respiration), movement, heat loss, and excretion — only ~10% is stored in body tissues available to the next level',
          'All energy is reflected as light',
          'Decomposers take all energy before the next level',
        ],
        a: 1,
        e: 'Only ~10% of energy from one trophic level is available to the next. The rest (90%) is lost as: heat (from respiration), energy in undigested waste (faeces), energy in excretion. This is why food chains are short (usually 4-5 links) and why eating lower on the food chain is more energy-efficient.',
        h: '~10% energy passed between trophic levels. 90% lost as heat, excretion, undigested material.',
        yr: 'SS2',
      },
      {
        q: 'The carbon cycle returns CO₂ to the atmosphere through:',
        o: [
          'Photosynthesis only',
          'Nitrogen fixation',
          'Respiration, decomposition of organic matter, combustion of fossil fuels, and volcanic activity',
          'Nitrogen cycle processes',
        ],
        a: 2,
        e: 'CO₂ returns to atmosphere via: (1) aerobic respiration (all living organisms); (2) decomposition/decay of dead organisms by bacteria and fungi; (3) combustion of fossil fuels (coal, oil, gas); (4) volcanic eruptions. Photosynthesis REMOVES CO₂ from atmosphere.',
        h: 'CO₂ returned to atmosphere: respiration + decomposition + combustion + volcanoes.',
        yr: 'SS2',
      },
      {
        q: 'Rhizobium bacteria in root nodules of legumes are important in the nitrogen cycle because they:',
        o: [
          'Denitrify nitrates back to N₂',
          'Convert ammonium to nitrates (nitrification)',
          'Fix atmospheric nitrogen (N₂) into ammonia/ammonium — making nitrogen available to plants',
          'Decompose dead organisms',
        ],
        a: 2,
        e: 'Rhizobium lives symbiotically in root nodules of legumes (peas, beans, groundnuts). It contains nitrogenase enzyme that converts N₂ (atmospheric nitrogen) into ammonium (NH₄⁺), which plants can use for amino acid and protein synthesis. This nitrogen fixation is the primary entry of nitrogen into the biotic cycle.',
        h: 'Rhizobium: fixes atmospheric N₂ → ammonium → available to plants. Lives in legume root nodules.',
        yr: 'SS2',
      },
      {
        q: 'A pyramid of numbers shows:',
        o: [
          'The amount of energy at each trophic level',
          'The biomass at each trophic level',
          'The number of organisms at each trophic level — usually decreasing from producer to top consumer (but can be inverted if producers are very large trees)',
          'The species diversity at each level',
        ],
        a: 2,
        e: 'Pyramid of numbers: number of organisms at each trophic level. Usually pyramid-shaped (many producers, fewer herbivores, fewer carnivores). Can be inverted if producers are large (one oak tree supports many caterpillars). Compare with pyramid of biomass and pyramid of energy (pyramids of energy are always true pyramids).',
        h: 'Pyramid of numbers: number of organisms per level. Can be inverted. Pyramid of energy: always pyramid-shaped.',
        yr: 'SS2',
      },
      {
        q: 'The carrying capacity (K) of an environment for a population refers to:',
        o: [
          'The maximum number of species in the ecosystem',
          'The maximum population size that the environment can sustain indefinitely given available resources (food, space, water)',
          'The total biomass produced per year',
          'The rate of population growth',
        ],
        a: 1,
        e: 'Carrying capacity K: the maximum population size the environment can support, limited by available resources (food, water, space, shelter, mates). Logistic growth (S-curve): population grows rapidly at first, then slows as resources become limiting, levelling off at K.',
        h: 'Carrying capacity K: maximum population size the environment can sustainably support.',
        yr: 'SS2',
      },
      {
        q: 'Denitrification in the nitrogen cycle is carried out by bacteria that:',
        o: [
          'Convert N₂ to ammonium',
          'Convert ammonium to nitrate',
          'Convert nitrates back to N₂ gas — reducing soil nitrate content (happens in waterlogged/anaerobic soils)',
          'Decompose dead organisms to ammonium',
        ],
        a: 2,
        e: 'Denitrification: bacteria (Pseudomonas, Thiobacillus) in anaerobic/waterlogged soils reduce nitrates (NO₃⁻) → N₂ gas (released to atmosphere). This returns nitrogen to the atmosphere. It is counterproductive to agriculture (reduces soil nitrates) and is why waterlogged soils have fewer nutrients.',
        h: 'Denitrification: bacteria convert nitrates → N₂ gas. Occurs in anaerobic/waterlogged soils.',
        yr: 'SS2',
      },
      {
        q: 'A food web is more realistic than a single food chain because:',
        o: [
          'It only shows one pathway for energy flow',
          'It shows all possible feeding relationships — most organisms feed on more than one type of food and are eaten by more than one predator (interconnected chains)',
          'It shows energy is not lost between levels',
          'It excludes decomposers',
        ],
        a: 1,
        e: 'In nature, organisms rarely have just one food source or one predator. Food webs show the complex, interconnected feeding relationships in an ecosystem. This makes ecosystems more stable — if one species declines, energy can flow through alternative pathways.',
        h: 'Food web: multiple, interconnected food chains. More realistic; shows complex feeding relationships.',
        yr: 'SS2',
      },
      {
        q: 'Decomposers (bacteria and fungi) are essential in ecosystems because they:',
        o: [
          'Produce food through photosynthesis',
          'Are the top predators in food chains',
          'Break down dead organic matter — releasing nutrients (especially nitrogen, phosphorus, carbon) back into the soil for producers to use again',
          'Block the recycling of nutrients',
        ],
        a: 2,
        e: 'Decomposers (bacteria and fungi of decay) break down dead organisms and waste into inorganic minerals (nitrates, phosphates, CO₂). This recycling of nutrients is essential — without decomposers, nutrients would remain locked in dead organic matter and unavailable to producers.',
        h: 'Decomposers: break down dead matter → release inorganic nutrients → producers reuse them. Essential for nutrient cycling.',
        yr: 'SS2',
      },
      {
        q: 'The exponential (J-shaped) growth of a population occurs when:',
        o: [
          'Resources are limited and competition is intense',
          'The environment has unlimited resources and no limiting factors — population doubles repeatedly in each time interval',
          'The population has reached carrying capacity',
          'Predators control the population',
        ],
        a: 1,
        e: 'J-shaped (exponential) growth: when resources are not limiting (unlimited food, space, water) and mortality is low. Each individual reproduces at a constant rate → population grows at an accelerating rate. Rarely sustained in nature — limiting factors (predation, disease, competition, food shortage) eventually slow growth → S-curve (logistic).',
        h: 'J-shaped growth: unlimited resources → exponential increase. Unsustained in nature.',
        yr: 'SS2',
      },
      {
        q: 'Competition between organisms of the SAME species is called:',
        o: [
          'Interspecific competition',
          'Symbiosis',
          'Intraspecific competition — members of the same species compete for the same resources (food, mates, territory)',
          'Predation',
        ],
        a: 2,
        e: 'Intraspecific competition: between individuals of the same species (same niche, same resource needs). This is often more intense than interspecific competition. It is a key factor in regulating population size and driving natural selection (individuals with advantageous traits win competition).',
        h: 'Intraspecific competition: same species compete for same resources. Drives natural selection.',
        yr: 'SS2',
      },
      {
        q: 'Mutualism is a type of symbiosis where:',
        o: [
          'One organism benefits while the other is harmed (parasitism)',
          'One organism benefits while the other is unaffected (commensalism)',
          'Both organisms benefit from the relationship',
          'Neither organism benefits',
        ],
        a: 2,
        e: 'Mutualism: both species benefit. Examples: (1) Rhizobium in legume root nodules (bacteria get carbon; plant gets fixed nitrogen); (2) mycorrhizae (fungi + plant roots; fungus gets glucose; plant gets water/minerals); (3) oxpecker birds and rhinoceros (birds eat ticks; rhino gets parasite removal).',
        h: 'Mutualism: both species benefit. Examples: Rhizobium-legume, mycorrhizae, cleaner fish.',
        yr: 'SS2',
      },
      {
        q: 'Eutrophication of water bodies is caused by:',
        o: [
          'Removal of algae',
          'Excessive nutrient (nitrogen and phosphorus) inputs from agricultural runoff or sewage — causing algal blooms, reduced light, O₂ depletion, and death of aquatic life',
          'Reduced temperature of the water',
          'Introduction of predatory fish',
        ],
        a: 1,
        e: 'Eutrophication: excess nutrients (N, P from fertiliser runoff/sewage) → algal bloom → algae block sunlight → underwater plants die → algae die → decomposers respire → O₂ depleted → fish and aquatic animals suffocate (BOD increases).',
        h: 'Eutrophication: excess nutrients → algal bloom → O₂ depletion → aquatic life death.',
        yr: 'SS2',
      },
      {
        q: 'An ecosystem is defined as:',
        o: [
          'All organisms in an area',
          'The physical environment only',
          'A community of organisms interacting with each other AND with their abiotic (non-living) environment — including nutrient cycles and energy flow',
          'A single food chain in an area',
        ],
        a: 2,
        e: 'Ecosystem = biotic community (all organisms: plants, animals, microorganisms) + abiotic environment (temperature, light, water, soil, minerals, pH). All interact through energy flow and nutrient cycling. Examples: rainforest, coral reef, lake, savanna.',
        h: 'Ecosystem = biotic community + abiotic environment (interactions included).',
        yr: 'SS2',
      },
      {
        q: 'Which trophic level contains the greatest amount of energy?',
        o: [
          'Top carnivores',
          'Secondary consumers',
          'Primary consumers',
          'Producers — they capture solar energy through photosynthesis; all subsequent levels have less energy',
        ],
        a: 3,
        e: 'Energy decreases at each trophic level (~10% efficiency). Producers (trophic level 1) capture the most solar energy. Each subsequent level has approximately 10% of the energy of the level below. Top predators have the least energy — this is why there are always fewer top predators than producers.',
        h: 'Greatest energy at producer level (trophic level 1). Energy decreases going up the food chain.',
        yr: 'SS2',
      },
      {
        q: 'Biotic factors affecting a population include:',
        o: [
          'Temperature and rainfall',
          'Soil pH and mineral content',
          'Predation, competition, disease, parasitism, and food availability (other organisms)',
          'Light intensity and wind speed',
        ],
        a: 2,
        e: 'Biotic factors: interactions with other living organisms — predation (direct mortality), competition (resource depletion), disease (pathogens), parasitism (health reduction), food availability. Abiotic factors: temperature, light, water, soil pH, wind, salinity, etc.',
        h: 'Biotic factors: predation, competition, disease, parasitism (involve other living organisms).',
        yr: 'SS2',
      },
      {
        q: 'The greenhouse effect and global warming are related to the carbon cycle because:',
        o: [
          'Plants stop photosynthesis when it is warm',
          "Burning fossil fuels releases CO₂ that was stored for millions of years — rapidly increasing atmospheric CO₂ concentration beyond the natural carbon cycle's ability to buffer it",
          'Nitrogen cycle produces more greenhouse gases',
          'Water cycle absorbs all excess CO₂',
        ],
        a: 1,
        e: 'Fossil fuels (coal, oil, gas) are ancient organic carbon stored underground. Burning them releases CO₂ rapidly into the atmosphere. This exceeds the rate at which photosynthesis and ocean absorption can remove CO₂ → atmospheric CO₂ increases → enhanced greenhouse effect → global warming.',
        h: 'Fossil fuel burning: releases ancient stored CO₂ faster than natural cycle absorbs → enhanced greenhouse effect.',
        yr: 'SS2',
      },
      {
        q: 'Ecological succession refers to:',
        o: [
          'The seasonal migration of animals',
          'A rapid decrease in biodiversity',
          'The gradual, directional change in a community over time — from pioneer species to a stable climax community',
          'The extinction of species from an area',
        ],
        a: 2,
        e: 'Ecological succession: the progressive change in species composition of a community over time. Primary succession: on bare rock/new land (pioneers = mosses, lichens → intermediate communities → climax community = rainforest or grassland depending on climate). Secondary succession: on disturbed land (after fire/logging) — faster as soil already present.',
        h: 'Ecological succession: pioneer → intermediate → climax community. Primary (bare rock) vs secondary (disturbed land).',
        yr: 'SS2',
      },
      {
        q: 'The water cycle (hydrological cycle) is important in ecosystems because:',
        o: [
          'It only affects ocean ecosystems',
          'It has no link to biological processes',
          'It continuously recycles water through evaporation, condensation, and precipitation — making water available to all organisms and linking terrestrial and aquatic ecosystems',
          'It only operates in tropical regions',
        ],
        a: 2,
        e: 'Water cycle: solar energy evaporates water from surfaces (sea, lakes, plants/transpiration) → water vapour rises → condenses to form clouds → precipitation (rain, snow) → runs off or infiltrates soil → groundwater → rivers → sea. Essential for all life (metabolic processes, transport of nutrients, temperature regulation). Links terrestrial and aquatic ecosystems.',
        h: 'Water cycle: evaporation → condensation → precipitation → runoff → recycling. Essential for all life.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Microorganisms and Disease',
    topicCode: 'SS2-BIO-11',
    module: 'Health Biology',
    term: 'Third Term',
    contentHTML:
      '<div class="learn-intro"><span class="learn-keyword">Microorganisms</span> include bacteria, viruses, fungi, and protozoa. Some are beneficial (decomposers, nitrogen fixers, food production) while others are pathogens causing disease. Understanding disease transmission and immunity is essential for public health.</div><h3 class="learn-subheading">1. Types of Microorganisms</h3><div class="learn-table-wrap"><table class="learn-table"><thead><tr><th>Type</th><th>Cell Type</th><th>Examples</th><th>Role</th></tr></thead><tbody><tr><td>Bacteria</td><td>Prokaryote</td><td>Lactobacillus, Rhizobium, Salmonella</td><td>Decomposition, N-fixation, disease</td></tr><tr><td>Viruses</td><td>Non-cellular</td><td>HIV, influenza, SARS-CoV-2</td><td>Obligate intracellular parasites</td></tr><tr><td>Fungi</td><td>Eukaryote</td><td>Penicillium, Aspergillus, Candida</td><td>Decomposition, food, antibiotic production</td></tr><tr><td>Protozoa</td><td>Eukaryote</td><td>Plasmodium, Amoeba, Trypanosoma</td><td>Some cause malaria, sleeping sickness</td></tr></tbody></table></div><h3 class="learn-subheading">2. Common Diseases in Nigeria</h3><div class="learn-table-wrap"><table class="learn-table"><thead><tr><th>Disease</th><th>Causative Agent</th><th>Vector/Transmission</th><th>Control</th></tr></thead><tbody><tr><td>Malaria</td><td>Plasmodium spp.</td><td>Female Anopheles mosquito</td><td>Insecticides, bed nets, antimalarials</td></tr><tr><td>Tuberculosis (TB)</td><td>Mycobacterium tuberculosis</td><td>Airborne droplets</td><td>BCG vaccine, antibiotics</td></tr><tr><td>Cholera</td><td>Vibrio cholerae</td><td>Contaminated water/food</td><td>Clean water, ORS, oral cholera vaccine</td></tr><tr><td>HIV/AIDS</td><td>HIV (retrovirus)</td><td>Blood, sexual contact, mother to child</td><td>ARVs, condoms, education</td></tr><tr><td>Typhoid</td><td>Salmonella typhi</td><td>Contaminated food/water</td><td>Sanitation, vaccine, antibiotics</td></tr></tbody></table></div><h3 class="learn-subheading">3. Body\'s Defence Mechanisms</h3><ul class="learn-list"><li><strong>Physical barriers:</strong> skin, mucus, cilia, stomach acid, tears (lysozyme)</li><li><strong>Non-specific immunity:</strong> phagocytosis by neutrophils and macrophages; inflammation; fever</li><li><strong>Specific immunity:</strong> lymphocytes produce antibodies specific to antigens; immunological memory (vaccines exploit this)</li></ul><h3 class="learn-subheading">4. Vaccines and Antibiotics</h3><ul class="learn-list"><li><strong>Vaccines:</strong> introduce weakened/killed pathogen or antigens → stimulate immune memory without causing disease → protection on re-exposure</li><li><strong>Antibiotics:</strong> kill or inhibit bacteria (NOT effective against viruses); overuse leads to antibiotic resistance</li></ul><div class="learn-tip-box"><span class="learn-tip-icon">💡</span><strong>Malaria:</strong> Plasmodium parasite transmitted by FEMALE Anopheles mosquito. Life cycle: mosquito bite → liver → red blood cells → symptoms → mosquito bites again. NOT male mosquito.</div><div class="learn-tip-box"><span class="learn-tip-icon">🎯</span><strong>Summary:</strong> Bacteria (prokaryote), virus (non-cellular), fungi (eukaryote), protozoa (eukaryote). Malaria=Plasmodium+Anopheles. Antibiotics: kill bacteria NOT viruses. Vaccines: immunological memory.</div>',
    questions: [
      {
        q: 'Malaria is transmitted by:',
        o: [
          'Male Anopheles mosquito',
          'Female Anopheles mosquito — which transmits the Plasmodium parasite when it takes a blood meal',
          'Contaminated drinking water',
          'Direct contact with infected person',
        ],
        a: 1,
        e: 'Only the female Anopheles mosquito transmits malaria — she needs blood for egg development. The male feeds only on plant nectar. Plasmodium sporozoites are injected when the female mosquito bites. The parasite multiplies in the liver then invades red blood cells.',
        h: 'Malaria: FEMALE Anopheles mosquito transmits Plasmodium parasite.',
        yr: 'SS2',
      },
      {
        q: 'Antibiotics are effective against:',
        o: [
          'All types of microorganisms',
          'Viruses only',
          'Bacteria only — they target bacterial cell structures (cell wall, ribosomes) not present in host cells; they have no effect on viruses',
          'Fungi only',
        ],
        a: 2,
        e: "Antibiotics (penicillin, streptomycin, etc.) target structures unique to bacteria: cell wall synthesis, bacterial ribosomes (70S), cell membrane function, DNA replication. These targets don't exist in human cells. Viruses have no cell wall, no ribosomes, no metabolism of their own — antibiotics have NO effect on viral infections.",
        h: 'Antibiotics: effective against bacteria. NOT effective against viruses. Target bacterial structures.',
        yr: 'SS2',
      },
      {
        q: 'Vaccines provide immunity against diseases by:',
        o: [
          'Killing all pathogens in the body immediately',
          'Directly providing antibodies (passive immunisation) in all cases',
          'Stimulating the immune system to produce memory cells without causing the disease — so future exposure triggers rapid antibody production (active immunity)',
          'Blocking all viral replication permanently',
        ],
        a: 2,
        e: 'Vaccines (active immunisation): introduce antigens (weakened/killed pathogens, or antigen proteins) → immune system recognises them → activates B lymphocytes → produce antibodies + form memory cells. On real infection: memory cells rapidly produce large amounts of antibodies → pathogen destroyed before disease develops.',
        h: 'Vaccines: introduce antigens → immune memory → rapid response on real infection.',
        yr: 'SS2',
      },
      {
        q: 'HIV (Human Immunodeficiency Virus) primarily attacks:',
        o: [
          'Red blood cells',
          "CD4+ T helper lymphocytes — destroying the immune system's ability to coordinate immune responses, leading to AIDS",
          'Neutrophils and platelets',
          'Nerve cells of the spinal cord',
        ],
        a: 1,
        e: 'HIV targets CD4+ T helper cells (T lymphocytes). HIV attaches to CD4 receptors, enters the cell, replicates, and destroys the cell. As CD4 cell count falls, the immune system is progressively weakened → susceptibility to opportunistic infections → AIDS (Acquired Immunodeficiency Syndrome).',
        h: 'HIV attacks CD4+ T helper lymphocytes → weakens immune system → AIDS.',
        yr: 'SS2',
      },
      {
        q: 'Cholera is caused by Vibrio cholerae and causes severe diarrhoea. It is mainly transmitted by:',
        o: [
          'Mosquito bites',
          'Airborne droplets',
          'Contaminated water and food — the bacteria produce enterotoxin causing massive fluid and electrolyte loss',
          'Sexual contact',
        ],
        a: 2,
        e: 'Cholera transmission: faecal-oral route. V. cholerae in faeces of infected person contaminates water/food. Ingested bacteria colonise small intestine, produce cholera toxin (CT), which causes cAMP-mediated massive watery diarrhoea (rice-water stools) → severe dehydration. Control: clean water, sanitation, ORS treatment.',
        h: 'Cholera: V. cholerae, contaminated water/food (faecal-oral). Causes severe watery diarrhoea.',
        yr: 'SS2',
      },
      {
        q: 'The main difference between bacteria and viruses is that viruses:',
        o: [
          'Are larger than bacteria',
          'Have a cell wall',
          'Are non-cellular — they have no cell structure, only genetic material (DNA or RNA) enclosed in a protein coat (capsid); they cannot replicate outside a host cell',
          'Can be treated with the same antibiotics as bacteria',
        ],
        a: 2,
        e: "Viruses are non-cellular obligate intracellular parasites: no cell membrane, no cytoplasm, no organelles, no ribosomes. They consist of nucleic acid (DNA or RNA) in a protein capsid (some with a lipid envelope). They can only replicate inside a host cell, hijacking the cell's machinery.",
        h: 'Viruses: non-cellular; DNA or RNA in protein capsid; replicate only inside host cells.',
        yr: 'SS2',
      },
      {
        q: 'The BCG vaccine protects against:',
        o: [
          'Malaria',
          'Cholera',
          'Tuberculosis (TB) — BCG (Bacille Calmette-Guérin) is a live attenuated strain of Mycobacterium bovis',
          'HIV',
        ],
        a: 2,
        e: 'BCG (Bacille Calmette-Guérin) vaccine: live attenuated strain of Mycobacterium bovis (bovine tuberculosis bacterium). Given to newborns in TB-endemic regions (including Nigeria). Provides ~80% protection against severe TB in children. Does not fully prevent all forms of adult TB but reduces severity.',
        h: 'BCG vaccine: protects against tuberculosis (TB). Live attenuated Mycobacterium bovis.',
        yr: 'SS2',
      },
      {
        q: 'Which of the following is a protozoan disease?',
        o: [
          'Tuberculosis (bacterial)',
          'Influenza (viral)',
          'Malaria — caused by Plasmodium, a protozoan parasite',
          'Ringworm (fungal)',
        ],
        a: 2,
        e: 'Protozoa are unicellular eukaryotes. Protozoan diseases: malaria (Plasmodium spp.), sleeping sickness (Trypanosoma brucei), amoebiasis (Entamoeba histolytica), giardiasis (Giardia), toxoplasmosis (Toxoplasma). Bacterial, viral, and fungal diseases are caused by different types of microorganisms.',
        h: 'Protozoan diseases: malaria (Plasmodium), sleeping sickness (Trypanosoma), amoebiasis.',
        yr: 'SS2',
      },
      {
        q: 'The skin acts as the first line of defence against disease by:',
        o: [
          'Producing antibodies',
          'Providing a physical barrier — keratin-rich, relatively impermeable; sebaceous glands produce sebum (slightly acidic, antimicrobial); sweat contains lysozyme',
          'Phagocytosing bacteria directly',
          'Producing white blood cells',
        ],
        a: 1,
        e: 'Skin defences: (1) intact skin = physical barrier (pathogens cannot penetrate); (2) sebum from sebaceous glands = slightly acidic → inhibits bacterial growth; (3) sweat contains lysozyme (enzyme that breaks down bacterial cell walls); (4) normal skin flora compete with pathogens.',
        h: 'Skin: physical barrier (keratin), acidic sebum, lysozyme in sweat → first line of defence.',
        yr: 'SS2',
      },
      {
        q: 'Antibiotic resistance develops in bacteria because:',
        o: [
          'Bacteria deliberately change to resist antibiotics',
          'Antibiotics cause bacteria to mutate',
          'Random mutations occasionally produce resistant bacteria; overuse of antibiotics selects for these bacteria — sensitive bacteria die, resistant bacteria survive and multiply, increasing resistance frequency',
          'Viruses transfer resistance to bacteria',
        ],
        a: 2,
        e: 'Antibiotic resistance evolution: random mutations produce some resistant bacteria before antibiotic use. Antibiotic exposure kills sensitive bacteria (removes competition) → resistant bacteria survive, reproduce, pass resistance genes (often on plasmids, transferable between bacteria by conjugation). Overuse/misuse = selective pressure for resistance.',
        h: 'Antibiotic resistance: mutation → resistance gene; antibiotic selects resistant bacteria → they multiply.',
        yr: 'SS2',
      },
      {
        q: 'The vector for malaria in Nigeria is:',
        o: ['Aedes mosquito', 'Culex mosquito', 'Female Anopheles mosquito', 'Tsetse fly'],
        a: 2,
        e: 'Malaria vector = female Anopheles mosquito. Aedes mosquito transmits dengue fever, yellow fever, Zika virus. Culex mosquito transmits filariasis (lymphatic filariasis/elephantiasis). Tsetse fly transmits sleeping sickness (Trypanosoma brucei).',
        h: 'Malaria vector = female Anopheles. Aedes = dengue/yellow fever. Culex = filariasis. Tsetse = sleeping sickness.',
        yr: 'SS2',
      },
      {
        q: 'Inflammation is a non-specific defence response that:',
        o: [
          'Is triggered only by viral infections',
          'Produces specific antibodies',
          'Brings increased blood flow, phagocytes, and plasma proteins to the site of infection or injury, destroying pathogens and initiating healing',
          'Prevents all microbial entry permanently',
        ],
        a: 2,
        e: 'Inflammation: mast cells release histamine → vasodilation + increased capillary permeability → redness, heat, swelling. Phagocytes (neutrophils, macrophages) migrate to site, engulf and destroy pathogens. Plasma proteins (complement, acute-phase proteins) help destroy pathogens. Non-specific — responds to any injury or pathogen.',
        h: 'Inflammation: non-specific. Vasodilation + phagocyte recruitment + plasma proteins → pathogen destruction.',
        yr: 'SS2',
      },
      {
        q: 'Lysozyme is found in tears, saliva, and nasal secretions. Its function is to:',
        o: [
          'Produce antibodies against pathogens',
          'Break down the cell walls of bacteria — it is an enzyme that cleaves peptidoglycan in bacterial cell walls, destroying the bacteria',
          'Neutralise viruses',
          'Produce mucus',
        ],
        a: 1,
        e: 'Lysozyme is an antimicrobial enzyme (lytic enzyme) found in tears, saliva, nasal mucus, and breast milk. It cleaves the glycosidic bonds in peptidoglycan (a component of bacterial cell walls) — causing osmotic lysis and death of bacteria. It is an important non-specific defence.',
        h: 'Lysozyme: enzyme in tears/saliva; breaks down bacterial cell walls (peptidoglycan) → bacteria die.',
        yr: 'SS2',
      },
      {
        q: 'Typhoid fever is caused by Salmonella typhi and is best prevented by:',
        o: [
          'Using insecticide-treated bed nets',
          'Vaccination against Plasmodium',
          'Clean water, proper sanitation, food hygiene, and typhoid vaccination',
          'Regular blood transfusions',
        ],
        a: 2,
        e: 'Typhoid prevention: (1) clean, treated drinking water (S. typhi spreads via contaminated water); (2) proper sewage disposal; (3) food hygiene (proper cooking, hand washing); (4) typhoid vaccines (oral Ty21a or injectable Vi polysaccharide); (5) treating and isolating cases.',
        h: 'Typhoid prevention: clean water + sanitation + food hygiene + typhoid vaccine.',
        yr: 'SS2',
      },
      {
        q: 'The life cycle of Plasmodium (malaria parasite) involves:',
        o: [
          'Only the human body',
          'Only the mosquito',
          'Both human (liver and red blood cells) and female Anopheles mosquito (sexual reproduction) — requiring both hosts to complete its life cycle',
          'Only water as a medium',
        ],
        a: 2,
        e: 'Plasmodium life cycle: (1) sporozoites injected by mosquito → infect liver cells (incubation); (2) multiply → merozoites released → invade RBCs; (3) RBCs burst (releasing toxins → fever); (4) gametocytes form in RBCs; (5) mosquito ingests gametocytes in blood meal → sexual reproduction in mosquito gut → sporozoites in salivary glands → injected in next bite.',
        h: 'Plasmodium: two hosts — human (liver→RBCs) and female Anopheles mosquito (sexual stage).',
        yr: 'SS2',
      },
      {
        q: 'Passive immunity differs from active immunity in that passive immunity:',
        o: [
          'Involves the individual producing their own antibodies',
          'Provides long-lasting protection lasting years',
          'Involves receiving ready-made antibodies from another source (mother via placenta/breast milk, or immunoglobulin injection) — protection is immediate but short-lived',
          'Requires memory cell formation',
        ],
        a: 2,
        e: 'Passive immunity: antibodies received from external source (not made by recipient). (1) Natural passive: antibodies from mother across placenta (IgG) or colostrum (IgA); (2) Artificial passive: antiserum/immunoglobulin injection (e.g., snake antivenom, rabies immunoglobulin). Immediate but short-lived (weeks-months, no memory cells).',
        h: 'Passive immunity: receive ready-made antibodies. Immediate but short-lived. No memory cells.',
        yr: 'SS2',
      },
      {
        q: 'Penicillin, the first antibiotic, was discovered by Alexander Fleming from the mould:',
        o: [
          'Aspergillus',
          'Rhizopus',
          'Penicillium notatum — Fleming observed that this mould produced a substance killing surrounding Staphylococcus bacteria on an agar plate',
          'Saccharomyces',
        ],
        a: 2,
        e: 'Alexander Fleming (1928) discovered that Penicillium notatum mould produced penicillin — he noticed a clear zone around the mould on a Staphylococcus culture plate (bacterial growth inhibited). Florey and Chain later developed it into a usable drug (1940s). Penicillin inhibits bacterial cell wall synthesis.',
        h: 'Penicillin discovered from Penicillium notatum mould by Alexander Fleming (1928).',
        yr: 'SS2',
      },
      {
        q: 'Herd immunity protects unvaccinated individuals in a population because:',
        o: [
          'Unvaccinated individuals become immune through eating',
          'Vaccines spread from person to person',
          'When a large proportion of the population is vaccinated/immune, the pathogen cannot spread easily — even unvaccinated individuals are protected because chains of transmission are broken',
          'Unvaccinated individuals are naturally resistant',
        ],
        a: 2,
        e: 'Herd immunity: when vaccine coverage exceeds a threshold (e.g., ~95% for measles), the pathogen cannot find enough susceptible hosts to spread efficiently. Unvaccinated individuals (who cannot receive vaccines due to age or medical conditions) are protected because transmission chains are disrupted. Critical for eliminating diseases.',
        h: "Herd immunity: high vaccination rate → pathogen can't spread → unvaccinated individuals protected.",
        yr: 'SS2',
      },
      {
        q: 'Fungi cause disease in humans by:',
        o: [
          'Injecting toxins via a mosquito vector',
          'Producing endospores that are breathed in only',
          'Invading tissues (ringworm, candidiasis), producing toxins (aflatoxins from Aspergillus), or triggering allergic reactions — antifungal drugs treat these infections',
          'Replicating inside white blood cells',
        ],
        a: 2,
        e: "Fungal diseases (mycoses): (1) superficial (skin/hair/nails): ringworm, athlete's foot, candidiasis; (2) systemic: histoplasmosis, cryptococcal meningitis (in immunocompromised); (3) allergic: mould spores trigger asthma; (4) toxicosis: aflatoxins from Aspergillus flavus contaminate stored grain → liver cancer. Treated with antifungals (not antibiotics).",
        h: 'Fungal diseases: ringworm (skin), candidiasis, aflatoxins (Aspergillus in stored food). Treated by antifungals.',
        yr: 'SS2',
      },
      {
        q: "The immune system's specific (adaptive) response involves:",
        o: [
          'Phagocytes engulfing all pathogens immediately',
          'Producing fever to kill all microorganisms',
          'B lymphocytes producing specific antibodies against specific antigens, and T lymphocytes coordinating and executing immune responses — with immunological memory for faster future response',
          'Only physical barriers like skin',
        ],
        a: 2,
        e: 'Specific (adaptive) immunity: (1) antigen-presenting cells display pathogen antigens; (2) T helper cells activate B lymphocytes; (3) B cells differentiate into plasma cells → produce specific antibodies (lock-and-key fit to antigen); (4) memory B and T cells persist → rapid response on re-exposure. Basis of vaccination.',
        h: 'Specific immunity: B cells → antibodies (antigen-specific) + memory cells → rapid future response.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Drugs and Health',
    topicCode: 'SS2-BIO-12',
    module: 'Health Biology',
    term: 'Third Term',
    contentHTML:
      '<div class="learn-intro"><span class="learn-keyword">Drugs</span> are chemical substances that alter the body\'s normal physiological functioning. They can be therapeutic (treat disease) or harmful (abused). Understanding drug effects, addiction, and the consequences of substance abuse is vital for health.</div><h3 class="learn-subheading">1. Classification of Drugs</h3><div class="learn-table-wrap"><table class="learn-table"><thead><tr><th>Class</th><th>Examples</th><th>Effect</th></tr></thead><tbody><tr><td>Stimulants</td><td>Caffeine, cocaine, amphetamines, nicotine</td><td>Speed up CNS; increase heart rate, alertness</td></tr><tr><td>Depressants</td><td>Alcohol, barbiturates, tranquillisers</td><td>Slow down CNS; reduce anxiety, cause sedation</td></tr><tr><td>Narcotics/Opioids</td><td>Heroin, morphine, codeine</td><td>Pain relief, euphoria, highly addictive</td></tr><tr><td>Hallucinogens</td><td>LSD, cannabis (high doses), psilocybin</td><td>Distort perception and reality</td></tr><tr><td>Anabolic steroids</td><td>Testosterone analogues</td><td>Increase muscle mass; illegal in sport</td></tr></tbody></table></div><h3 class="learn-subheading">2. Effects of Alcohol</h3><ul class="learn-list"><li>Depressant — slows CNS, reduces reaction time, impairs judgment</li><li>Chronic effects: liver cirrhosis, brain damage, cardiovascular disease</li><li>Addiction: physical and psychological dependence</li></ul><h3 class="learn-subheading">3. Effects of Tobacco/Nicotine</h3><ul class="learn-list"><li>Nicotine: stimulant; causes addiction</li><li>Tar: carcinogen; causes lung, mouth, throat cancer</li><li>CO (carbon monoxide): binds haemoglobin → reduces O₂ transport</li><li>Chronic smoking: emphysema, bronchitis, cardiovascular disease</li></ul><h3 class="learn-subheading">4. Drug Addiction and Rehabilitation</h3><ul class="learn-list"><li><strong>Tolerance:</strong> more drug needed for same effect</li><li><strong>Dependence:</strong> physical (withdrawal symptoms) and psychological</li><li><strong>Rehabilitation:</strong> counselling, detoxification, support groups</li></ul><div class="learn-tip-box"><span class="learn-tip-icon">💡</span><strong>Alcohol effects:</strong> CNS depressant. Impairs judgment and coordination. CO from cigarettes reduces O₂ transport (CO binds Hb 200× stronger than O₂).</div><div class="learn-tip-box"><span class="learn-tip-icon">🎯</span><strong>Summary:</strong> Stimulants (cocaine, caffeine) speed up CNS. Depressants (alcohol, barbiturates) slow CNS. Opioids (heroin, morphine) relieve pain but highly addictive. Tolerance: need more for same effect.</div>',
    questions: [
      {
        q: 'Which class of drugs slows down the central nervous system (CNS)?',
        o: [
          'Stimulants',
          'Narcotics',
          'Depressants — including alcohol, barbiturates, and tranquillisers',
          'Hallucinogens',
        ],
        a: 2,
        e: 'CNS depressants reduce neuronal activity: slow brain function, decrease reaction time, reduce anxiety, cause sedation. Alcohol is the most widely abused CNS depressant. Stimulants (cocaine, caffeine) speed up CNS. Narcotics (opioids) primarily relieve pain with euphoria. Hallucinogens distort perception.',
        h: 'Depressants: slow CNS. Examples: alcohol, barbiturates, benzodiazepines.',
        yr: 'SS2',
      },
      {
        q: 'Nicotine in cigarettes is classified as a:',
        o: [
          'Depressant',
          'Hallucinogen',
          'Stimulant — it increases heart rate, blood pressure, and alertness while causing addiction through dopamine release',
          'Narcotic',
        ],
        a: 2,
        e: 'Nicotine (stimulant): binds acetylcholine receptors in the brain, triggers dopamine release → pleasure/reward → addiction. Increases heart rate, blood pressure, alertness. Despite being a stimulant, overall smoking is harmful due to tar and CO also present.',
        h: 'Nicotine: stimulant; addictive; increases heart rate; triggers dopamine → reward.',
        yr: 'SS2',
      },
      {
        q: 'The tar in cigarette smoke is harmful because:',
        o: [
          'It increases oxygen levels in the blood',
          'It paralyses cilia and contains carcinogens — chemicals that cause cancer (lung, mouth, throat), and forms a thick layer reducing gas exchange',
          'It is a stimulant that raises blood pressure',
          'It replaces carbon dioxide in the lungs',
        ],
        a: 1,
        e: 'Cigarette tar: (1) coats and paralyses respiratory cilia → mucociliary escalator fails → mucus accumulates → chronic bronchitis; (2) contains carcinogens (benzopyrene, formaldehyde, etc.) → lung, throat, and mouth cancer; (3) accumulates in alveoli → reduces gas exchange surface → emphysema.',
        h: 'Tar: paralyses cilia, contains carcinogens → cancer; coats alveoli → reduced gas exchange.',
        yr: 'SS2',
      },
      {
        q: 'Carbon monoxide in cigarette smoke reduces oxygen transport because:',
        o: [
          'It reacts with oxygen to form CO₂',
          'It binds to haemoglobin about 200 times more strongly than oxygen — forming stable carboxyhaemoglobin that cannot carry O₂',
          'It destroys red blood cells directly',
          'It constricts arteries preventing blood flow',
        ],
        a: 1,
        e: 'CO + Hb → carboxyhaemoglobin (bright cherry-red colour). CO has ~200× higher affinity for Hb than O₂. Carboxyhaemoglobin cannot carry O₂ — effectively reducing the O₂-carrying capacity of blood. Smokers and people in enclosed spaces with CO have reduced O₂ delivery to tissues.',
        h: 'CO: binds Hb 200× stronger than O₂ → carboxyhaemoglobin → reduced O₂ transport.',
        yr: 'SS2',
      },
      {
        q: 'Alcohol (ethanol) affects the nervous system by:',
        o: [
          'Speeding up nerve impulse transmission',
          'Stimulating adrenaline release',
          'Acting as a CNS depressant — slowing nerve impulse transmission, impairing judgment, coordination, and reaction time; reducing inhibitions',
          'Increasing production of neurotransmitters',
        ],
        a: 2,
        e: 'Alcohol: enhances inhibitory GABA receptors and inhibits excitatory NMDA receptors → CNS depression. Effects: impaired judgment (frontal lobe), poor coordination (cerebellum), slurred speech, slow reaction time, reduced inhibitions. Increasing doses → unconsciousness → respiratory depression → death.',
        h: 'Alcohol: CNS depressant. Impairs judgment, coordination, reaction time. Liver cirrhosis with chronic use.',
        yr: 'SS2',
      },
      {
        q: 'Drug tolerance means:',
        o: [
          'The drug has no effect on the individual',
          'The drug cures the disease permanently',
          'The individual requires increasing doses of a drug to achieve the same effect — due to physiological adaptation',
          'The individual is immune to all drugs',
        ],
        a: 2,
        e: 'Tolerance: with repeated drug use, the body adapts (e.g., produces fewer receptors, increases metabolism of the drug) → same dose produces less effect. User needs higher and higher doses to feel the same effect. This escalation increases risk of overdose and accelerates addiction.',
        h: 'Tolerance: increased dose needed for same effect (body adapts to drug).',
        yr: 'SS2',
      },
      {
        q: 'Anabolic steroids are misused in sport to:',
        o: [
          'Increase coordination and mental focus',
          'Produce hallucinations during competition',
          'Increase muscle mass and strength, improve recovery time — giving unfair advantage; banned by sports authorities',
          'Reduce heart rate during competition',
        ],
        a: 2,
        e: 'Anabolic steroids (synthetic testosterone analogues): promote protein synthesis → increased muscle mass and strength; reduce recovery time. Misuse in sport is banned (performance-enhancing drugs). Side effects: liver damage, cardiovascular disease, acne, mood changes (aggression/roid rage), testicular atrophy in males, masculinisation in females.',
        h: 'Anabolic steroids: increase muscle mass; banned in sport; side effects: liver damage, aggression.',
        yr: 'SS2',
      },
      {
        q: 'Long-term heavy alcohol consumption damages mainly the:',
        o: [
          'Kidneys — causing kidney stones',
          'Lungs — causing emphysema',
          'Liver — causing fatty liver, alcoholic hepatitis, and cirrhosis; also affects brain and heart',
          'Pancreas only',
        ],
        a: 2,
        e: 'Chronic alcohol effects: (1) liver: fatty liver → hepatitis → cirrhosis (irreversible scarring, liver failure); (2) brain: Wernicke-Korsakoff syndrome (thiamine deficiency → memory loss); (3) cardiovascular: cardiomyopathy; (4) pancreas: pancreatitis. Liver is most severely affected because it metabolises alcohol.',
        h: 'Chronic alcohol: liver cirrhosis (main damage), brain damage, cardiovascular disease.',
        yr: 'SS2',
      },
      {
        q: 'Heroin (a narcotic) is especially dangerous because:',
        o: [
          'It speeds up the heart to dangerous levels',
          'It causes immediate blindness',
          'It is highly addictive — causing rapid physical dependence; overdose causes respiratory depression (breathing stops) and death; sharing needles spreads HIV and hepatitis',
          'It permanently destroys all brain function with one use',
        ],
        a: 2,
        e: 'Heroin (diamorphine): powerful opioid. Risks: (1) extremely rapid addiction (physical and psychological); (2) overdose → respiratory depression → death; (3) IV injection → HIV, hepatitis B/C transmission via shared needles; (4) severe withdrawal symptoms; (5) social destruction.',
        h: 'Heroin: highly addictive; overdose → respiratory depression → death; needle-sharing → HIV, hepatitis.',
        yr: 'SS2',
      },
      {
        q: 'The safest approach to prevent drug addiction is:',
        o: [
          'Trying all drugs in small amounts',
          'Taking drugs only on weekends',
          'Complete abstinence from non-prescription drugs, education about drug effects, and seeking medical help for prescribed medications',
          'Using only herbal drugs which are always safe',
        ],
        a: 2,
        e: 'Primary prevention of addiction: (1) abstinence from recreational/illegal drugs; (2) drug education (schools, families); (3) peer pressure resistance skills; (4) proper use of prescribed medications under medical supervision; (5) addressing underlying stress/mental health. Social, economic, and family support are also protective.',
        h: 'Drug addiction prevention: abstinence, education, peer pressure resistance, proper medical supervision.',
        yr: 'SS2',
      },
      {
        q: 'Cannabis (marijuana) is classified as:',
        o: [
          'A pure stimulant',
          'A pure depressant',
          'A drug with both depressant and mild hallucinogenic properties — active ingredient THC affects perception, memory, and mood',
          'A narcotic identical to heroin',
        ],
        a: 2,
        e: 'Cannabis: THC (tetrahydrocannabinol) activates endocannabinoid receptors. Effects: euphoria, relaxation (depressant-like), altered sensory perception, impaired memory and coordination, increased appetite. High doses → hallucinations (hallucinogenic). Chronic use linked to anxiety, psychosis, respiratory disease (if smoked), motivation problems.',
        h: 'Cannabis: THC; mild depressant + hallucinogen; impairs memory and coordination.',
        yr: 'SS2',
      },
      {
        q: 'Physical dependence on a drug means:',
        o: [
          'The person uses drugs only for pleasure',
          "The person's body has adapted so that it functions abnormally without the drug — stopping causes withdrawal symptoms (tremors, sweating, nausea, seizures)",
          'The person thinks about the drug occasionally',
          'The drug is taken only when prescribed',
        ],
        a: 1,
        e: 'Physical dependence: the body adapts its physiology to the presence of the drug (e.g., down-regulates receptors). Removing the drug → withdrawal symptoms (physical illness): alcohol withdrawal → tremors, seizures; opioid withdrawal → severe pain, sweating, vomiting. Different from psychological dependence (craving without physical symptoms).',
        h: 'Physical dependence: body adapted to drug; stopping causes withdrawal symptoms (tremors, vomiting, seizures).',
        yr: 'SS2',
      },
      {
        q: 'Which of the following accurately describes the effect of stimulants on the body?',
        o: [
          'Slow heart rate and reduce blood pressure',
          'Cause sedation and sleepiness',
          'Increase heart rate, blood pressure, and alertness; improve focus but may cause anxiety and cardiovascular problems',
          'Only affect the digestive system',
        ],
        a: 2,
        e: 'Stimulants (cocaine, amphetamines, caffeine, nicotine): increase neuronal activity → raise heart rate, blood pressure; increase alertness, energy, confidence. Short-term: increased performance. Long-term risks: cardiovascular damage, addiction, anxiety, psychosis (cocaine, methamphetamine), hypertension.',
        h: 'Stimulants: increase heart rate, blood pressure, alertness. Examples: cocaine, caffeine, amphetamines.',
        yr: 'SS2',
      },
      {
        q: 'Smoking is linked to lung cancer primarily because:',
        o: [
          'Nicotine directly destroys lung cells',
          'CO prevents oxygen reaching lung cells',
          'Tar contains carcinogens (cancer-causing chemicals) that damage DNA in lung epithelial cells — causing uncontrolled cell division (cancer)',
          'Cigarette smoke cools the lungs excessively',
        ],
        a: 2,
        e: 'Lung cancer mechanism: tar carcinogens (benzopyrene, nitrosamines) → absorbed by bronchial epithelium → damage DNA (cause mutations in tumour suppressor genes like p53 and oncogenes) → uncontrolled cell division → malignant tumour → spreads (metastasis). Smoking causes ~85% of lung cancers.',
        h: 'Lung cancer: tar carcinogens → DNA damage in lung cells → mutations → uncontrolled division.',
        yr: 'SS2',
      },
      {
        q: 'Rehabilitation from drug addiction typically involves:',
        o: [
          'Increasing the drug dose then stopping suddenly',
          'Taking a different type of drug permanently',
          'Medical detoxification to manage withdrawal symptoms, counselling (CBT, motivational interviewing), support groups, and addressing underlying psychological issues',
          'Complete isolation from society',
        ],
        a: 2,
        e: 'Drug rehabilitation: (1) detoxification (medically supervised drug withdrawal); (2) psychological therapy (CBT, motivational interviewing); (3) support groups (AA, NA, peer support); (4) treatment of underlying mental health issues (depression, anxiety); (5) social support (family, housing, employment). Long-term relapse prevention is the goal.',
        h: 'Drug rehabilitation: detoxification + counselling (CBT) + support groups + mental health treatment.',
        yr: 'SS2',
      },
      {
        q: 'The liver plays a central role in drug metabolism. This is important because:',
        o: [
          'It produces the drugs the body needs',
          'The liver converts drugs into inactive forms (metabolites) for excretion — this is why liver disease affects drug dosing and why the liver is most vulnerable to drug toxicity',
          'It produces all white blood cells',
          'It stores all drugs taken into the body permanently',
        ],
        a: 1,
        e: 'Liver drug metabolism: hepatic cytochrome P450 enzymes break down drugs into water-soluble metabolites excreted in bile or urine. Liver disease → reduced drug metabolism → drug accumulates → toxicity. The liver itself is vulnerable to drug toxicity (alcohol cirrhosis, paracetamol overdose → acute liver failure).',
        h: 'Liver: metabolises drugs (cytochrome P450). Liver disease → drug accumulation → toxicity.',
        yr: 'SS2',
      },
      {
        q: 'Second-hand smoke (passive smoking) is harmful because:',
        o: [
          'It only affects the smoker',
          'It contains no harmful chemicals',
          'Non-smokers who inhale second-hand smoke are exposed to the same carcinogens and toxins (tar, CO, nicotine) — increasing risk of lung cancer, heart disease, and respiratory illness',
          'It contains less nicotine than direct smoking',
        ],
        a: 2,
        e: 'Second-hand (passive) smoke: non-smokers involuntarily inhale sidestream smoke (from burning cigarette end) and exhaled mainstream smoke. Contains: carcinogens, CO, nicotine, PM₂.₅ particles. Associated with: lung cancer (25% increased risk), cardiovascular disease, asthma in children, SIDS (sudden infant death syndrome).',
        h: 'Passive smoking: non-smokers exposed to carcinogens → lung cancer, heart disease, asthma risk.',
        yr: 'SS2',
      },
      {
        q: 'LSD is classified as a hallucinogen. Its effect on the body is to:',
        o: [
          'Relieve pain and produce euphoria',
          'Slow the heart rate',
          'Distort sensory perception and reality — causing visual and auditory hallucinations by activating serotonin receptors in the brain',
          'Increase oxygen delivery to brain cells',
        ],
        a: 2,
        e: 'LSD (lysergic acid diethylamide): potent hallucinogen. Mechanism: agonist at 5-HT₂A serotonin receptors in prefrontal cortex. Effects: vivid visual hallucinations, distorted sense of time, altered body image, synesthesia (mixing of senses), paranoia, panic. Long-term: flashbacks (HPPD). Not physically addictive.',
        h: 'LSD (hallucinogen): distorts perception, causes hallucinations; serotonin receptor agonist.',
        yr: 'SS2',
      },
      {
        q: 'The social consequences of drug abuse include:',
        o: [
          'Improved academic and work performance',
          'Stronger family relationships',
          'Family breakdown, unemployment, crime (to fund drug habit), social isolation, loss of productivity, and burden on healthcare systems',
          'No significant social impact',
        ],
        a: 2,
        e: 'Social consequences of drug abuse: (1) family dysfunction and breakdown; (2) unemployment → poverty (drugs impair work performance); (3) crime (theft, violence to fund addiction); (4) social isolation; (5) drain on health services and law enforcement; (6) reduced educational achievement; (7) homelessness.',
        h: 'Drug abuse social consequences: family breakdown, unemployment, crime, social isolation, healthcare burden.',
        yr: 'SS2',
      },
      {
        q: "The term 'psychoactive drug' refers to:",
        o: [
          'Any drug taken by mouth',
          'Drugs used only for pain relief',
          'Substances that affect the central nervous system, altering mood, perception, consciousness, or behaviour — including stimulants, depressants, hallucinogens, and narcotics',
          'Drugs that only affect the digestive system',
        ],
        a: 2,
        e: 'Psychoactive drugs: cross the blood-brain barrier and alter brain function → changes in mood, perception, consciousness, behaviour. Categories: stimulants (cocaine, caffeine), depressants (alcohol, benzodiazepines), hallucinogens (LSD, psilocybin), opioids/narcotics (heroin, morphine), dissociatives (ketamine). Most addictive substances are psychoactive.',
        h: 'Psychoactive drugs: affect CNS → alter mood, perception, behaviour. Includes stimulants, depressants, hallucinogens.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Pollution and Conservation',
    topicCode: 'SS2-BIO-13',
    module: 'Ecology',
    term: 'Third Term',
    contentHTML:
      '<div class="learn-intro"><span class="learn-keyword">Pollution</span> is the introduction of harmful substances or energy into the environment. <span class="learn-keyword">Conservation</span> is the management and protection of natural resources and biodiversity for present and future generations.</div><h3 class="learn-subheading">1. Types and Effects of Pollution</h3><div class="learn-table-wrap"><table class="learn-table"><thead><tr><th>Type</th><th>Examples</th><th>Effects</th></tr></thead><tbody><tr><td>Air pollution</td><td>SO₂, NOₓ, CO, particulates, CFCs</td><td>Acid rain, respiratory disease, ozone depletion, global warming</td></tr><tr><td>Water pollution</td><td>Sewage, fertilisers, oil spills, heavy metals</td><td>Eutrophication, disease, death of aquatic life, bioaccumulation</td></tr><tr><td>Soil pollution</td><td>Pesticides, heavy metals, oil spills</td><td>Reduced soil fertility, food chain contamination</td></tr><tr><td>Noise pollution</td><td>Industrial noise, traffic</td><td>Hearing loss, stress, sleep disturbance</td></tr></tbody></table></div><h3 class="learn-subheading">2. Biodiversity and Its Importance</h3><ul class="learn-list"><li>Biodiversity = variety of living organisms in an ecosystem</li><li>Importance: ecosystem services (clean water, O₂ production, pollination, soil formation), food security, medicinal plants, aesthetic value</li><li>Threats: habitat destruction, overexploitation, pollution, invasive species, climate change</li></ul><h3 class="learn-subheading">3. Conservation Strategies</h3><ul class="learn-list"><li><strong>In-situ conservation:</strong> protection in natural habitat (game reserves, national parks, biosphere reserves, marine reserves)</li><li><strong>Ex-situ conservation:</strong> protection outside natural habitat (zoos, botanical gardens, seed banks, captive breeding)</li><li>Afforestation, reforestation, sustainable agriculture, waste management</li></ul><h3 class="learn-subheading">4. Deforestation and Its Effects</h3><p class="learn-p">Effects: loss of biodiversity, soil erosion, flooding, climate change (less CO₂ absorbed), disruption of water cycle, displacement of communities.</p><div class="learn-tip-box"><span class="learn-tip-icon">💡</span><strong>In-situ vs Ex-situ:</strong> In-situ = in natural habitat (national parks). Ex-situ = outside habitat (zoos, seed banks). In-situ is preferred — preserves ecological interactions.</div><div class="learn-tip-box"><span class="learn-tip-icon">🎯</span><strong>Summary:</strong> Pollution types: air (SO₂, CFCs), water (sewage, fertilisers), soil (pesticides). Biodiversity: threatened by deforestation, pollution. Conservation: in-situ (national parks) or ex-situ (zoos, seed banks).</div>',
    questions: [
      {
        q: 'Pollution is defined as:',
        o: [
          'The natural weathering of rocks',
          'The introduction of harmful substances or energy into the environment at levels that cause damage to living organisms and ecosystems',
          'Burning of fossil fuels only',
          'The recycling of waste materials',
        ],
        a: 1,
        e: 'Pollution: the introduction of contaminants (chemical, physical, biological) into the natural environment at concentrations that cause adverse effects on living organisms, ecosystems, and human health. Not all substances in the environment are pollutants — concentration and context matter.',
        h: 'Pollution = introduction of harmful substances into environment at damaging levels.',
        yr: 'SS2',
      },
      {
        q: 'Acid rain is formed when:',
        o: [
          'Rain mixes with ocean water',
          'SO₂ and NOₓ from combustion dissolve in rainwater forming H₂SO₄ and HNO₃ — making rain with pH below 5.6',
          'Plants release acids into the atmosphere',
          'CO₂ alone dissolves in rainwater',
        ],
        a: 1,
        e: 'Acid rain formation: (1) SO₂ (from coal burning, volcanic eruptions) + H₂O → H₂SO₃ → oxidised to H₂SO₄; (2) NOₓ (from vehicle engines) + H₂O → HNO₃. Rain pH < 5.6. Effects: acidifies lakes (kills fish), leaches soil minerals, damages forests, corrodes limestone buildings and statues.',
        h: 'Acid rain: SO₂ + NOₓ + H₂O → H₂SO₄ + HNO₃. pH < 5.6. Kills fish, damages forests.',
        yr: 'SS2',
      },
      {
        q: 'Eutrophication of water bodies is caused by:',
        o: [
          'Oil spills in the water',
          'Excessive nutrients (N and P from fertilisers and sewage) → algal bloom → O₂ depletion → death of aquatic organisms',
          'Removal of aquatic plants',
          'Introduction of large predatory fish',
        ],
        a: 1,
        e: 'Eutrophication: excess nitrates and phosphates (from agricultural runoff, sewage) → algal bloom (blocks sunlight → aquatic plants die) → algae die → decomposers respire (consume O₂) → O₂ depleted → fish and other aquatic life suffocate. Water becomes turbid and smelly.',
        h: 'Eutrophication: excess N+P → algal bloom → O₂ depletion → aquatic death.',
        yr: 'SS2',
      },
      {
        q: 'In-situ conservation means:',
        o: [
          'Protecting species in zoos and botanical gardens',
          'Protecting species in their natural habitat — through national parks, game reserves, biosphere reserves, and marine protected areas',
          'Storing seeds in seed banks',
          'Captive breeding of endangered species',
        ],
        a: 1,
        e: 'In-situ conservation: protecting biodiversity in the natural habitat where species have evolved. Methods: national parks (restrict human activity), game reserves, biosphere reserves, marine protected areas (MPAs). Preferred method — preserves ecological interactions, behaviours, and genetic diversity in natural context.',
        h: 'In-situ: conservation in natural habitat (national parks, reserves). Preferred method.',
        yr: 'SS2',
      },
      {
        q: 'Deforestation leads to increased flooding because:',
        o: [
          'Trees produce excess water',
          'Tree roots and canopy intercept rainfall — when removed, water runs off immediately to rivers without being absorbed, causing flooding and soil erosion',
          'Deforestation increases rainfall',
          'Trees block rivers',
        ],
        a: 1,
        e: 'Trees intercept rainfall (canopy), slow runoff (roots bind soil, litter absorbs water), and promote infiltration into soil. Without trees: (1) surface runoff increases → rivers flood faster; (2) soil is not held together → erosion → siltation of rivers; (3) groundwater recharge reduced → rivers dry out between rains.',
        h: 'Deforestation: no tree roots/canopy → runoff increases → flooding + soil erosion.',
        yr: 'SS2',
      },
      {
        q: 'CFCs (chlorofluorocarbons) contribute to which environmental problem?',
        o: [
          'Acid rain',
          'Eutrophication',
          'Ozone layer depletion — CFCs release chlorine radicals in the stratosphere that catalytically destroy ozone (O₃) molecules',
          'Global warming only',
        ],
        a: 2,
        e: 'CFCs (refrigerants, aerosols): UV light breaks CFCs → Cl• radicals. Cl• + O₃ → ClO• + O₂; ClO• + O• → Cl• + O₂. Each Cl• destroys thousands of O₃ molecules. Ozone depletion → more UV-B reaches Earth → increased skin cancer, cataracts, damage to phytoplankton (marine food webs). Montreal Protocol (1987) phased out CFCs.',
        h: 'CFCs → Cl• radicals → destroy ozone (O₃) → ozone hole → more UV-B radiation.',
        yr: 'SS2',
      },
      {
        q: 'Biodiversity is important for ecosystems because:',
        o: [
          'More species means more competition and less stability',
          'It has no effect on ecosystem functioning',
          'Greater biodiversity increases ecosystem stability, resilience, and productivity — more diverse communities have more functional redundancy and can withstand disturbances better',
          'Only one or two species are needed per ecosystem',
        ],
        a: 2,
        e: 'Biodiversity importance: (1) ecosystem stability — diverse communities recover from disturbances better (redundancy); (2) ecosystem services — clean water, air, pollination, soil formation; (3) food security — diverse crop gene pool; (4) medicine — many drugs derived from plants/animals; (5) aesthetic/cultural value; (6) intrinsic value of all life.',
        h: 'Biodiversity: increases ecosystem stability, resilience, and provides ecosystem services.',
        yr: 'SS2',
      },
      {
        q: 'Ex-situ conservation is used when:',
        o: [
          'Species can thrive in their natural habitat',
          'In-situ conservation is sufficient',
          'A species is so threatened that it cannot survive in the wild alone — managed in zoos, botanical gardens, seed banks, or captive breeding programmes',
          'Hunting is the only conservation method',
        ],
        a: 2,
        e: 'Ex-situ conservation: outside natural habitat. Used for: critically endangered species too vulnerable for in-situ alone; species whose habitat is destroyed; diseases or invasive species threaten wild populations. Methods: zoos (animal breeding), botanical gardens (plant collections), seed banks (genetic diversity storage), cryopreservation.',
        h: 'Ex-situ: zoo, seed bank, botanical garden. Used when species cannot survive in wild alone.',
        yr: 'SS2',
      },
      {
        q: 'Bioaccumulation (biomagnification) of heavy metals (e.g., mercury) in food chains means:',
        o: [
          'Heavy metals break down at each trophic level',
          'Mercury concentration decreases toward the top of the food chain',
          'The concentration of the pollutant increases at each successive trophic level — top predators accumulate the highest concentrations, causing serious health effects',
          'All organisms in the food chain are equally affected',
        ],
        a: 2,
        e: 'Biomagnification: persistent chemicals (DDT, mercury, PCBs) are not excreted efficiently — they accumulate in fatty tissues. Each level of the food chain consumes many organisms from the level below, concentrating the pollutant further. Top predators (eagles, tuna, humans) accumulate toxic concentrations. Example: Minamata disease (mercury poisoning in Japan).',
        h: 'Biomagnification: pollutant concentrates up food chain. Top predators most affected.',
        yr: 'SS2',
      },
      {
        q: 'The main cause of biodiversity loss globally is:',
        o: [
          'Overuse of renewable energy',
          'Too much rainfall in tropical regions',
          'Habitat destruction (deforestation, wetland drainage, urban expansion) — the primary driver of species extinction globally',
          'Natural evolution causing species to disappear',
        ],
        a: 2,
        e: 'The primary driver of biodiversity loss is habitat destruction: deforestation (clearing rainforest for agriculture), wetland drainage, coral reef bleaching, urban expansion. Other drivers (HIPPO): Habitat destruction, Invasive species, Pollution, Population growth (human), Overexploitation.',
        h: 'Biodiversity loss: main cause = habitat destruction (deforestation, urbanisation, drainage). HIPPO mnemonic.',
        yr: 'SS2',
      },
      {
        q: 'Sustainable development means:',
        o: [
          'Developing as fast as possible regardless of environmental impact',
          'Using all natural resources immediately',
          'Meeting the needs of the present generation without compromising the ability of future generations to meet their own needs',
          'Stopping all development to protect nature',
        ],
        a: 2,
        e: "Sustainable development (Brundtland definition, 1987): development that meets present needs without compromising future generations' ability to meet their needs. Balances economic growth, social equity, and environmental protection (three pillars of sustainability).",
        h: 'Sustainable development: meet present needs without compromising future generations. Three pillars: economic, social, environmental.',
        yr: 'SS2',
      },
      {
        q: 'Afforestation differs from reforestation in that afforestation:',
        o: [
          'Replaces trees where they were previously cut',
          'Removes trees for development',
          'Involves planting trees on land that has not been forested recently — new forest creation',
          'Protects existing forests from being cut',
        ],
        a: 2,
        e: 'Afforestation: planting trees on land not previously forested (or not forested for a very long time) → new forest creation. Reforestation: planting trees to restore a previously forested area that was deforested. Both help: absorb CO₂, prevent soil erosion, restore biodiversity, regulate water cycles.',
        h: 'Afforestation = new forest on non-forest land. Reforestation = restoring previously forested land.',
        yr: 'SS2',
      },
      {
        q: 'The greenhouse effect and global warming are primarily caused by:',
        o: [
          'Ozone depletion by CFCs',
          'Increased UV radiation from the Sun',
          'Accumulation of greenhouse gases (CO₂, CH₄, N₂O) from burning fossil fuels and deforestation — trapping more infrared radiation in the atmosphere',
          'Acid rain dissolving carbon compounds',
        ],
        a: 2,
        e: "Enhanced greenhouse effect: GHGs (CO₂, CH₄, N₂O, H₂O vapour) absorb Earth's outgoing infrared radiation and re-emit it in all directions. More GHGs → more heat retained → global warming. Consequences: rising sea levels, extreme weather, coral bleaching, species extinction, changed crop yields.",
        h: 'Global warming: excess GHGs (CO₂, CH₄) trap infrared → temperature rises → climate change.',
        yr: 'SS2',
      },
      {
        q: 'National parks and game reserves in Nigeria include:',
        o: [
          'Sahara Desert and Amazon Rainforest',
          'Yankari National Park and Kainji Lake National Park — among several that protect wildlife and biodiversity in Nigeria',
          'Only marine reserves in Lagos',
          'Only forest reserves in Cross River State',
        ],
        a: 1,
        e: "Nigeria's protected areas: Yankari National Park (Bauchi — hippos, elephants, baboons); Kainji Lake National Park (Niger/Kwara); Cross River National Park (gorillas, chimpanzees); Gashaka Gumti National Park (largest in Nigeria, Taraba/Adamawa); Okomu National Park (Edo). These protect biodiversity in situ.",
        h: 'Nigeria national parks: Yankari (Bauchi), Kainji Lake, Cross River, Gashaka Gumti, Okomu.',
        yr: 'SS2',
      },
      {
        q: 'Oil pollution in the Niger Delta, Nigeria, has caused:',
        o: [
          'Increased agricultural productivity',
          'No significant environmental changes',
          'Severe damage to mangrove ecosystems, farmland, and fishing grounds — destroying livelihoods, polluting water sources, and causing health problems for communities',
          'Increased fish populations',
        ],
        a: 2,
        e: 'Niger Delta oil pollution: decades of oil spills (pipeline leaks, illegal bunkering) have: (1) destroyed mangroves and coastal ecosystems; (2) contaminated farmland making it infertile; (3) polluted rivers and groundwater; (4) devastated fishing communities; (5) caused respiratory and skin diseases; (6) denied local communities clean water.',
        h: 'Niger Delta oil pollution: destroyed mangroves, farmland, fishing grounds; contaminated water; health impacts.',
        yr: 'SS2',
      },
      {
        q: 'The seed bank is an important ex-situ conservation tool because:',
        o: [
          'It grows plants indoors year-round',
          'It is where illegal wildlife is stored',
          'It preserves genetic diversity of plant species — especially crops and wild relatives — protecting against extinction and future food security needs',
          'It only preserves seeds of common plants',
        ],
        a: 2,
        e: 'Seed banks (e.g., Svalbard Global Seed Vault): store seeds of diverse plant species (especially crop varieties and wild relatives) at low temperature and humidity. Purpose: (1) preserve genetic diversity against extinction; (2) future crop improvement (disease resistance, drought tolerance); (3) insurance against agricultural disasters.',
        h: 'Seed banks: preserve plant genetic diversity → future food security and crop improvement.',
        yr: 'SS2',
      },
      {
        q: 'Which of the following is a consequence of global warming?',
        o: [
          'Decreased sea levels due to more evaporation',
          'Increased biodiversity in all regions',
          'Rising sea levels (thermal expansion of water + ice melting), more frequent extreme weather events, coral bleaching, and habitat shifts',
          'Reduced rainfall everywhere',
        ],
        a: 2,
        e: 'Global warming consequences: (1) sea level rise (thermal expansion + ice sheet melting); (2) more frequent hurricanes, floods, droughts, heatwaves; (3) coral bleaching (warm water → expulsion of symbiotic algae); (4) habitat shifts (species move poleward or to higher altitudes); (5) changed crop yields; (6) species extinction.',
        h: 'Global warming: sea level rise, extreme weather, coral bleaching, habitat shifts, species extinction.',
        yr: 'SS2',
      },
      {
        q: 'Integrated pest management (IPM) is a conservation-friendly approach to agriculture that:',
        o: [
          'Uses maximum pesticide application on all crops',
          'Relies only on biological control',
          'Combines biological, cultural, mechanical, and judicious chemical control methods — minimising pesticide use and environmental damage while maintaining crop yields',
          'Completely eliminates all insect species from farmland',
        ],
        a: 2,
        e: 'IPM: combines multiple pest control strategies. Biological control (natural predators, parasitoids); cultural control (crop rotation, resistant varieties, trap crops); mechanical control (physical barriers, trapping); chemical control (targeted, minimal, last resort). Goal: effective pest control with minimal environmental and health impact.',
        h: 'IPM: combines biological, cultural, mechanical, minimal chemical control. Environmentally friendly.',
        yr: 'SS2',
      },
      {
        q: 'The major threat to coral reefs globally is:',
        o: [
          'Overfishing of coral fish only',
          'Plastic pollution only',
          'Ocean warming causing coral bleaching, ocean acidification (CO₂ dissolves in seawater → lowers pH → dissolves calcium carbonate coral skeletons), and physical damage from human activities',
          'Natural algal growth always covers reefs',
        ],
        a: 2,
        e: 'Coral reef threats: (1) ocean warming → bleaching (zooxanthellae expelled → white coral skeleton → coral death); (2) ocean acidification (increased CO₂ → H₂CO₃ → lower pH → CaCO₃ dissolves → coral skeleton weakened); (3) physical damage (tourism, destructive fishing, ship grounding); (4) pollution (fertiliser runoff → algae overgrowth).',
        h: 'Coral reef threats: warming (bleaching), acidification (dissolves skeleton), physical damage, pollution.',
        yr: 'SS2',
      },
      {
        q: 'The ozone layer in the stratosphere is important for life on Earth because:',
        o: [
          'It provides oxygen for breathing at high altitudes',
          'It reflects radio waves back to Earth',
          "It absorbs most of the Sun's harmful ultraviolet (UV-B and UV-C) radiation — preventing it from reaching Earth's surface where it would damage DNA and cause skin cancer",
          'It prevents rainfall from occurring',
        ],
        a: 2,
        e: 'Stratospheric ozone (O₃, 15-35 km altitude): absorbs UV-B (280-315 nm) and UV-C radiation. Without ozone layer: increased UV-B → increased skin cancer (melanoma), cataracts, weakened immune systems; damage to phytoplankton (base of marine food chain); reduced crop yields. Ozone depletion from CFCs led to Montreal Protocol (1987).',
        h: 'Ozone layer: absorbs UV-B and UV-C → prevents skin cancer, DNA damage, phytoplankton damage.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Adaptation',
    topicCode: 'SS2-BIO-14',
    module: 'Ecology',
    term: 'Third Term',
    contentHTML:
      '<div class="learn-intro"><span class="learn-keyword">Adaptation</span> is the process by which organisms develop features that improve their survival and reproduction in their particular environment. Adaptations can be structural (body form), physiological (body functions), or behavioural (actions).</div><h3 class="learn-subheading">1. Types of Adaptation</h3><ul class="learn-list"><li><strong>Structural (morphological):</strong> physical features of the body. Examples: thick fur in Arctic animals, CAM metabolism in desert succulents, streamlined body in fish.</li><li><strong>Physiological:</strong> internal body processes. Examples: desert mammals produce concentrated urine, camels store metabolic water from fat, deep-sea fish tolerate high pressure.</li><li><strong>Behavioural:</strong> actions or activities. Examples: hibernation, migration, camouflage, nocturnal activity, seasonal breeding.</li></ul><h3 class="learn-subheading">2. Adaptations to Different Environments</h3><div class="learn-table-wrap"><table class="learn-table"><thead><tr><th>Environment</th><th>Challenge</th><th>Adaptation</th></tr></thead><tbody><tr><td>Desert</td><td>Heat, water scarcity</td><td>Succulent plants, CAM photosynthesis, nocturnal animals, concentrated urine</td></tr><tr><td>Arctic/cold</td><td>Extreme cold</td><td>Thick fur/blubber, hibernation, countercurrent heat exchange, small surface area</td></tr><tr><td>Aquatic</td><td>Water breathing, movement</td><td>Gills, streamlined body, lateral line, swim bladder</td></tr><tr><td>Rainforest</td><td>Low light at forest floor</td><td>Large thin leaves for maximum light absorption, buttress roots</td></tr></tbody></table></div><h3 class="learn-subheading">3. Camouflage and Mimicry</h3><ul class="learn-list"><li><strong>Camouflage:</strong> blend in with surroundings to avoid detection (prey avoids predator, predator avoids prey detection)</li><li><strong>Mimicry:</strong> resemble another species to gain protection. Batesian mimicry (harmless species mimics harmful one). Müllerian mimicry (two harmful species resemble each other).</li></ul><div class="learn-tip-box"><span class="learn-tip-icon">💡</span><strong>Adaptation vs Evolution:</strong> Adaptation is the outcome of evolution by natural selection over many generations. Individual organisms do NOT adapt during their lifetime (that would be Lamarckism).</div><div class="learn-tip-box"><span class="learn-tip-icon">🎯</span><strong>Summary:</strong> Structural (thick fur), physiological (concentrated urine), behavioural (hibernation). Desert: water conservation, CAM plants. Arctic: insulation, countercurrent exchange. Aquatic: streamlined body, gills.</div>',
    questions: [
      {
        q: 'Structural adaptations are:',
        o: [
          'Changes in behaviour to suit the environment',
          'Internal physiological processes that improve survival',
          'Physical features of the body that improve survival in the environment',
          "Temporary changes that occur during an organism's lifetime",
        ],
        a: 2,
        e: 'Structural (morphological) adaptations: physical body features that improve survival. Examples: thick blubber in polar bears (insulation), streamlined body in dolphins (reduced water resistance), long tongue of anteater (reaching ants in narrow tunnels), spines of cactus (reduced water loss + protection).',
        h: 'Structural adaptation: physical body feature improving survival. Examples: thick fur, streamlined body, spines.',
        yr: 'SS2',
      },
      {
        q: 'Desert plants such as cacti are adapted to water scarcity by:',
        o: [
          'Having very large, thin leaves to maximise photosynthesis',
          'Requiring frequent watering to survive',
          'Having succulent (water-storing) stems, reduced leaves (spines), thick waxy cuticle, deep or shallow spreading roots, and CAM photosynthesis',
          'Growing only at night',
        ],
        a: 2,
        e: 'Cactus desert adaptations: (1) succulent stem stores water; (2) leaves reduced to spines (reduce transpiration, protect from herbivores); (3) thick waxy cuticle (impermeable to water); (4) stomata open at night only (CAM photosynthesis — reduces water loss); (5) wide shallow roots (capture rainfall) or deep roots (reach groundwater).',
        h: 'Cactus: succulent stem, spines (not leaves), waxy cuticle, CAM (stomata open at night only).',
        yr: 'SS2',
      },
      {
        q: 'Hibernation in cold-climate animals is a behavioural adaptation that:',
        o: [
          'Increases metabolic rate to keep warm',
          'Allows animals to remain active throughout winter',
          'Involves entering a state of greatly reduced metabolic activity during winter — conserving energy when food is scarce and temperatures are too low for normal activity',
          'Prevents the animal from accumulating fat reserves',
        ],
        a: 2,
        e: 'Hibernation: metabolic rate drops dramatically (body temperature, heart rate, breathing all fall). Animals live off fat reserves stored before winter. This is an adaptation to cold temperatures and food scarcity. Examples: bears, hedgehogs, dormice, bats. Reduces energy expenditure by up to 90% compared to winter activity.',
        h: 'Hibernation: greatly reduced metabolism during winter; live off stored fat when food scarce.',
        yr: 'SS2',
      },
      {
        q: 'The streamlined body shape of fish is an adaptation to aquatic life that:',
        o: [
          'Helps them breathe underwater',
          'Allows them to control their depth using a swim bladder',
          'Reduces water resistance (drag) during swimming — allowing efficient movement through water',
          'Increases their surface area for gas exchange',
        ],
        a: 2,
        e: 'Streamlined body (fusiform shape): tapered at both ends, smooth body surface. Reduces hydrodynamic drag when swimming. This adaptation allows fish, dolphins, and sharks to move efficiently through water. Additional aquatic adaptations: fins (steering and propulsion), lateral line (pressure sense), gills (oxygen extraction from water).',
        h: 'Streamlined body: reduces water resistance (drag) → efficient swimming.',
        yr: 'SS2',
      },
      {
        q: 'CAM (Crassulacean Acid Metabolism) photosynthesis is an adaptation of desert plants. It involves:',
        o: [
          'Photosynthesising only during daylight with stomata open all day',
          'Opening stomata at night to take in CO₂ (which is stored as malic acid) and closing stomata during the day — drastically reducing water loss while still photosynthesising',
          'Growing leaves only during the rainy season',
          'Replacing water in photosynthesis with soil minerals',
        ],
        a: 1,
        e: 'CAM photosynthesis: stomata open at NIGHT (cooler, more humid → less water loss) → CO₂ absorbed and stored as malic acid. During the day: stomata closed (prevents water loss) → malic acid releases CO₂ for the Calvin cycle → photosynthesis occurs. Brilliant water-conservation strategy for desert plants (cacti, aloes, agaves).',
        h: 'CAM: stomata open at night (absorb CO₂ → stored as malic acid). Daytime: stomata closed. Saves water.',
        yr: 'SS2',
      },
      {
        q: 'Arctic animals such as polar bears and walruses have thick layers of blubber (fat). This is an adaptation because:',
        o: [
          'Blubber makes them heavier to stay underwater',
          'It provides a source of carbohydrate energy',
          'Blubber acts as thermal insulation, reducing heat loss to the cold environment, and also serves as an energy store',
          'It increases their body temperature above normal',
        ],
        a: 2,
        e: 'Blubber (thick subcutaneous fat layer) in Arctic animals: (1) thermal insulation — fat is a poor conductor of heat, reducing heat loss in cold water/air; (2) energy reserve — metabolised during fasting periods or winter; (3) buoyancy in water. Also: white/pale coloured fur (camouflage), small external ears (reduced SA/surface area to reduce heat loss).',
        h: 'Blubber: insulation against cold (poor thermal conductor) + energy reserve.',
        yr: 'SS2',
      },
      {
        q: 'Migration is a behavioural adaptation that allows animals to:',
        o: [
          'Stay in the same location throughout the year',
          'Reproduce more frequently',
          'Move to more favourable environments seasonally — avoiding harsh conditions (winter cold, drought) and following food sources',
          'Escape from predators permanently',
        ],
        a: 2,
        e: 'Migration: seasonal movement between two habitats. Advantages: (1) avoid seasonal food shortage/harsh weather; (2) exploit rich feeding grounds available only seasonally; (3) breed in specific locations (e.g., salmon return to birth stream; wildebeest follow rain in Serengeti; European swallows to Africa in winter). Requires navigation ability (stars, magnetic field, landmarks).',
        h: 'Migration: seasonal movement to favourable environments; follows food/avoids harsh conditions.',
        yr: 'SS2',
      },
      {
        q: 'Countercurrent heat exchange in Arctic animals (e.g., in the legs of seabirds standing on ice) works by:',
        o: [
          'Reversing blood flow in the body',
          'Warm arterial blood flowing close to cool venous blood returning from cold extremities — heat transfers from warm to cool blood, pre-warming it before it reaches the core',
          'Having no blood vessels in the legs',
          'Generating extra heat in the leg muscles',
        ],
        a: 1,
        e: 'Countercurrent heat exchange: in legs of Arctic birds/seals, arteries and veins run close together. Warm arterial blood (from core) flows toward feet; cool venous blood (from feet) flows toward core. Heat transfers from arteries to veins → blood reaching feet is already cool → less heat lost to ice. Blood returning to core is pre-warmed. Core temperature maintained.',
        h: 'Countercurrent exchange: warm arterial blood heats cool venous blood → reduces heat loss to environment.',
        yr: 'SS2',
      },
      {
        q: 'Batesian mimicry is a form of adaptation where:',
        o: [
          'Two harmful species resemble each other for mutual protection',
          'A harmless species evolves to resemble a harmful/dangerous species — gaining protection from predators without the cost of producing actual defences',
          'A predator mimics its prey to get close',
          'Two beneficial species share the same colouration',
        ],
        a: 1,
        e: 'Batesian mimicry: harmless mimic resembles a harmful model. Predators learn to avoid the harmful model → also avoid the similar-looking harmless mimic. Example: hoverflies (harmless, no sting) mimic bees/wasps (harmful, sting). The mimic benefits; the model gains nothing. Named after Henry Walter Bates.',
        h: 'Batesian mimicry: harmless species looks like harmful species → predators avoid both.',
        yr: 'SS2',
      },
      {
        q: 'Rainforest plants at the forest floor have large, dark green leaves as an adaptation to:',
        o: [
          'Resist heavy rainfall',
          'Store more water than smaller leaves',
          'Maximise light absorption in the low-light conditions of the forest floor — large surface area captures more of the limited light penetrating through the canopy',
          'Prevent water loss in humid conditions',
        ],
        a: 2,
        e: 'Forest floor adaptations: (1) large leaves — greater surface area to capture limited light filtered through canopy; (2) dark green (more chlorophyll) — absorbs more of available light; (3) drip tips — shed rain quickly to prevent fungal growth; (4) thin leaves — reduce cost of construction; (5) upright orientation to catch vertical rays.',
        h: 'Forest floor plants: large dark leaves → maximise light absorption in low-light conditions.',
        yr: 'SS2',
      },
      {
        q: 'The long neck of the giraffe is best explained by natural selection as:',
        o: [
          'Giraffes stretched their necks over generations and inherited longer necks (Lamarck — disproved)',
          'All giraffes have equal neck length with no variation',
          'In ancestral giraffe populations, individuals with longer necks could reach higher foliage and survived better — longer neck alleles were passed to offspring, increasing frequency over generations',
          'A divine design feature with no evolutionary explanation',
        ],
        a: 2,
        e: "Darwin's explanation: ancestral giraffes had varying neck lengths (genetic variation). Those with longer necks accessed more food (advantage) → better survival and reproduction → passed long-neck alleles to offspring → over many generations, average neck length increased. This is natural selection — not deliberate stretching.",
        h: 'Giraffe neck: variation → longer-necked individuals survived better → long-neck alleles increased over generations.',
        yr: 'SS2',
      },
      {
        q: 'Halophytes are plants adapted to live in salty environments. Their adaptations include:',
        o: [
          'Producing more glucose to dilute salt',
          'Having large thin leaves',
          'Salt glands to excrete excess salt, succulent leaves to dilute salt concentration, and deep roots to access freshwater below saltwater table',
          'Growing only in freshwater areas',
        ],
        a: 2,
        e: 'Halophyte adaptations to salt stress: (1) salt glands (mangroves, saltmarsh cordgrass) actively excrete salt onto leaf surface; (2) succulent leaves (store water to dilute salt concentration); (3) exclusion at roots (prevent salt entry); (4) compartmentalise salt in vacuoles; (5) prop roots/pneumatophores (mangroves get oxygen in waterlogged salty mud).',
        h: 'Halophytes: salt glands, succulent leaves (dilute salt), salt exclusion at roots.',
        yr: 'SS2',
      },
      {
        q: 'Nocturnal activity in desert animals is an adaptation because:',
        o: [
          'There is more food available at night in deserts',
          'Night is cooler — animals avoid daytime heat and evaporative water loss by being active only after dark',
          'Predators cannot see at night',
          'They need moonlight to navigate',
        ],
        a: 1,
        e: 'Nocturnal desert adaptation: daytime temperatures in deserts can exceed 50°C → dangerous heat + high evaporation. By being active at night: (1) cooler temperatures → less water lost by evaporation and panting; (2) morning dew provides water; (3) some food sources (insects, plants) more available at night. Examples: scorpions, geckos, fennec fox, jerboa.',
        h: 'Nocturnal desert: avoid daytime heat and water loss. Night = cooler, less evaporation.',
        yr: 'SS2',
      },
      {
        q: 'The swim bladder in bony fish is a physiological adaptation that allows:',
        o: [
          'Gas exchange in the gills',
          'Reproduction in freshwater only',
          'Regulation of buoyancy — by adjusting gas volume in the swim bladder, the fish can remain at a particular depth without expending energy in swimming',
          'Propulsion through water',
        ],
        a: 2,
        e: 'Swim bladder (gas bladder): gas-filled organ derived from the gut lining. Fish secrete or reabsorb gas → change swim bladder volume → adjust overall density → control depth without muscular effort. Absent in cartilaginous fish (sharks must swim continuously). Allows bony fish to hover at any depth energy-efficiently.',
        h: 'Swim bladder: adjusts buoyancy (depth control) without continuous swimming. Bony fish only.',
        yr: 'SS2',
      },
      {
        q: 'Thorns on a rose plant or spines on a hedgehog are examples of:',
        o: [
          'Physiological adaptation',
          'Behavioural adaptation',
          'Structural adaptation for protection from predators or herbivores',
          'Mimicry',
        ],
        a: 2,
        e: 'Thorns (rose, acacia), spines (cactus, hedgehog, porcupine): structural adaptations providing physical defence against herbivores (grazing animals avoid thorny plants) and predators (hedgehog rolls up, spines injure predator). This reduces being eaten → increased survival and reproduction.',
        h: 'Thorns and spines: structural adaptations for defence against predators/herbivores.',
        yr: 'SS2',
      },
      {
        q: 'The ability of desert animals to produce very concentrated urine is a:',
        o: [
          'Behavioural adaptation',
          'Structural adaptation of the kidneys only',
          'Physiological adaptation — the long loop of Henlé in their kidneys creates an extreme osmotic gradient → reabsorbs maximum water → excretes very concentrated urine, conserving body water',
          'A temporary change during drought',
        ],
        a: 2,
        e: 'Physiological adaptation for water conservation: desert mammals (kangaroo rat, sand rat) have very long loops of Henlé in their kidneys → extreme medullary osmotic gradient → maximum water reabsorption → urine 10× more concentrated than plasma. Kangaroo rats never drink — get all water from metabolic reactions and food.',
        h: 'Desert animals: long loop of Henlé → extremely concentrated urine → maximum water conservation.',
        yr: 'SS2',
      },
      {
        q: 'Echolocation in bats is an adaptation that:',
        o: [
          'Allows them to see better in daylight',
          'Is only used for communication with other bats',
          'Allows navigation and prey detection in total darkness — bats emit ultrasonic pulses; echoes bouncing back from objects reveal their location, size, and movement',
          'Is the same as vision in nocturnal animals',
        ],
        a: 2,
        e: "Echolocation: bats emit ultrasonic pulses (20-200 kHz) from mouth/nose. Sound bounces off objects → bat's large ears detect echoes → brain processes timing and frequency → creates a 3D acoustic image. Allows precise navigation and hunting of insects in total darkness. Also used by dolphins (biosonar) and some birds (oilbirds, swiftlets).",
        h: 'Bat echolocation: ultrasonic pulses → echoes → 3D image → navigation + prey detection in darkness.',
        yr: 'SS2',
      },
      {
        q: 'Müllerian mimicry differs from Batesian mimicry in that Müllerian mimicry involves:',
        o: [
          'A harmless species resembling a harmful one',
          'A predator resembling its prey',
          'Two or more harmful/unpalatable species that all share similar warning colouration — benefiting all species as predators learn to avoid the pattern more quickly',
          'A harmless species tricking a harmful one',
        ],
        a: 2,
        e: 'Müllerian mimicry: two or more harmful species share the same warning colouration (aposematic pattern). This benefits all species — predators learn the aversive pattern more quickly from encountering any of the species. Example: multiple species of toxic Heliconius butterflies share the same red/black/yellow pattern in South America.',
        h: 'Müllerian mimicry: multiple harmful species share warning colouration → predators learn faster to avoid all.',
        yr: 'SS2',
      },
      {
        q: 'The deep-sea fish that produce their own light (bioluminescence) use this adaptation to:',
        o: [
          'Photosynthesise in deep water',
          'Compete for sunlight',
          'Attract prey, communicate with potential mates, and confuse predators in the lightless deep ocean',
          'Warm their bodies in cold deep water',
        ],
        a: 2,
        e: "Deep-sea bioluminescence: light produced by chemical reaction (luciferin + luciferase + O₂). Uses: (1) attract prey (anglerfish's lure draws fish to its mouth); (2) mate attraction in total darkness; (3) counter-illumination (match faint downwelling light — hide from predators below); (4) startle predators; (5) communication.",
        h: 'Bioluminescence: attract prey, attract mates, confuse predators in deep-sea darkness.',
        yr: 'SS2',
      },
      {
        q: 'The thick waxy cuticle on desert plant leaves is an adaptation that:',
        o: [
          'Increases the rate of transpiration',
          'Helps the plant absorb more water from the air',
          'Reduces water loss by making the leaf surface relatively impermeable to water vapour — an important water conservation strategy in arid environments',
          'Increases the rate of photosynthesis',
        ],
        a: 2,
        e: 'Waxy cuticle (cutin layer): secreted by epidermal cells. Relatively impermeable to water — reduces cuticular transpiration. In desert plants, cuticle is thicker than in mesophytes. Works alongside other adaptations (stomatal closure, CAM, succulence) to conserve water. Also provides some protection against UV radiation and pathogen entry.',
        h: 'Thick waxy cuticle: impermeable to water → reduces transpiration → water conservation in desert plants.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Behaviour in Animals',
    topicCode: 'SS2-BIO-15',
    module: 'Ecology',
    term: 'Third Term',
    contentHTML:
      '<div class="learn-intro"><span class="learn-keyword">Behaviour</span> refers to all the actions and responses of an organism. Animal behaviour can be innate (inherited, not learned) or learned (acquired through experience). Behaviour helps animals survive and reproduce in their environment.</div><h3 class="learn-subheading">1. Innate (Instinctive) Behaviour</h3><ul class="learn-list"><li>Present from birth; genetically determined; no learning required</li><li>Examples: reflex actions (withdrawal from pain), fixed action patterns (FAPs), taxis, kinesis, migration in many animals</li><li><strong>Taxis:</strong> directed movement toward (+) or away from (-) a stimulus. Phototaxis (light), chemotaxis (chemicals), thigmotaxis (contact)</li><li><strong>Kinesis:</strong> undirected change in speed of movement in response to a stimulus intensity (not directional)</li></ul><h3 class="learn-subheading">2. Learned Behaviour</h3><ul class="learn-list"><li><strong>Habituation:</strong> organism stops responding to a repeated harmless stimulus. Example: garden snail withdrawing and re-emerging after repeated touches.</li><li><strong>Imprinting:</strong> learning during a critical period early in life. Example: newly hatched goslings following the first moving object they see (Lorenz\'s experiments).</li><li><strong>Classical conditioning:</strong> association of a neutral stimulus with a natural stimulus. Example: Pavlov\'s dogs salivating at a bell.</li><li><strong>Operant conditioning:</strong> learning through trial and error and reinforcement. Example: rat pressing a lever for food (Skinner box).</li><li><strong>Insight learning:</strong> solving a novel problem without trial and error. Example: chimpanzees using sticks to reach bananas.</li></ul><h3 class="learn-subheading">3. Social Behaviour</h3><ul class="learn-list"><li><strong>Courtship:</strong> behaviours that attract a mate and ensure successful mating (display, songs, scent)</li><li><strong>Territorial behaviour:</strong> defence of an area providing resources (food, mates, nesting sites)</li><li><strong>Dominance hierarchies:</strong> social ranking determining access to resources (pecking order)</li><li><strong>Communication:</strong> visual (colour, display), acoustic (calls, songs), chemical (pheromones)</li></ul><div class="learn-tip-box"><span class="learn-tip-icon">💡</span><strong>Taxis vs Kinesis:</strong> Taxis = DIRECTED movement toward or away from stimulus. Kinesis = UNDIRECTED change in movement rate in response to stimulus intensity.</div><div class="learn-tip-box"><span class="learn-tip-icon">🎯</span><strong>Summary:</strong> Innate: reflex, taxis (directed), kinesis (undirected). Learned: habituation, imprinting, classical conditioning (Pavlov), operant (Skinner), insight (chimps). Social: courtship, territory, dominance, pheromones.</div>',
    questions: [
      {
        q: 'Innate behaviour differs from learned behaviour in that innate behaviour is:',
        o: [
          'Acquired through practice and experience',
          "Modified by the environment during the animal's lifetime",
          'Inherited and present from birth — performed correctly the first time without any prior experience or learning',
          'Observed only in mammals',
        ],
        a: 2,
        e: 'Innate behaviour: genetically programmed (coded in DNA), consistent within a species, performed correctly first time without experience. Examples: spider spinning web, bird building species-typical nest, knee-jerk reflex, newly hatched sea turtle moving toward the ocean. Learned behaviour: acquired through experience, variable between individuals.',
        h: 'Innate: genetic, present at birth, performed correctly first time. Learned: acquired through experience.',
        yr: 'SS2',
      },
      {
        q: 'Taxis differs from kinesis in that taxis is:',
        o: [
          'An undirected change in speed of movement',
          'Only observed in insects',
          'A directed movement toward (+) or away from (-) a specific stimulus — the direction of movement is determined by the direction of the stimulus',
          'A form of learned behaviour',
        ],
        a: 2,
        e: 'Taxis: directed movement where the organism moves toward (positive) or away from (negative) a stimulus. Phototaxis: toward/away from light. Chemotaxis: toward/away from chemicals. Geotaxis: toward/away from gravity. The direction of movement depends on the direction of the stimulus. Kinesis: undirected — changes in speed or turning rate, not directional.',
        h: 'Taxis: directed movement (toward/away from stimulus). Kinesis: undirected change in movement rate.',
        yr: 'SS2',
      },
      {
        q: 'Habituation is a form of learning where:',
        o: [
          'An animal forms a permanent response to a new stimulus',
          'An animal increases its response to a repeated stimulus',
          'An animal gradually decreases and eventually stops responding to a repeated harmless stimulus — because response is neither rewarded nor punished',
          'An animal copies the behaviour of another',
        ],
        a: 2,
        e: 'Habituation: simplest form of learning. Repeated presentation of a stimulus that has no significant consequences → response diminishes and eventually disappears. Adaptive: prevents wasting energy responding to irrelevant stimuli. Example: snail withdraws at first touch, but after many gentle touches stops withdrawing. Disappears if stimulus is removed for some time (spontaneous recovery).',
        h: 'Habituation: stop responding to repeated harmless stimulus. Saves energy. Reversible.',
        yr: 'SS2',
      },
      {
        q: "Konrad Lorenz's experiments with goslings demonstrated:",
        o: [
          'Classical conditioning',
          'Operant conditioning',
          'Imprinting — goslings follow the first large moving object they see within hours of hatching (critical period), even if it is not their mother',
          'Insight learning',
        ],
        a: 2,
        e: "Lorenz (ethologist): newly hatched goslings will imprint on and follow the first moving object they see (usually their mother). Lorenz became the 'mother' himself in his experiments. Imprinting: (1) occurs only during a sensitive/critical period (within hours of hatching); (2) irreversible; (3) affects later social and sexual preferences. Critical for social bonding.",
        h: 'Lorenz imprinting: goslings follow first moving object seen after hatching (critical period). Irreversible.',
        yr: 'SS2',
      },
      {
        q: "Pavlov's experiments with dogs demonstrated:",
        o: [
          'Habituation to bell sounds',
          'Insight learning in dogs',
          'Classical conditioning — the dogs learned to associate a neutral stimulus (bell) with food (unconditional stimulus) until the bell alone (conditioned stimulus) caused salivation (conditioned response)',
          'Operant conditioning using reward/punishment',
        ],
        a: 2,
        e: 'Pavlov: dogs naturally salivate to food (unconditioned stimulus → unconditioned response). After repeatedly pairing bell (neutral stimulus) with food, the bell alone caused salivation (conditioned stimulus → conditioned response). This is classical (Pavlovian) conditioning — learning by association.',
        h: 'Pavlov: bell (neutral stimulus) + food → dog salivates. Bell alone → salivation (classical conditioning).',
        yr: 'SS2',
      },
      {
        q: 'Operant conditioning (Skinner) differs from classical conditioning in that operant conditioning:',
        o: [
          'Involves pairing two stimuli together',
          'Is based on reflex responses only',
          'Involves an animal learning to associate its own behaviour with a consequence (reward or punishment) — reinforcing or discouraging that behaviour',
          'Only applies to humans',
        ],
        a: 2,
        e: 'Operant conditioning (B.F. Skinner): animal learns through trial and error. When an action produces a reward (positive reinforcement) → behaviour is repeated more often. When an action produces punishment → behaviour decreases. Example: rat accidentally presses lever → food reward → presses lever deliberately more often (Skinner box).',
        h: 'Operant conditioning: behaviour + reward/punishment → increase/decrease behaviour frequency.',
        yr: 'SS2',
      },
      {
        q: 'Insight learning is considered the most complex form of learning because:',
        o: [
          'It involves only trial and error',
          'It relies entirely on instinct',
          'The animal solves a novel problem by mental reasoning — applying past experience in a new context without needing trial and error',
          'It is the same as habituation',
        ],
        a: 2,
        e: "Insight learning (cognitive learning): the animal suddenly arrives at a solution to a new problem without trial and error (apparent 'aha' moment). Requires mental manipulation of the problem. Köhler's experiments: chimpanzees spontaneously stacked boxes or used sticks to reach bananas out of reach. Considered highest form of learning; found mainly in primates.",
        h: 'Insight learning: mental reasoning to solve new problem without trial and error. Chimps, great apes.',
        yr: 'SS2',
      },
      {
        q: 'Pheromones are important in animal communication because they:',
        o: [
          'Are visual signals only',
          'Are heard by all members of a group simultaneously',
          'Are chemical signals secreted by one individual that affect the behaviour or physiology of other members of the same species — used for mating attraction, alarm signals, trail marking, and territorial demarcation',
          'Are sounds produced in specific frequencies',
        ],
        a: 2,
        e: 'Pheromones: chemical signals released into the environment, detected by olfactory receptors. Examples: (1) sex pheromones (female moths attract males from km away); (2) alarm pheromones (ants, bees release on disturbance); (3) trail pheromones (ants mark food paths); (4) queen pheromones (suppress worker reproduction in bee colonies).',
        h: 'Pheromones: chemical signals between same-species members. Mating, alarm, trail-marking, territorial.',
        yr: 'SS2',
      },
      {
        q: 'Territorial behaviour in animals serves to:',
        o: [
          'Attract predators to the area',
          'Increase competition within the group',
          'Defend a resource-rich area (food, mates, nesting sites) from rivals — reducing intraspecific competition and ensuring the territory holder has exclusive access to resources',
          'Encourage sharing of resources with other species',
        ],
        a: 2,
        e: 'Territorial behaviour: individual defends an area through display, vocalisation, scent-marking, or fighting. Benefits: (1) exclusive access to food within territory; (2) secure nesting/breeding sites; (3) reduced intraspecific competition; (4) mate attraction. Costs: energy expenditure, risk of injury. Common in birds, mammals, fish, insects.',
        h: 'Territorial behaviour: defends resource-rich area from rivals. Benefits: exclusive food, breeding sites.',
        yr: 'SS2',
      },
      {
        q: 'A reflex action is an example of innate behaviour because:',
        o: [
          'It requires conscious thought and decision-making',
          'It is learned through experience',
          'It is a rapid, automatic, and involuntary response to a stimulus — mediated by the spinal cord (not the brain) and present without prior experience',
          'It can be modified easily by learning',
        ],
        a: 2,
        e: 'Reflex arc: stimulus → receptor → sensory neuron → relay neuron in spinal cord → motor neuron → effector → response. Does not pass through brain initially → very fast response. Examples: withdrawal reflex (hand from hot surface), knee-jerk reflex. Essential for survival — allows rapid response to potential harm.',
        h: 'Reflex: automatic, involuntary, rapid response via spinal cord reflex arc. Innate (not learned).',
        yr: 'SS2',
      },
      {
        q: 'Courtship behaviour in animals has evolved because:',
        o: [
          'It wastes energy with no benefit',
          'It causes aggressive encounters between males only',
          'It helps animals identify potential mates of the same species, assess their fitness and quality, and synchronise reproductive cycles — increasing chances of successful mating and offspring survival',
          'It is only for entertainment',
        ],
        a: 2,
        e: 'Courtship behaviour functions: (1) species recognition — ensures mating with same species (reproductive isolation); (2) mate quality assessment — displays reveal genetic quality, health, resource-holding potential; (3) synchronise hormonal cycles for ovulation timing; (4) pair bond formation (in monogamous species); (5) reduce aggression to allow close approach for mating.',
        h: 'Courtship: species recognition + mate quality assessment + synchronise cycles + reduce aggression.',
        yr: 'SS2',
      },
      {
        q: 'A dominance hierarchy (pecking order) in a group of animals:',
        o: [
          'Ensures all individuals have equal access to resources',
          'Increases constant fighting within the group',
          'Establishes a social ranking system where higher-ranking individuals have priority access to resources — reducing constant fighting by establishing clear rules',
          'Is only found in insect colonies',
        ],
        a: 2,
        e: 'Dominance hierarchy: social rank established through initial contests (display or fighting). Once established, lower-ranking individuals submit without fighting, giving priority to higher-ranking individuals. Reduces energy wasted on constant fighting while maintaining stable social structure. Examples: wolf pack, hen house pecking order, baboon troops.',
        h: 'Dominance hierarchy: rank determines resource access; reduces constant fighting.',
        yr: 'SS2',
      },
      {
        q: 'The waggle dance of honeybees is an example of:',
        o: [
          'Sexual display behaviour',
          'A form of aggression',
          'A complex form of communication — bees returning from food sources perform waggle dances to communicate the direction, distance, and quality of food to hivemates',
          'Random movement in the hive',
        ],
        a: 2,
        e: 'Honeybee waggle dance (Karl von Frisch): returning bee performs figure-8 dance on honeycomb. Straight run direction = direction of food relative to sun. Duration of straight run = distance to food (longer = further). Vigour of dance = food quality. Remarkable example of symbolic animal communication.',
        h: 'Honeybee waggle dance: communicates direction (relative to sun), distance, and quality of food.',
        yr: 'SS2',
      },
      {
        q: 'Negative phototaxis in woodlice (pill bugs) means they:',
        o: [
          'Move toward light sources',
          'Show no response to light',
          'Move away from light — this leads them to dark, moist habitats under stones/logs where they avoid desiccation',
          'Only respond to artificial light',
        ],
        a: 2,
        e: 'Woodlice (Oniscus, Porcellio): negatively phototaxic (move away from light) and positively thigmotaxic (move toward surfaces). This behavioural combination leads them to dark, moist, sheltered habitats under rocks and leaf litter — reducing water loss (woodlice have no waterproof cuticle and desiccate easily in light/dry conditions).',
        h: 'Woodlice: negative phototaxis (away from light) → dark moist habitats → reduced desiccation.',
        yr: 'SS2',
      },
      {
        q: 'Seasonal migration in birds (e.g., swallows flying from Europe to Africa in winter) is:',
        o: [
          'Purely a learned behaviour',
          'A form of insight learning',
          'Primarily innate behaviour — the drive to migrate, direction, and timing are largely genetically programmed — but young birds may refine navigation using experience',
          'Only triggered by cold weather directly',
        ],
        a: 2,
        e: "Bird migration: largely innate. The urge to migrate (Zugunruhe = migratory restlessness) is triggered by day length (photoperiod) changes. Direction and basic navigation are genetically programmed (internal compass using stars, Earth's magnetic field, landmarks). Experience may refine navigation. Some species (first-year birds) migrate alone — without guidance — showing the innate nature.",
        h: 'Bird migration: largely innate (genetically programmed timing/direction). Triggered by photoperiod.',
        yr: 'SS2',
      },
      {
        q: 'Kinesis in behaviour involves:',
        o: [
          'Directed movement toward a stimulus',
          'Moving away from harmful stimuli',
          'An undirected change in the rate of movement or turning frequency in response to stimulus intensity — organism does not move directly toward or away from the stimulus',
          'The same response to all stimuli',
        ],
        a: 2,
        e: 'Kinesis: stimulus intensity causes changes in: (1) orthokinesis = change in speed of movement; or (2) klinokinesis = change in rate of turning. Example: woodlice in dry conditions move faster and turn more often (klinokinesis) → increase chance of finding moist area by chance. Not directional — the organism cannot detect the direction of the stimulus.',
        h: 'Kinesis: undirected. Changes speed or turning rate based on stimulus intensity (not direction).',
        yr: 'SS2',
      },
      {
        q: 'Alarm calls in bird species serve as:',
        o: [
          'A form of territorial display',
          'An attraction call for mates',
          'Communication signals warning other individuals (including members of different species) of predator presence — triggering flight or hiding responses in others',
          'A signal of dominance over other birds',
        ],
        a: 2,
        e: 'Alarm calls: vocalizations triggered by predator detection. Often high-pitched (hard to locate — reduces risk to caller), or repeated short bursts. Benefits group members (even other species) who respond by fleeing or freezing. Examples: vervet monkey specific calls for different predators (eagle, snake, leopard); robin alarm call.',
        h: 'Alarm calls: warn group of predator presence → group escapes. High-pitched (hard to locate).',
        yr: 'SS2',
      },
      {
        q: 'The critical period in imprinting is important because:',
        o: [
          'It determines when the animal starts eating',
          'It allows unlimited learning throughout life',
          'Learning only occurs effectively during a specific early time window — outside this period, the same experiences do not produce imprinting',
          'It prevents any future learning by the animal',
        ],
        a: 2,
        e: "Critical (sensitive) period: a window early in development when the nervous system is especially plastic and imprinting can occur. After this period, the same stimulus has much less (or no) effect. Example: goslings' sensitive period = first 24-36 hours after hatching. Lorenz noted that imprinting outside this window failed to produce normal following behaviour.",
        h: 'Critical period: early window when imprinting can occur. Outside it, same experience has little effect.',
        yr: 'SS2',
      },
      {
        q: 'Fixed action patterns (FAPs) are innate behaviours that are:',
        o: [
          'Learned through observation of others',
          'Highly variable between individuals of the same species',
          'Stereotyped sequences of behaviour triggered by a sign stimulus — once initiated, they run to completion regardless of changes in the stimulus',
          'Modified by reward and punishment',
        ],
        a: 2,
        e: 'Fixed action patterns (FAPs): innate, stereotyped behavioural sequences (same motor pattern each time) triggered by a specific sign stimulus (releaser). Once initiated, the pattern runs to completion even if the stimulus is removed. Example: male stickleback attacks any red-bellied object (sign stimulus = red belly); male robin attacks red feathers. Species-specific.',
        h: 'FAP: innate, stereotyped behaviour triggered by sign stimulus; runs to completion once started.',
        yr: 'SS2',
      },
      {
        q: 'Aggressive behaviour between members of the same species most commonly serves to:',
        o: [
          'Eliminate all rivals permanently',
          'Reduce the population size',
          'Establish dominance hierarchies and territorial boundaries — with most conflicts resolved through display and threat rather than actual combat, reducing injury',
          'Promote cooperation in hunting',
        ],
        a: 2,
        e: 'Intraspecific aggression: typically ritualized (display, posturing, vocalisation) rather than actual combat. Purpose: establish dominance hierarchy (priority access to resources), defend territory, compete for mates. Most conflicts resolved by threat display and submission — actual fighting risks injury to both parties. Only escalates to combat when display fails.',
        h: 'Intraspecific aggression: mainly ritualized display (not combat) → establishes dominance/territory with minimal injury.',
        yr: 'SS2',
      },
    ],
  },
];
